<template>
    <div>

        <!-- Informações da Solicitação -->
        <template v-if="dados && !carregando">

            <div class="progress mt-1 mb-3">
                <div class="indeterminate" v-if="$store.state.loading"></div>
                <div class="determinate" v-else></div>
            </div>

            <template v-if="!consultorio">
                <lembrete-paciente ref="lembretepac" v-model="dados.paciente" />
            </template>

            <div class="alert alert-primary small d-flex justify-content-between mt-1 mb-1 p-1" v-if="dados.infusao">
                <div>
                    <strong>{{ dados.infusao.st_titulo }}</strong>
                </div>
                <div>
                    D{{ dados.in_qt_dia }}
                </div>
            </div>

            <!-- Consultas -->
            <div class="form-row small" v-else-if="dados.consultas && ( dados.consultas.realizada || dados.consultas.futura )">
                <div class="col col-12 col-md-6">
                    <div class="alert alert-primary mb-1 p-1" v-if="dados.consultas.realizada">
                        <div class="d-flex justify-content-between">
                            <div>
                                <strong class="mr-1">Última Consulta Realizada:</strong>
                                <span>{{ $util.getFormatDate(dados.consultas.realizada.dt_inicio) }}</span>
                                <span class="ml-3">há {{ dados.consultas.realizada.tempo_diff }}</span>
                            </div>
                            <span v-if="dados.consultas.realizada.plano && dados.consultas.realizada.plano.operadora">
                                {{ dados.consultas.realizada.plano.operadora.search }}
                            </span>
                        </div>
                        <div class="d-flex justify-content-between">
                            <div>
                                <span>{{ dados.consultas.realizada.procedimento.search }}</span>
                                <span class="badge badge-light mx-2" v-if="dados.consultas.realizada.unidade_operacao && dados.consultas.realizada.unidade_operacao.st_sigla">
                                    {{ dados.consultas.realizada.unidade_operacao.st_sigla }}
                                </span>
                                <span class="text-muted">{{ dados.consultas.realizada.medico.search }}</span>
                            </div>
                            <span>
                                #{{ dados.consultas.realizada.id_atendimento }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="col col-12 col-md-6">
                    <div class="alert alert-primary mb-1 p-1" v-if="dados.consultas.futura">
                        <div class="d-flex justify-content-between">
                            <div>
                                <strong class="mr-1">Próxima Consulta Agendada:</strong>
                                <span>{{ $util.getFormatDate(dados.consultas.futura.dt_inicio) }}</span>
                                <span class="ml-3">em {{ dados.consultas.futura.tempo_diff }}</span>
                            </div>

                            <span v-if="dados.consultas.futura.plano && dados.consultas.futura.plano.operadora">
                                {{ dados.consultas.futura.plano.operadora.search }}
                            </span>
                        </div>
                        <div>
                            <span>{{ dados.consultas.futura.procedimento.search }}</span>
                            <span class="badge badge-light mx-2" v-if="dados.consultas.futura.unidade_operacao && dados.consultas.futura.unidade_operacao.st_sigla">
                                {{ dados.consultas.futura.unidade_operacao.st_sigla }}
                            </span>
                            <span class="text-muted">{{ dados.consultas.futura.medico.search }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Lançamento -->
            <div class="form-row" v-if="!consultorio && dados.rec_subs && dados.rec_subs.length">
                <div class="col col-12 col-md-4">
                    <az-input
                        icon="fas fa-concierge-bell fa-fw"
                        sizing="sm"
                        type="select"
                        placeholder="Selecione a recepção..."
                        :optionsList="dados.rec_subs"
                        optionsData="id_recepcao"
                        required
                        :validate="validate"
                        optionsDisplay="search"
                        :edicao="edicao"
                        v-model="dados.id_recepcao"
                        @input="recalc_preco_cirurgias()" />
                </div>
            </div>

            <div class="form-row" v-if="!consultorio">

                <div class="col col-12 col-md-4">

                    <az-input
                        label="Tipo de Atendimento"
                        sizing="sm"
                        type="select"
                        optionsMode="aql"
                        service="tipo_atendimentos.index"
                        optionsData="id_tipo_atendimento"
                        optionsDisplay="search"
                        :optionsSync="false"
                        :edicao="edicao"
                        :required="!particular"
                        :validate="validate"
                        v-model="dados.atendimento.id_tipo_atendimento" />

                </div>

                <div class="col col-12 col-md-2">

                    <az-input
                        label="Data/Hora do Atendimento"
                        type="text"
                        sizing="sm"
                        :edicao="false"
                        :value="$util.getFormatDate(dados.atendimento.dt_inicio) + ' ' + dados.atendimento.dt_inicio.substr(11,5)" />

                </div>

                <div class="col col-12 col-md-2">
                    <span data-toggle="tooltip" data-placement="top" data-html="true" :title="dados.atendimento.medico ? dados.atendimento.medico.assinatura : ''">
                        <az-input
                            label="Profissional"
                            sizing="sm"
                            type="text"
                            :edicao="false"
                            v-model="dados.atendimento.medico.search">
                            <template v-slot:buttonposb>
                                <div class="input-group-append" v-if="altera_prof && dados.atendimento.atendimento_procedimentos && dados.atendimento.atendimento_procedimentos[0] && !dados.atendimento.atendimento_procedimentos[0].id_atendimento_procedimento">
                                    <button class="btn btn-outline-primary" type="button" title="Trocar Profissional" @click="alterar_profissional_geral()">
                                        <i class="fas fa-edit"></i>
                                    </button>
                                </div>
                            </template>
                        </az-input>
                    </span>
                </div>

                <div class="col col-12 col-md-4">

                    <az-input
                        label="Observação"
                        sizing="sm"
                        type="text"
                        :edicao="edicao"
                        :validate="validate"
                        :iconEnd="{ color: 'outline-secondary', icon: 'fas fa-thumbtack', tooltip: 'Lembrete relacionado ao paciente.' }"
                        @clickButtonEnd="$refs.lembretepac.editar()"
                        v-model="dados.atendimento.st_observacao" />

                </div>

            </div>

            <div class="form-row" v-if="( is_cirurgia || is_externo ) && !dados.plano.operadora.fl_sus">

                <div class="col col-12 col-md-4">
                    <az-input
                        label="Local"
                        sizing="sm"
                        type="select"
                        optionsMode="aql"
                        service="centro_cirurgicos.index"
                        optionsData="id_centro_cirurgico"
                        optionsDisplay="st_centro_cirurgico"
                        :optionsParm="{ ativos: true }"
                        v-model="dados.atendimento.id_centro_cirurgico"
                        :edicao="edicao"
                        :required="cirurg_campos_obrig"
                        :validate="validate" />
                </div>

                <div class="col col-12 col-md-2">
                    <az-input
                        label="Caratér"
                        sizing="sm"
                        type="select"
                        :optionsList="[
                            { id: 'E', search: 'Eletivo' },
                            { id: 'U', search: 'Urgência' }
                        ]"
                        optionsData="id"
                        v-model="dados.atendimento.ch_carater"
                        :edicao="edicao"
                        :validate="validate"
                        @input="recalc_preco_cirurgias()" />
                </div>

                <div class="col col-12 col-md-2">
                    <az-input
                        label="Acomodação"
                        sizing="sm"
                        type="select"
                        :optionsList="[{id: 'A', search: 'Apartamento'},{id: 'E', search: 'Enfermaria'}]"
                        optionsData="id"
                        required
                        :validate="validate"
                        v-model="dados.atendimento.st_cirurgia_acomodacao"
                        @input="recalc_preco_cirurgias()" />
                </div>

                <div class="col col-12 col-md-2" v-if="!particular">
                    <az-input
                        label="Tipo de Guia"
                        sizing="sm"
                        type="select"
                        optionsData="id"
                        :optionsList="[{id: 'S', search: 'SP/SADT'}, {id: 'H', search: 'Honorário Individual'}, {id: 'R', search: 'Resumo de Internação'}, {id: 'C', search: 'Consulta'}]"
                        required
                        :validate="validate"
                        v-model="dados.atendimento.st_cirurgia_tipo_guia"  />
                </div>

                <div class="col col-12 col-md-2" v-if="!particular && dados.atendimento.st_cirurgia_tipo_guia">
                    <az-input
                        :label="dados.atendimento.st_cirurgia_tipo_guia == 'H' ? 'Guia de Solic de Internação' : (dados.atendimento.st_cirurgia_tipo_guia == 'R' ? 'Guia Solicitação de Internação' : 'Guia Principal')"
                        sizing="sm"
                        type="text"
                        :validate="validate"
                        v-model="dados.atendimento.st_guia_principal"  />
                </div>

            </div>

            <div class="form-row" v-else-if="( is_cirurgia || is_externo || dados.procedimento.id_classe_procedimento == 6 ) && dados.plano.operadora.fl_sus && dados.atendimento.js_info">
                <div class="col col-12 col-md-4">
                    <az-input   
                        sizing="sm"
                        label="Motivo de Saída"
                        type="select"
                        optionsMode="aql"
                        service="sus.motivo_saidas"
                        optionsData="id"
                        optionsDisplay="search"
                        :validate="validate"
                        v-model="dados.atendimento.js_info.st_motivo_saida" />
                </div>
                <div class="col col-12 col-md-2">
                    <az-input   
                        sizing="sm" 
                        label="Data de Saída" 
                        type="date"
                        :validate="validate"
                        v-model="dados.atendimento.js_info.dt_saida" />
                </div>
                <div class="col col-12 col-md-2" v-if="false">
                    <az-input
                        label="Tipo de Guia"
                        sizing="sm"
                        type="select"
                        optionsData="id"
                        placeholder="Padrão..."
                        :optionsList="[
                            {id: 'B', search: 'BPA'}, 
                            {id: 'A', search: 'APAC'}, 
                            {id: 'I', search: 'AIH'}
                        ]"
                        :validate="validate"
                        v-model="dados.atendimento.st_cirurgia_tipo_guia"  />
                </div>
            </div>

            <div class="form-row" v-else-if="dados.atendimento.st_cirurgia_tipo_guia !== null && !particular">
                <div class="col col-12 col-md-4">
                    <az-input
                        label="Tipo de Guia"
                        sizing="sm"
                        type="select"
                        optionsData="id"
                        placeholder="Padrão"
                        :optionsList="[{id: 'S', search: 'SP/SADT'}, {id: 'H', search: 'Honorário Individual'}, {id: 'R', search: 'Resumo de Internação'}, {id: 'C', search: 'Consulta'}]"
                        :validate="validate"
                        v-model="dados.atendimento.st_cirurgia_tipo_guia"  />
                </div>
            </div>

            <div class="form-row" v-if="is_cirurgia && dados.paciente.st_nome.substr(0,3) == 'RN '">
                <div class="col col-12 col-md-6">
                    <az-input
                        sizing="sm"
                        type="checkbox-group"
                        label="Cirurgia para Recém Nascido com menos de 2.500 gramas"
                        v-model="dados.atendimento.fl_recem_nascido"
                        @input="recalc_preco_cirurgias()" />
                </div>
            </div>
            
            <div class="form-row" v-if="(is_externo) && (dados.atendimento.st_cirurgia_tipo_guia == 'H' || dados.atendimento.st_cirurgia_tipo_guia == 'R')">
                <div class="col col-12 col-md-2">
                    <az-input
                        label="Data Início do Faturamento"
                        type="date"
                        sizing="sm"
                        :validate="validate"
                        v-model="dados.atendimento.dt_hi_inicio_faturamento" />
                </div>
                <div class="col col-12 col-md-1">
                    <az-input
                        label="Hora Início"
                        type="time"
                        sizing="sm"
                        :validate="validate"
                        v-model="dados.atendimento.st_hr_ini_fat" />
                </div>
                <div class="col col-12 col-md-2">
                    <az-input
                        label="Data Fim do Faturamento"
                        type="date"
                        sizing="sm"
                        :validate="validate"
                        v-model="dados.atendimento.dt_hi_fim_faturamento" />
                </div>
                <div class="col col-12 col-md-1">
                    <az-input
                        label="Hora Fim"
                        type="time"
                        sizing="sm"
                        :validate="validate"
                        v-model="dados.atendimento.st_hr_fim_fat" />
                </div>
            </div>

            <div v-if="( !guia_consulta || lancar_solicitante || ( dados.atendimento.medico_solicitante && dados.atendimento.medico_solicitante.st_nome ) ) && !consultorio">

                <az-sub-title title="Informações da Solicitação">
                    <a class="card-link small" v-if="( config_solicitantes_multiplos || dados.procedimento.id_especialidade == 2 || dados.procedimento.id_especialidade == 67 ) || ( dados.unidade_operacao && dados.unidade_operacao.indic )" 
                        href="#" @click.prevent="incluirSolicitante()">
                        <i class="fas fa-plus mr-1"></i>
                        Incluir Solicitante
                    </a>

                    <a class="card-link small ml-3" v-if="( dados.unidade_operacao && dados.unidade_operacao.indic )" 
                        href="#" @click.prevent="incluirSolicitante(true)">
                        Informar Indicador
                    </a>
                </az-sub-title>

                <medico-solicitante
                    v-if="!troca_solic"
                    :block_executante="( dados && dados.block_executante ) ? true : false"
                    ref="medico_solic"
                    v-model="dados.atendimento"
                    :validate="validate"
                    :required="!particular"
                    :edicao="edicao" />

                <template v-if="dados.texto_indic_clin && dados.atendimento.js_info">
                    <az-input
                        label="Indicação Clínica"
                        :compact="true"
                        sizing="sm"
                        :edicao="edicao"
                        v-model="dados.atendimento.js_info.ic" />
                </template>

                <div v-if="dados.atendimento.solicitante_outros && dados.atendimento.solicitante_outros.length">
                    <div class="mt-2" v-for="(medsol, isx) in dados.atendimento.solicitante_outros" :key="isx">
                        <medico-solicitante
                            :block_executante="( dados && dados.block_executante ) ? true : false"
                            v-show="!medsol.excluido"
                            :can_remove="true"
                            ref="medico_solic"
                            v-model="dados.atendimento.solicitante_outros[isx]"
                            :validate="validate"
                            :required="!particular"
                            :edicao="edicao" />
                    </div> 
                </div>
            </div>

            <div class="form-row" v-if="cidInfoShow && !consultorio">
                <div class="col col-12 col-md-3">
                    <az-input
                        type="select"
                        sizing="sm"
                        optionsMode="json"
                        :optionsList="[{ id: 'A', search: 'Aguda' }, { id: 'C', search: 'Crônica' }]"
                        optionsData="id"
                        optionsDisplay="search"
                        label="Tipo de Doença" />
                </div>
                <div class="col col-12 col-md-3">
                    <div ref="azinput" class="form-group" :class="mb ? 'mb-1' : 'mb-0'">
                        <small class="form-text text-muted text-nowrap text-truncate">
                            Tempo da Doença
                        </small>
                        <div class="input-group input-group-sm">
                            <input type="text" class="form-control" value="Tempo..." />
                            <span class="input-group-btn"></span>
                            <select class="form-control">
                                <option value="D">Dia(s)</option>
                                <option value="D">Mes(es)</option>
                                <option value="D">Ano(s)</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="col col-12 col-md-6">
                    <az-input
                        type="select"
                        sizing="sm"
                        optionsMode="json"
                        :optionsList="[{ id: 'T', search: 'Acidente de trabalho ou doença relacionada ao trabalho' }, { id: 'C', search: 'Acidente de trânsito' }, { id: 'O', search: 'Outros' }]"
                        optionsData="id"
                        optionsDisplay="search"
                        placeholder="Nenhum..."
                        label="Indicação de Acidente" />
                </div>
            </div>

            <!-- Procedimentos -->

            <AzSubTitle title="Procedimentos" v-if="!consultorio">
                <span v-if="!( !guia_consulta || ( dados.atendimento.medico_solicitante && dados.atendimento.medico_solicitante.st_nome ) ) && !consultorio">
                    <a href="#" class="mr-4" @click.prevent="lancar_solicitante = !lancar_solicitante">
                        <small>Solicitante</small>
                    </a>
                </span>
                <span v-if="!particular || ( dados.atendimento.cancelados && dados.atendimento.cancelados.length > 0 )">
                    <a href="#" @click.prevent="trocar_view_mode('L')">
                        <small :class="viewMode == 'L' ? 'font-weight-bold' : 'font-weight-light'">Lançamento</small>
                    </a>
                    <a href="#" class="ml-2" @click.prevent="trocar_view_mode('A')" v-if="!particular">
                        <small :class="viewMode == 'A' ? 'font-weight-bold' : 'font-weight-light'">Autorização</small>
                    </a>
                    <a href="#" class="ml-2" @click.prevent="trocar_view_mode('C')" v-if="viewMode == 'C' || ( dados.atendimento.cancelados && dados.atendimento.cancelados.length > 0 )">
                        <small :class="viewMode == 'C' ? 'font-weight-bold' : 'font-weight-light'">Cancelados</small>
                    </a>
                </span>
            </AzSubTitle>

            <!-- Cancelados -->

            <div v-if="viewMode == 'C'">

                <table class="table table-sm small mt-3" v-if="dados.atendimento.cancelados && dados.atendimento.cancelados.length > 0">
                    <thead>
                        <tr>
                            <td>Id</td>
                            <td>Procedimento</td>
                            <td>Data</td>
                            <td>Responsável</td>
                            <td>Motivo</td>
                            <td>Observação</td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(canc, icx) in dados.atendimento.cancelados" :key="icx">
                            <td>{{ canc.id_atendimento_procedimento }}</td>
                            <td>{{ canc.procedimento.search }}
                                <span class="badge badge-light" v-if="canc.in_quantidade > 1">x{{ canc.in_quantidade }}</span>
                            </td>
                            <td>
                                <template v-if="canc.atendimento_procedimento_cancela && canc.atendimento_procedimento_cancela.created_at">
                                    {{ $util.getFormatDate(canc.atendimento_procedimento_cancela.created_at) }}
                                    {{ canc.atendimento_procedimento_cancela.created_at.slice(11,16) }}
                                </template>
                            </td>
                            <td><template v-if="canc.atendimento_procedimento_cancela && canc.atendimento_procedimento_cancela.usuario">{{ canc.atendimento_procedimento_cancela.usuario.st_login }}</template></td>
                            <td><template v-if="canc.atendimento_procedimento_cancela && canc.atendimento_procedimento_cancela.motivo">{{ canc.atendimento_procedimento_cancela.motivo.st_motivo }}</template></td>
                            <td><template v-if="canc.atendimento_procedimento_cancela">{{ canc.atendimento_procedimento_cancela.st_observacao }}</template></td>
                        </tr>
                    </tbody>
                </table>

                <div v-else class="alert alert-info mt-3">
                    Nenhum item cancelado.
                </div>

            </div>

            <!-- Lançamentos -->

            <div v-else-if="viewMode == 'L'">

                <div v-for="(proc, index) in lista_atendimento_procedimentos" :key="index">

                    <div class="form-row">

                        <!-- Lançamento de Item -->

                        <template v-if="!index">

                            <div class="col col-12" :class="( consultorio && !conta ) ? 'col-md-10' : 'col-md-6'">

                                <az-input
                                    label="Procedimento Principal"
                                    sizing="sm"
                                    type="text"
                                    :edicao="false"
                                    @clickButton="excluirProcedimento(proc, true)"
                                    :icon="conta ? '' : 'fas fa-minus fa-fw'"
                                    :buttonText="conta ? '' : 'fas fa-minus fa-fw'"
                                    :buttonIcon="true"
                                    buttonColor="outline-danger"
                                    v-model="proc.procedimento.search">
                                    <template v-slot:button-pre>
                                        <div class="input-group-prepend" v-if="lista_medicos_solicitantes.length > 1 && proc.js_solics">
                                            <button class="btn btn-outline-secondary" type="button" v-for="(solic, isx) in lista_medicos_solicitantes" :key="isx"
                                                @click="toggle_proc_solic(proc, solic)">
                                                <i class="fa-solid fa-user-doctor fa-fw" v-if="!proc.js_solics[solic.id_medico_solicitante]"></i>
                                                <i class="fa-solid fa-user-slash fa-fw" v-else></i>
                                            </button>
                                        </div>
                                    </template>
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append">
                                            <procedimento-regiao v-model="lista_atendimento_procedimentos[index]" />
                                            <!--
                                            <template v-if="proc.procedimento.js_complementos && proc.procedimento.js_complementos.length">
                                                <div class="input-group-append input-group-sm">
                                                    <select class="form form-control" v-model="proc.st_regiao" required :disabled="proc.atendimento_pacote">
                                                        <option>Região...</option>
                                                        <option v-for="(comp, icx) in proc.procedimento.js_complementos" :key="icx" :value="comp">{{ comp }}</option>
                                                    </select>
                                                </div>
                                            </template>
                                            -->
                                            <template v-if="!assistencia">
                                                <!-- Autorização -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.js_solics && proc.js_solics.aut_glosa" :title="'Autorização Negada: ' + proc.js_solics.aut_glosa">
                                                    <i class="fa-solid fa-stamp text-danger"></i>
                                                </span>
                                                
                                                <!-- Código do Procedimento -->
                                                <span class="input-group-text small" id="basic-addon2" v-if="proc.procedimento && proc.procedimento.id_especialidade == 2 && proc.procedimento.st_sigla" title="Sigla.">
                                                    <span class="small">
                                                        {{ proc.procedimento.st_sigla }}
                                                    </span>
                                                </span>

                                                <!-- Sem cobrança de valor -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc && proc.js_solics && proc.js_solics.fl_block_valor" title="Sem cobrança de valor.">
                                                    <i class="fa-brands fa-creative-commons-nc fa-fw" :class="proc.js_solics.fl_force_guia ? 'text-muted' : 'text-dark'"></i>
                                                </span>

                                                <!-- Coleta de Laboratório -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.nao_coletar" title="Não Coletar.">
                                                    <i class="fas fa-ban"></i>
                                                </span>

                                                <!-- Laudo e Rateio -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.fl_pag_med" title="Pagamento Direto para o Profissional">
                                                    <i class="fa-solid fa-dollar-sign"></i>
                                                </span>

                                                <span class="input-group-text" id="basic-addon2" v-if="proc.js_solics && proc.js_solics.coop" title="Cooperado">
                                                    <i class="fa-solid fa-c"></i>
                                                </span>

                                                <span class="input-group-text" id="basic-addon2" v-if="proc.cid && !proc.info_cid" title="CID do Procedimento">
                                                    <span class="small">
                                                        {{ proc.cid.st_codigo }}
                                                    </span>
                                                </span>

                                                <span class="input-group-text" id="basic-addon2" v-if="proc.id_rateio_medico">
                                                    <span data-toggle="tooltip" data-placement="top" title="Rateio" class="small">
                                                        #{{ proc.id_rateio_medico }}
                                                    </span>
                                                </span>
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.laudo">
                                                    <span title="Item com laudo não disponível para o paciente." v-if="proc.js_solics && proc.js_solics.fl_block_print">
                                                        <i class="fa-solid fa-file-circle-xmark"></i>
                                                    </span>
                                                    <span :title="'Item para Laudar' + (proc.laudo.status_laudo ? ' (' + proc.laudo.status_laudo.search + ')' : '')" v-else>
                                                        <i class="fas fa-notes-medical"></i>
                                                    </span>
                                                </span>

                                                <!-- Amostra Gerada -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.amostra_atendimento_procedimento" title="Amostra Gerada">
                                                    <i class="fa-solid fa-vial"></i>
                                                </span>
                                                <!-- Pacs -->
                                                <span class="input-group-text" id="basic-addon2" v-if="proc.st_pacs || proc.st_an" :title="'Accession number: ' + proc.st_an">
                                                    <i class="fas fa-x-ray" :class="proc.st_pacs == 'OK' ? '' : 'text-muted'"></i>
                                                </span>
                                            </template>
                                            <template v-else-if="consultorio && assistencia">
                                                <span class="input-group-text" v-if="assist_real(proc, 'O')" title="Realizado">
                                                    {{ assist_real(proc, 'O') }}
                                                </span>
                                                <span class="input-group-text" v-if="assist_real(proc, 'R')" title="Realizado">
                                                    <i class="fa fa-check-circle text-success" aria-hidden="true"></i>
                                                </span>
                                                <span class="input-group-text" v-else-if="assist_real(proc, 'A')" title="A Realizar">
                                                    <i class="fa fa-check-circle text-warning" aria-hidden="true"></i>
                                                </span>
                                            </template>
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.item_tabela && ( proc.item_tabela.in_porte_anestesico || proc.item_tabela.in_porte_anestesico === 0 )" title="Porte Anestésico">
                                                P{{ proc.item_tabela.in_porte_anestesico }}
                                            </span>
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.centro && proc.centro.st_sigla">
                                                <span title="Centro de Custo." v-if="proc.centro.st_sigla" class="small">
                                                    <a href="#" @click.prevent="alterar_centro_custo(proc)" class="card-link">
                                                        {{ proc.centro.st_sigla }}
                                                    </a>
                                                </span>
                                            </span>
                                            <button class="btn btn-outline-primary" type="button" data-toggle="tooltip" data-placement="top" title="Incluir Agregado"
                                                @click="incluirConsumo(proc)">
                                                <span v-if="( !consultorio || conta ) && !dados.block_valores && total_consumo(proc)">
                                                    {{ $util.formatMoeda(total_consumo(proc)) }}
                                                </span>
                                                <span v-else-if="lista_consumo(proc).length" class="badge badge-primary mr-1">
                                                    {{ lista_consumo(proc).length }}
                                                </span>
                                                <i class="fas fa-cart-plus fa-fw" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </template>
                                </az-input>

                            </div>

                            <div class="col col-12 col-md-2" v-if="1 == 0">

                                <az-input
                                    label="Profissional"
                                    sizing="sm"
                                    type="text"
                                    :edicao="false"
                                    v-model="dados.atendimento.medico.search"/>

                            </div>

                        </template>

                        <div class="col col-12" :class="( consultorio && !conta ) ? 'col-md-10' : 'col-md-6'" v-else>

                            <!-- type="autocomplete"
                                optionsMode="aql"
                                optionsName="procedimentos.agenda"
                                :optionsSync="false"
                                v-model="proc.procedimento"
                            -->

                            <az-input
                                placeholder="Procedimento"
                                sizing="sm"
                                :icon="conta ? '' : 'fas fa-minus fa-fw'"
                                v-model="proc.procedimento.search"
                                :ref="'info_procedimento.' + index"
                                :buttonText="conta ? '' : 'fas fa-minus fa-fw'"
                                :buttonIcon="true"
                                :edicao="false"
                                buttonColor="outline-danger"
                                @clickButton="excluirProcedimento(proc, proc.id_atendimento_procedimento ? true : false)">
                                <template v-slot:button-pre>
                                    <div class="input-group-prepend" v-if="lista_medicos_solicitantes.length > 1">
                                        <button class="btn btn-outline-secondary" type="button" v-for="(solic, isx) in lista_medicos_solicitantes" :key="isx"
                                            @click="toggle_proc_solic(proc, solic)">
                                            <i class="fa-solid fa-user-doctor fa-fw" v-if="!proc.js_solics || !proc.js_solics[solic.id_medico_solicitante]"></i>
                                            <i class="fa-solid fa-user-slash fa-fw" v-else></i>
                                        </button>
                                    </div>
                                </template>
                                <template v-slot:buttonposb>
                                    <template v-if="proc.procedimento.js_complementos && proc.procedimento.js_complementos.length">
                                        <div class="input-group-append input-group-sm">
                                            <procedimento-regiao v-model="lista_atendimento_procedimentos[index]" :disabled="proc.atendimento_pacote" />

                                            <select class="form form-control" v-if="false" v-model="proc.st_regiao" required :disabled="proc.atendimento_pacote">
                                                <option>Região...</option>
                                                <option v-for="(comp, icx) in proc.procedimento.js_complementos" :key="icx" :value="comp">{{ comp }}</option>
                                            </select>
                                        </div>
                                    </template>
                                    <div class="input-group-append">
                                        <template v-if="!assistencia">
                                            <!-- Indicação de Dupliciadade -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.procedimento 
                                                && proc.procedimento.id_especialidade == 2 
                                                && lista_lab_duplicados
                                                && lista_lab_duplicados[proc.procedimento.id_procedimento + '|' + proc.id_amostra_material]
                                                && lista_lab_duplicados[proc.procedimento.id_procedimento + '|' + proc.id_amostra_material].length > 1" 
                                                title="Duplicado.">
                                                <i class="fa-solid fa-exclamation text-danger"></i>
                                                <i class="fa-solid fa-exclamation text-danger"></i>
                                            </span>

                                            <!-- Autorização -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.js_solics && proc.js_solics.aut_glosa" :title="'Autorização Negada: ' + proc.js_solics.aut_glosa">
                                                <i class="fa-solid fa-stamp text-danger"></i>
                                            </span>
                                            
                                            <!-- Código do Procedimento -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.procedimento && proc.procedimento.id_especialidade == 2 && proc.procedimento.st_sigla" title="Sigla.">
                                                <span class="small">
                                                    {{ proc.procedimento.st_sigla }}
                                                </span>
                                            </span>

                                            <!-- Sem cobrança de valor -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc && proc.js_solics && proc.js_solics.fl_block_valor" title="Sem cobrança de valor.">
                                                <i class="fa-brands fa-creative-commons-nc fa-fw" :class="proc.js_solics.fl_force_guia ? 'text-muted' : 'text-dark'"></i>
                                            </span>

                                            <!-- Coleta de Laboratório -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.nao_coletar" title="Não Coletar.">
                                                <i class="fas fa-ban"></i>
                                            </span>

                                            <!-- Rateio -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.fl_pag_med" title="Pagamento Direto para o Profissional">
                                                <i class="fa-solid fa-dollar-sign"></i>
                                            </span>

                                            <span class="input-group-text" id="basic-addon2" v-if="proc.js_solics && proc.js_solics.coop" title="Cooperado">
                                                <i class="fa-solid fa-c"></i>
                                            </span>

                                            <span class="input-group-text" id="basic-addon2" v-if="proc.cid && !proc.info_cid" title="CID do Procedimento">
                                                <span class="small">
                                                    {{ proc.cid.st_codigo }}
                                                </span>
                                            </span>

                                            <span class="input-group-text" id="basic-addon2" title="Rateio" v-if="proc.id_rateio_medico">
                                                <span class="small">
                                                    #{{ proc.id_rateio_medico }}
                                                </span>
                                            </span>

                                            <span class="input-group-text" id="basic-addon2" v-if="proc.laudo" title="Item para Laudar">
                                                <i class="fa-solid fa-file-circle-xmark" v-if="proc.js_solics && proc.js_solics.fl_block_print"></i>
                                                <span :title="'Item para Laudar' + (proc.laudo.status_laudo ? ' (' + proc.laudo.status_laudo.search + ')' : '')" v-else>
                                                    <i class="fas fa-notes-medical"></i>
                                                </span>
                                            </span>

                                            <!-- Amostra Gerada -->
                                            <span class="input-group-text" id="basic-addon2" v-if="proc.amostra_atendimento_procedimento" title="Amostra Gerada">
                                                <i class="fa-solid fa-vial"></i>
                                            </span>
                                            
                                            <button class="btn btn-outline-primary" type="button" data-toggle="tooltip" data-placement="top" title="Outro Profissional Executante"
                                                @click="proc.outro_medico = !proc.outro_medico" v-if="!proc.id_atendimento_procedimento && !consultorio">
                                                <i class="fa fa-user-md" aria-hidden="true"></i>
                                            </button>
                                        </template>
                                        <template v-else-if="consultorio && assistencia">
                                            <span class="input-group-text" v-if="assist_real(proc, 'O')" title="Realizado">
                                                {{ assist_real(proc, 'O') }}
                                            </span>
                                            <span class="input-group-text" v-if="assist_real(proc, 'R')" title="Realizado">
                                                <i class="fa fa-check-circle text-success" aria-hidden="true"></i>
                                            </span>
                                            <span class="input-group-text" v-else-if="assist_real(proc, 'A')" title="A Realizar">
                                                <i class="fa fa-check-circle text-warning" aria-hidden="true"></i>
                                            </span>
                                        </template>
                                        <span class="input-group-text" id="basic-addon2" v-if="proc.item_tabela && ( proc.item_tabela.in_porte_anestesico || proc.item_tabela.in_porte_anestesico === 0 )" title="Porte Anestésico">
                                            P{{ proc.item_tabela.in_porte_anestesico }}
                                        </span>
                                        <button class="btn btn-outline-primary" type="button" data-toggle="tooltip" data-placement="top" title="Incluir Agregado"
                                            @click="incluirConsumo(proc)">
                                            <span v-if="( !consultorio || conta ) && !dados.block_valores && total_consumo(proc)">
                                                {{ $util.formatMoeda(total_consumo(proc)) }}
                                            </span>
                                            <span v-else-if="lista_consumo(proc).length" class="badge badge-primary mr-1">
                                                {{ lista_consumo(proc).length }}
                                            </span>
                                            <i class="fas fa-cart-plus" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </template>
                            </az-input>
                            
                        </div>

                        <!-- Consulta / Amostra / Quantidade / guia_consulta -->

                        <!-- Amostra -->
                        <div class="col col-12 col-md-2" v-if="proc.procedimento && proc.procedimento.amostras && proc.procedimento.amostras.length > 0">
                            <az-input
                                sizing="sm"
                                :label="index ? '' : 'Tipo'"
                                type="select"
                                :edicao="true"
                                optionsMode="json"
                                optionsData="id_amostra_material"
                                :optionsList="ordernar(proc.procedimento.amostras, 'search')"
                                :required="proc.procedimento && proc.procedimento.amostras && proc.procedimento.amostras.length > 1"
                                :validate="validate"
                                v-model="proc.id_amostra_material">
                                <template v-slot:button-pre>
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">
                                            <input type="checkbox" v-model="proc.fl_urgente">
                                            <small class="ml-1">Urg</small>
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:buttonposb>
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <span class="small">
                                                x{{ proc.in_quantidade }}
                                            </span>
                                        </button>
                                        <div class="dropdown-menu">
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 1">x1</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 2">x2</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 3">x3</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 4">x4</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 5">x5</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 6">x6</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 7">x7</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 8">x8</a>
                                            <a class="dropdown-item" href="#" @click.prevent="proc.in_quantidade = 9">x9</a>
                                            <template v-if="proc.id_amostra_material">
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" href="#" @click.prevent="toggle_info_obs_amostra(proc)">Observação da Amostra</a>
                                            </template>
                                        </div>
                                    </div>
                                    <!--
                                    <div class="input-group-append" v-if="false">
                                        <span class="input-group-text">
                                            <span class="small">
                                                x{{ proc.in_quantidade }}
                                            </span>
                                        </span>
                                    </div>
                                    -->
                                </template>
                            </az-input>

                            <az-input   
                                sizing="sm"
                                placeholder="Descrição do Material..."
                                v-if="proc.id_amostra_material && proc.js_solics && dados && ( ( dados.id_amostra_material_diversos && dados.id_amostra_material_diversos[proc.id_amostra_material] ) || ( proc.js_solics.oa ) )"
                                required
                                :validate="validate"
                                v-model="proc.js_solics.amo" />
                        </div>

                        <div class="col col-12 col-md-2" v-else-if="proc.amostra">
                            <az-input
                                sizing="sm"
                                :label="index ? '' : 'Tipo'"
                                :edicao="false"
                                v-model="proc.amostra.search">
                                <template v-slot:button-pre>
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">
                                            <input type="checkbox" v-model="proc.fl_urgente">
                                            <small class="ml-1">Urg</small>
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:buttonposb>
                                    <div class="input-group-append" v-if="proc.in_quantidade != 1">
                                        <span class="input-group-text">
                                            x{{ proc.in_quantidade }}
                                        </span>
                                    </div>
                                </template>
                            </az-input>

                            <az-input   
                                sizing="sm"
                                placeholder="Descrição do Material..."
                                v-if="proc.id_amostra_material && proc.js_solics && dados && ( ( dados.id_amostra_material_diversos && dados.id_amostra_material_diversos[proc.id_amostra_material] ) || ( proc.js_solics.oa ) )"
                                v-model="proc.js_solics.amo" />
                        </div>
                        <!-- Fim Amostra -->

                        <div class="col col-12 col-md-2" v-else-if="proc.procedimento && proc.procedimento.id_classe_procedimento == 1 && proc.procedimento.st_codigo_cbhpm != '10102019'">

                            <az-input
                                sizing="sm"
                                :label="index ? '' : 'Tipo de Consulta'"
                                type="select"
                                :edicao="dados.atendimento.pode_alterar_tipo_consulta"
                                optionsMode="json"
                                optionsData="id"
                                :optionsList="[
                                    {id: 'I', search: 'Inicial'},
                                    {id: 'S', search: 'Subsequente'},
                                    {id: 'R', search: 'Revisão'},
                                    {id: 'P', search: 'Pré-Natal'},
                                    {id: 'E', search: 'Encaminhamento'},
                                ]"

                                :icon="pode_lancar_pacote(proc) ? 'fas fa-layer-group fa-fw' : ''"
                                :buttonText="pode_lancar_pacote(proc) ? 'fas fa-layer-group fa-fw' : ''"
                                :buttonIcon="true"
                                buttonColor="outline-primary"
                                @clickButton="ver_pacotes(proc.procedimento.procedimento_pacotes, index)"

                                v-if="!proc.id_atendimento_procedimento || pode_alterar_valor"
                                v-model="proc.ch_tipo_consulta"
                                @input="recalc_preco_cirurgias(proc)"
                                input2="recalc_preco_cirurgia(proc)">
                                <template v-slot:buttonposb>
                                    <div class="input-group-append" v-if="proc.in_quantidade != 1">
                                        <span class="input-group-text">
                                            x{{ proc.in_quantidade }}
                                        </span>
                                    </div>
                                </template>
                            </az-input>
                            <!-- v-if="pode_alterar_valor" -->


                            <az-input
                                v-else
                                sizing="sm"
                                type="text"
                                :edicao="false"
                                :label="index ? '' : 'Tipo de Consulta'"
                                :placeholder="{I: 'Inicial', R: 'Revisão', S: 'Subsequente'}[proc.ch_tipo_consulta]" />

                        </div>

                        <div class="col col-12 col-md-2" v-else>
                            <az-input
                                sizing="sm"
                                :edicao="!pode_alterar_proc(proc, false) || !!proc.atendimento_pacote
                                    || ( proc.atendimento_procedimento_consumos && proc.atendimento_procedimento_consumos.length ) ? false : edicao"
                                :label="index ? '' : 'Qtd'"
                                :icon="pode_lancar_pacote(proc) ? 'fas fa-layer-group fa-fw' : ''"
                                :buttonText="pode_lancar_pacote(proc) ? 'fas fa-layer-group fa-fw' : ''"
                                :buttonIcon="true"
                                buttonColor="outline-primary"
                                @clickButton="ver_pacotes(proc.procedimento.procedimento_pacotes, index)"
                                type="mask"
                                mask="####"
                                required2
                                :validate="true"
                                v-model="proc.in_quantidade">
                                <template v-slot:button-pre v-if="proc.procedimento.id_status_laudo">
                                    <div class="input-group-prepend">
                                        <div class="input-group-text">
                                            <input type="checkbox" v-model="proc.fl_urgente">
                                            <small class="ml-1">Urg</small>
                                        </div>
                                    </div>
                                </template>
                                <template v-slot:buttonposb>
                                    <div class="input-group-append">
                                        <span class="input-group-text" id="basic-addon2" data-toggle="tooltip" data-placement="top" title="Redução/Acréscimo"
                                            v-if="proc.dc_percentual_reducao && proc.dc_percentual_reducao != 1">
                                            {{ Math.round(proc.dc_percentual_reducao * 100) / 100 }}
                                        </span>
                                        <template v-if="( !proc.atendimento_pacote
                                            && ( proc.atendimento_procedimento_consumos && proc.atendimento_procedimento_consumos.length ) )
                                            || ( proc.atendimento_pacote && ( pode_alterar_valor || proc.atendimento_pacote || !proc.id_atendimento_procedimento ) ) ">
                                            <button class="btn btn-outline-danger" type="button" @click="alterar_qtde(proc, false)"
                                                v-if="proc.in_quantidade > ( 
                                                    ( 
                                                        proc.atendimento_pacote 
                                                            && proc.atendimento_pacote.procedimento_pacote 
                                                            && proc.atendimento_pacote.procedimento_pacote.pacote 
                                                            && proc.atendimento_pacote.procedimento_pacote.pacote.fl_pre_pago ) 
                                                        ? 0 
                                                        : 1 
                                                    )
                                                    && pode_alterar_qt(proc, false)">
                                                <i class="fa fa-minus-circle" aria-hidden="true"></i>
                                            </button>
                                            <button class="btn btn-outline-success" type="button" @click="alterar_qtde(proc, true)" v-if="pode_alterar_qt(proc, false)">
                                                <i class="fa fa-plus-circle" aria-hidden="true"></i>
                                            </button>
                                        </template>
                                    </div>
                                </template>
                            </az-input>
                        </div>

                        <template v-if="!consultorio || conta">

                            <template v-if="!proc.id_atendimento_procedimento && proc.outro_medico">
                                <div class="col col-12 col-md-2">
                                    <i class="" aria-hidden="true"></i>
                                    <az-input
                                        :label="index ? '' : 'Profissional Executante'"
                                        sizing="sm"
                                        icon="fa fa-user-md fa-fw"
                                        type="autocomplete"
                                        optionsMode="aql"
                                        service="medico_procedimento_unidades.index"
                                        optionsData="id_pessoa"
                                        optionsDisplay="search"
                                        :optionsParm="{
                                            troca_em_procedimento: true,
                                            id_procedimento: proc.procedimento.id_procedimento,
                                            id_especialidade: proc.procedimento.id_especialidade,
                                            id_operadora: dados.plano.operadora.id_operadora,
                                        }"
                                        required
                                        v-model="proc.id_outro_medico" />
                                </div>
                            </template>

                            <template v-else>

                                <!-- Exibição do Valor -->
                                <div class="col col-12 col-md-2" v-if="!proc.atendimento_pacote">
                                    <template v-if="!dados.block_valores">
                                        <az-input
                                            :key="'valor' + index"
                                            sizing="sm"
                                            :label="index ? '' : 'Valor Unitário'"
                                            :type="editando_valores ? 'number' : 'money'"
                                            vif2="!excluindo && dados.atendimento.plano.fl_valor_editavel && dados.atendimento.plano.valor_editavel && !proc.id_atendimento_procedimento_empacotado"
                                            v-if="tem_valor_editavel(proc)"
                                            v-model="proc.dc_valor_procedimento">
                                            <template v-slot:button-pre>
                                                <div class="input-group-prepend" v-if="$store.state.user.id_usuario == 1 || habilita_recalc_preco || recalc_com_comps">
                                                    <button class="btn btn-outline-secondary" type="button" id="button-addon1" @click="recalc_preco_cirurgia(proc)">
                                                        <i class="fa-solid fa-brazilian-real-sign fa-fw"></i>
                                                    </button>
                                                </div>
                                            </template>
                                            <template v-slot:buttonposb>
                                                <div class="input-group-append" v-if="proc.in_quantidade > 1">
                                                    <button type="button" class="btn btn-outline-secondary" v-if="tem_valor_editavel(proc)"
                                                        @click="$refs.edit_valor_total.editar(proc, 'in_quantidade', 'dc_valor_procedimento')">
                                                        <i class="fas fa-equals fa-fw"></i>
                                                        {{ $util.formatMoeda(proc.dc_valor_procedimento * proc.in_quantidade) }}
                                                    </button>
                                                    <span class="input-group-text" id="basic-addon2" v-else>
                                                        <i class="fas fa-equals fa-fw"></i>
                                                        {{ $util.formatMoeda(proc.dc_valor_procedimento * proc.in_quantidade) }}
                                                    </span>
                                                </div>
                                            </template>
                                        </az-input>

                                        <az-input
                                            :key="'valor_fixo_' + index"
                                            sizing="sm"
                                            :label="index ? '' : 'Valor Unitário'"
                                            v-else
                                            :edicao="false"
                                            :value="$util.formatMoeda(proc.dc_valor_procedimento)">
                                            <template v-slot:button-pre>
                                                <div class="input-group-prepend">
                                                    <button class="btn btn-outline-primary" type="button" data-toggle="tooltip" data-placement="top" title="Empacotado" @click="exibir_info_pacote(proc)"
                                                        v-if="proc.id_atendimento_procedimento_empacotado">
                                                        <i class="fas fa-archive fa-fw"></i>
                                                    </button>
                                                    <button type="button" class="btn btn-outline-secondary" title="Editar Valor" @click="alterar_valor(proc)" v-else-if="dados.atendimento.plano.fl_valor_editavel && !dados.atendimento.plano.valor_editavel">
                                                        <i class="fa-solid fa-brazilian-real-sign fa-fw"></i>
                                                    </button>
                                                    <button class="btn btn-outline-secondary" type="button" id="button-addon1"
                                                        @click="recalc_preco_cirurgia(proc)"
                                                        v-else-if="$store.state.user.id_usuario == 1 || habilita_recalc_preco || recalc_com_comps">
                                                        <i class="fa-solid fa-brazilian-real-sign fa-fw"></i>
                                                    </button>
                                                    <span class="input-group-text" id="basic-addon2" v-else>
                                                        <i class="fa-solid fa-brazilian-real-sign fa-fw"></i>
                                                    </span>
                                                </div>
                                            </template>
                                            <template v-slot:buttonposb>
                                                <div class="input-group-append" v-if="proc.in_quantidade > 1">
                                                    <span class="input-group-text" id="basic-addon2">
                                                        <i class="fas fa-equals mr-2"></i>
                                                        {{ $util.formatMoeda(proc.dc_valor_procedimento * proc.in_quantidade) }}
                                                    </span>
                                                </div>
                                                
                                                <div class="input-group-append" v-if="dados && dados.plano && dados.plano.id_operadora != 1 && proc && !proc.dc_valor_procedimento && !proc.id_plano_particular && !proc.item_tabela" title="Sem valor definido.">
                                                    <span class="input-group-text" id="basic-addon2">
                                                        <i class="fa-solid fa-triangle-exclamation text-danger"></i>
                                                    </span>
                                                </div>

                                            </template>
                                        </az-input>
                                    </template>
                                </div>

                                <!-- :value="$util.formatMoeda(proc.ch_tipo_consulta == 'R' ? 0 : proc.dc_valor_procedimento)" -->

                                <!-- Exibição do Pacote -->
                                <div class="col col-12 col-md-2" v-else>
                                    <az-input
                                        v-if="1 == 1"
                                        sizing="sm"
                                        :label="index ? '' : 'Valor'"
                                        icon="fas fa-layer-group fa-fw"
                                        :edicao="false"
                                        type="text"
                                        :title="( proc.atendimento_pacote && proc.atendimento_pacote.st_obs ) ?  proc.atendimento_pacote.st_obs : ''"
                                        :value="
                                            ( ( proc.atendimento_pacote && proc.atendimento_pacote.in_sessoes_previas ) ? proc.atendimento_pacote.in_sessoes_previas + ' + ' : '' ) + 
                                            ( 
                                                ( ( proc.atendimento_pacote && proc.atendimento_pacote.pacote && proc.atendimento_pacote.pacote.fl_combo )
                                                ? ( ( proc.procedimento_pacote && proc.procedimento_pacote.pacote && proc.procedimento_pacote.pacote.procedimento ) ? proc.procedimento_pacote.pacote.procedimento.search : 'Pacote' ) 
                                                : ( proc.acum_sessao ? proc.acum_sessao : proc.numero_sessao ) + '/' + proc.total_sessao )
                                            )
                                        ">
                                        <template v-slot:buttonposb>
                                            <div class="input-group-append" title="Número do Pacote" v-if="proc.id_atendimento_pacote">
                                                <div class="input-group-text">
                                                    {{ proc.id_atendimento_pacote }}
                                                </div>
                                            </div>
                                            <div class="input-group-append" v-if="!particular && proc.atendimento_pacote.id_atendimento_pacote && proc.atendimento_pacote.id_atendimento == proc.id_atendimento && proc.atendimento_pacote.pacote && proc.atendimento_pacote.pacote.fl_qtde_variada">
                                                <button type="button" class="btn btn-outline-primary" @click="editar_qtde_pacote(proc)">
                                                    <i class="fa-solid fa-plus-minus"></i>
                                                </button>
                                            </div>
                                        </template>
                                    </az-input>
                                </div>

                            </template>

                            <!-- Senha de Autorização -->
                            <div class="col col-12 col-md-2" v-if="!particular && ( !guia_consulta || proc.restricao.autorizacao )">

                                <template v-if="proc.id_plano_particular">

                                    <az-input
                                        :label="index ? '' : 'Senha de Autorização'"
                                        sizing="sm"
                                        type="select"
                                        service="planos.index"
                                        optionsMode="aql"
                                        optionsName="planos.particular"
                                        optionsData="id_plano"
                                        :optionsParm="{ where: { id_operadora: 1 } }"
                                        placeholder="Cobrança Operadora."
                                        v-model="proc.id_plano_particular"
                                        :edicao="( !proc.atendimento_procedimento_consumos || !proc.atendimento_procedimento_consumos.length ) && ( !dados || !dados.tem_pagamento_particular )"
                                        @input="recalc_preco_cirurgia(proc)">

                                        <template v-slot:buttonposb>
                                            <!-- Botão para Ações em Procedimentos Laboratoriais -->
                                            <div class="input-group-append" v-if="proc.procedimento.especialidade.id_especialidade == 2 || proc.procedimento.especialidade.id_especialidade == 67 || config_pag_medico">
                                                <button class="btn px-1" :class="proc.guia_separada ? 'btn-secondary' : 'btn-outline-secondary'"
                                                    type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                    <i class="fas fa-ellipsis-v"></i>
                                                </button>
                                                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <template v-if="proc.procedimento.especialidade.id_especialidade == 2 || proc.procedimento.especialidade.id_especialidade == 67">
                                                        <a class="dropdown-item" href="#" @click.prevent="nao_coletar(proc)">
                                                            <i class="fas fa-ban mr-2" v-if="proc.nao_coletar"></i>
                                                            {{ proc.nao_coletar ? '' : 'Não' }}
                                                            Coletar
                                                        </a>

                                                        <a href="#" class="dropdown-item" @click.prevent="abrir_help (proc.procedimento.id_procedimento)">
                                                            Informações do Exame
                                                        </a>
                                                    </template>

                                                    <a class="dropdown-item" href="#" @click.prevent="gerar_laudo(proc)" v-if="proc.id_atendimento_procedimento && !proc.laudo && proc.procedimento.id_status_laudo">
                                                        Gerar Laudo
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="toggle_pag_med(proc)" v-if="config_pag_medico">
                                                        <i class="fa-solid fa-dollar-sign fa-fw mr-2" v-if="proc.fl_pag_med"></i>
                                                        <span v-if="proc.fl_pag_med">(Retirar)</span>
                                                        Pagamento Direto para o Profissional
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="toggle_cid_proc(proc)">
                                                        CID do Procedimento
                                                    </a>
                                                </div>
                                            </div>
                                        </template>

                                    </az-input>

                                </template>

                                <template v-else>

                                    <az-input
                                        v-if="!excluindo"
                                        :key="'senha' + index"
                                        sizing="sm"
                                        :label="index ? '' : 'Senha de Autorização'"
                                        type="text"
                                        v-model="proc.st_codigo_autorizacao"
                                        :edicao="edicao"
                                        :validate="validate"
                                        :required="proc.restricao.autorizacao && proc.restricao.bloquear"
                                        :icon="!proc.restricao.cobertura
                                            ? ( 'fa fa-ban fa-fw ' + ( proc.restricao.bloquear ? 'text-danger' : '' ) )
                                            : ( 
                                                proc.restricao.autorizacao
                                                    ? 'fas fa-exclamation-triangle fa-fw ' + ( proc.restricao.cor ? 'text-' + proc.restricao.cor : 'text-primary' )
                                                    : ( proc.restricao.id ? 'fas fa-info-circle fa-fw ' : '' ) 
                                            )"
                                        :buttonColor="proc.restricao.bloquear ? ( proc.restricao.cobertura ? 'warning' : 'danger' ) : ''"
                                        :buttonToolTip="( proc.restricao.bloquear ? ( !proc.restricao.cobertura ? 'Item não coberto' : 'Senha Obrigatória' ) : ( proc.restricao.autorizacao ? 'Autorização Prévia' : '' ) ) + ' #' + proc.restricao.id + ( proc.restricao.id_operadora ? '' : 'g' ) + ( ' ' + proc.restricao.titulo )"
                                        @click="exibir_senha_datas(proc)">

                                        <template v-slot:buttonposb>
                                            <!-- Botão para Replicar Senha -->
                                            <div class="input-group-append">
                                                <button class="btn px-1" :class="proc.guia_separada ? 'btn-secondary' : 'btn-outline-secondary'"
                                                    type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                    <i class="fas fa-ellipsis-v"></i>
                                                </button>
                                                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_senha(proc)"
                                                        v-if="proc.st_codigo_autorizacao && lista_atendimento_procedimentos.length > 1">
                                                        Replicar Senha
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_senha(proc, true)"
                                                        v-if="proc.st_codigo_autorizacao && lista_atendimento_procedimentos.length > 1">
                                                        Replicar Data e Validade da Senha
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_info_proc(proc)"
                                                        v-if="( proc.procedimento
                                                            && ( proc.procedimento.id_classe_procedimento == 2
                                                                || proc.procedimento.fl_info_data
                                                                || ( proc.item_tabela && proc.item_tabela.fl_info_data )
                                                                || is_externo ) )">
                                                        Replicar Informações do Procedimento
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_equipe_proc(proc)"
                                                        v-if="proc.procedimento
                                                            && proc.procedimento.id_classe_procedimento == 2
                                                            && lista_equipe(proc).length">
                                                        Replicar Equipe
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="selec_guia_separada(proc)">
                                                        <i class="fa fa-check mr-2" aria-hidden="true" v-if="proc.guia_separada"></i>
                                                        Separar Guia
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="toggle_block_fatura(proc)" v-if="opcao_sobre_fatura(proc)">
                                                        <i class="fa fa-check mr-2" aria-hidden="true" v-if="proc.js_solics && proc.js_solics.fl_block_valor"></i>
                                                        Não Faturar 
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="selec_apenas_cobranca(proc)" v-if="opcao_apenas_cobranca(proc)">
                                                        <i class="fa fa-check mr-2" aria-hidden="true" v-if="proc.apenas_cobranca"></i>
                                                        Apenas Cobrança 
                                                    </a>
                                                    <a class="dropdown-item" href="#" @click.prevent="lancar_outras_datas(proc)" v-if="!index && proc.procedimento.id_classe_procedimento == 17 && ( proc.procedimento.fl_info_data || ( proc.item_tabela && proc.item_tabela.fl_info_data ) ) && !proc.atendimento_pacote">
                                                        Lançar Outras Datas
                                                    </a>
                                                    <a class="dropdown-item" href="#" @click.prevent="cobranca_particular(proc)"
                                                        :class="( proc.atendimento_procedimento_consumos && proc.atendimento_procedimento_consumos.length && lista_consumo(proc).length ) ? 'disabled' : ''">
                                                        Cobrança Particular
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="toggle_cid_proc(proc)">
                                                        CID do Procedimento
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="proc.senha_datas = false" v-if="proc.st_codigo_autorizacao && proc.senha_datas">
                                                        Ocultar datas (autorização e validade) da Senha
                                                    </a>
                                                    <template v-if="proc.procedimento.especialidade.id_especialidade == 2 || proc.procedimento.especialidade.id_especialidade == 67">
                                                        <div class="dropdown-divider"></div>
                                                        <a class="dropdown-item" href="#" @click.prevent="nao_coletar(proc)">
                                                            <i class="fas fa-ban mr-2" v-if="proc.nao_coletar"></i>
                                                            {{ proc.nao_coletar ? '' : 'Não' }}
                                                            Coletar
                                                        </a>
                                                        <a href="#" class="dropdown-item" @click.prevent="abrir_help (proc.procedimento.id_procedimento)">
                                                            Informações do Exame
                                                        </a>
                                                    </template>

                                                    <a class="dropdown-item" href="#" @click.prevent="gerar_laudo(proc)" v-if="proc.id_atendimento_procedimento && !proc.laudo && proc.procedimento.id_status_laudo">
                                                        Gerar Laudo
                                                    </a>

                                                    <template v-if="proc.id_atendimento_procedimento && proc.procedimento && proc.procedimento.id_classe_procedimento == 2">
                                                        <div class="dropdown-divider"></div>
                                                        <a class="dropdown-item" href="#" @click.prevent="alterar_profissional(proc)">
                                                            Alterar Profissional
                                                        </a>
                                                    </template>
                                                </div>
                                            </div>
                                        </template>

                                    </az-input>

                                </template>

                            </div>

                            <div v-else class="col col-12 col-md-2">

                                <az-input
                                    :label="index ? '' : 'Senha de Autorização'"
                                    sizing="sm"
                                    type="select"
                                    service="planos.index"
                                    optionsMode="aql"
                                    optionsName="planos.particular"
                                    optionsData="id_plano"
                                    :optionsParm="{ where: { id_operadora: 1 } }"
                                    placeholder="Cobrança Principal"
                                    v-model="proc.id_plano_particular"
                                    :edicao="!proc.atendimento_procedimento_consumos || !proc.atendimento_procedimento_consumos.length || !lista_consumo(proc).length"
                                    @input="recalc_preco_cirurgia(proc)">

                                    <template v-slot:buttonposb>
                                        <!-- Botão para Replicar Senha -->
                                        <div class="input-group-append" v-if="proc.procedimento.especialidade.id_especialidade == 2 || proc.procedimento.especialidade.id_especialidade == 67 || config_pag_medico || ( proc.id_atendimento_procedimento && !proc.laudo && proc.procedimento.id_status_laudo )">
                                            <button class="btn px-1" :class="proc.guia_separada ? 'btn-secondary' : 'btn-outline-secondary'"
                                                type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                <template v-if="proc.procedimento.especialidade.id_especialidade == 2 || proc.procedimento.especialidade.id_especialidade == 67">
                                                    <a class="dropdown-item" href="#" @click.prevent="nao_coletar(proc)">
                                                        <i class="fas fa-ban mr-2" v-if="proc.nao_coletar"></i>
                                                        {{ proc.nao_coletar ? '' : 'Não' }}
                                                        Coletar
                                                    </a>

                                                    <a href="#" class="dropdown-item" @click.prevent="abrir_help (proc.procedimento.id_procedimento)">
                                                        Informações do Exame
                                                    </a>
                                                </template>

                                                <a class="dropdown-item" href="#" @click.prevent="gerar_laudo(proc)" v-if="proc.id_atendimento_procedimento && !proc.laudo && proc.procedimento.id_status_laudo">
                                                    Gerar Laudo
                                                </a>

                                                <a class="dropdown-item" href="#" @click.prevent="toggle_pag_med(proc)" v-if="config_pag_medico">
                                                    <i class="fa-solid fa-dollar-sign fa-fw mr-2" v-if="proc.fl_pag_med"></i>
                                                    <span v-if="proc.fl_pag_med">(Retirar)</span>
                                                    Pagamento Direto para o Profissional
                                                </a>
                                            </div>
                                        </div>

                                        <template v-else-if="proc.procedimento.id_classe_procedimento == 2">
                                            <div class="input-group-append">
                                                <button class="btn px-1 btn-outline-secondary"
                                                    type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                    <i class="fas fa-ellipsis-v"></i>
                                                </button>
                                                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_info_proc(proc)">
                                                        Replicar Informações do Procedimento
                                                    </a>

                                                    <a class="dropdown-item" href="#" @click.prevent="copiar_equipe_proc(proc)" v-if="proc.procedimento && lista_equipe(proc).length">
                                                        Replicar Equipe
                                                    </a>
                                                </div>
                                            </div>
                                        </template>
                                    </template>

                                </az-input>

                            </div>

                        </template>

                    </div>

                    <template v-if="show_consumo || proc.incluir_consumo">

                        <div class="form-row" v-for="(consumo, icx) in lista_consumo(proc, false)" :key="index + '-c-' + icx">
                            <div class="col col-12" :class="( consultorio && !conta ) ? 'col-md-10' : 'col-md-6'">
                                <az-input
                                    class="ml-3"
                                    :edicao="false"
                                    sizing="sm"
                                    v-model="consumo.search">
                                    <template v-slot:button-pre>
                                        <div class="input-group-prepend" v-if="( !consultorio || assistencia || !$store.state.user.medico || config_medico_lancar_material_consultorio ) && ( !consumo.id_taxa || !consumo.tch )">
                                            <button class="btn btn-outline-danger" type="button" id="button-addon1" @click="excluirConsumo(consumo)" :disabled="!pode_alterar_proc(proc, false)">
                                                <i class="fa fa-minus fa-fw" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </template>
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append">
                                            <span class="input-group-text" id="basic-addon2" v-if="consumo.fl_pag_med">
                                                <i class="fa-solid fa-dollar-sign"></i>
                                            </span>
                                            <span class="input-group-text" id="basic-addon2" v-if="consumo.plano">{{ consumo.plano.search }}</span>
                                            <span class="input-group-text" id="basic-addon2" v-if="( consumo.centro && consumo.centro.st_sigla ) || ( dados && dados.atp_cc )">
                                                <span title="Centro de Custo." class="small">
                                                    <a href="#" @click.prevent="alterar_centro_custo(consumo, consumo.id_atendimento_procedimento_consumo ? false : true)" class="card-link">
                                                        <i class="fa-solid fa-location-dot" v-if="!consumo.centro"></i>
                                                        <template v-else>
                                                            {{ consumo.centro.st_sigla }}
                                                        </template>
                                                    </a>
                                                </span>
                                            </span>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                            <div class="col col-12 col-md-2">
                                <az-input
                                    type="number"
                                    sizing="sm"
                                    required
                                    :validate="validate"
                                    :rules="['zero-positivo']"
                                    :edicao="!consultorio || assistencia || !$store.state.user.medico || config_medico_lancar_material_consultorio"
                                    v-model="consumo.in_quantidade">
                                    <template v-slot:button-pre>
                                        <div class="input-group-append" v-if="proc.id_atendimento_procedimento_empacotado || consumo.id_atendimento_procedimento_empacotado">
                                            <button class="btn btn-outline-primary" type="button" data-toggle="tooltip" data-placement="top" title="Empacotado" @click="exibir_info_pacote(proc.id_atendimento_procedimento_empacotado ? proc : consumo)">
                                                <i class="fas fa-archive fa-fw"></i>
                                            </button>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                            <template v-if="( !consultorio || conta ) && !dados.block_valores">
                                <div class="col col-12 col-md-2">
                                    <az-input
                                        :key="'valor_consumo' + icx"
                                        icon="fa-solid fa-brazilian-real-sign fa-fw"
                                        sizing="sm"
                                        :type="editando_valores ? 'number' : 'money'"
                                        v-if="tem_valor_editavel(proc, consumo)"
                                        vif2="!excluindo && dados.atendimento.plano.fl_valor_editavel && dados.atendimento.plano.valor_editavel && !proc.id_atendimento_procedimento_empacotado"
                                        v-model="consumo.dc_valor_unitario">
                                    </az-input>

                                    <az-input
                                        v-else
                                        sizing="sm"
                                        icon="fa-solid fa-brazilian-real-sign fa-fw"
                                        :edicao="false"
                                        :value="$util.formatMoeda(consumo.dc_valor_unitario, 6, 2)"  />
                                </div>
                                <div class="col col-12 col-md-2">
                                    <az-input
                                        type="label"
                                        sizing="sm"
                                        :edicao="false"
                                        :label="$util.formatMoeda(consumo.in_quantidade * consumo.dc_valor_unitario)">
                                        <template v-slot:button-pre>
                                            <div class="input-group-prepend">
                                                <button type="button" class="btn btn-outline-secondary" v-if="tem_valor_editavel(proc, consumo)"
                                                    @click="$refs.edit_valor_total.editar(consumo, 'in_quantidade', 'dc_valor_unitario')">
                                                    <i class="fas fa-equals fa-fw"></i>
                                                </button>
                                                <span class="input-group-text" id="basic-addon2" v-else>
                                                    <i class="fas fa-equals fa-fw"></i>
                                                </span>
                                            </div>
                                        </template>
                                        <template v-slot:buttonposb>
                                            <div class="input-group-append" v-if="config_pag_medico && ( dados.plano.id_operadora == 1 || proc.id_plano_particular || consumo.id_plano )">
                                                <button class="btn btn-outline-secondary px-1" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                    <i class="fas fa-ellipsis-v"></i>
                                                </button>
                                                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <a class="dropdown-item" href="#" @click.prevent="toggle_pag_med(consumo)" v-if="config_pag_medico">
                                                        <i class="fa-solid fa-dollar-sign fa-fw mr-2" v-if="consumo.fl_pag_med"></i>
                                                        <span v-if="consumo.fl_pag_med">(Retirar)</span>
                                                        Pagamento Direto para o Profissional
                                                    </a>
                                                </div>
                                            </div>
                                        </template>

                                    </az-input>
                                </div>
                            </template>
                        </div>

                    </template>

                    <div class="form-row" v-if="!conta && ( proc.incluir_consumo || proc.st_codigo_autorizacao || ( proc.procedimento && !dados.plano.operadora.fl_sus && ( proc.procedimento.id_classe_procedimento == 2 || proc.procedimento.fl_info_data || ( proc.item_tabela && proc.item_tabela.fl_info_data ) || is_externo ) ) )">
                        <div class="col col-12" :class="consultorio ? 'col-md-10' : 'col-md-6'">
                            <template v-if="proc.incluir_consumo">
                                <div class="ml-3 mb-3">
                                    <az-input
                                        sizing="sm"
                                        type="autocomplete"
                                        optionsMode="aql"
                                        optionsName="consumo.index"
                                        service="consumo.index"
                                        :optionsParm="{ tipo: proc.incluir_consumo, id_operadora: ( proc.plano_particular ? proc.plano_particular.id_operadora : ( novoPlanoCobranca ? novoPlanoCobranca.id_operadora : ( proc.id_plano_particular ? proc.id_plano_particular : dados.plano.operadora.id_operadora ) ) ) }"
                                        :optionsSync="true"
                                        :placeholder="placeholder_consumo(proc)"
                                        :edicao="( !consultorio || assistencia || !$store.state.user.medico || config_medico_lancar_material_consultorio ) && pode_alterar_proc(proc, false)"
                                        v-model="novoComponente"
                                        :procsigla="true"
                                        @input="incluirComponente (proc)"
                                        @onEnter2="incluirComponente (proc)">
                                        <template v-slot:button-pre>
                                            <div class="input-group-prepend">
                                                <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-expanded="false" title="Incluir material, medicamento ou taxa.">
                                                    <template v-if="proc.incluir_consumo !== true">
                                                        {{ proc.incluir_consumo }}
                                                    </template>
                                                    <i class="fas fa-cart-plus fa-fw" aria-hidden="true" v-else></i>
                                                </button>
                                                <div class="dropdown-menu">
                                                    <a class="dropdown-item" href="#" @click.prevent="change_filtro_consumo(proc, true)">Todos</a>
                                                    <div role="separator" class="dropdown-divider"></div>
                                                    <a class="dropdown-item" href="#" @click.prevent="change_filtro_consumo(proc, 'MAT')">Materiais</a>
                                                    <a class="dropdown-item" href="#" @click.prevent="change_filtro_consumo(proc, 'MED')">Medicamentos</a>
                                                    <a class="dropdown-item" href="#" @click.prevent="change_filtro_consumo(proc, 'T')">Taxas</a>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:buttonposb>
                                            <div class="input-group-append">
                                                <span class="input-group-text" id="basic-addon2" v-if="dados && dados.atp_cc">
                                                    <span title="Centro de Custo." class="small">
                                                        <a href="#" @click.prevent="alterar_centro_custo(centro_base, true)" class="card-link">
                                                            <i class="fa-solid fa-location-dot" v-if="!centro_base.centro"></i>
                                                            <template v-else>{{ centro_base.centro.st_sigla }}</template>
                                                        </a>
                                                    </span>
                                                </span>
                                            </div>

                                            <div class="input-group-append" v-if="proc.procedimento && proc.procedimento.id_classe_procedimento == 18 && ( !consultorio || assistencia || !$store.state.user.medico ) && pode_alterar_proc(proc, false)">
                                                <button class="btn btn-outline-primary" type="button" @click="buscar_prescricao (proc)">
                                                    <i class="fa-solid fa-pills"></i>
                                                </button>
                                            </div>

                                            <div class="input-group-append" v-else-if="proc.procedimento && ( !consultorio || assistencia || !$store.state.user.medico ) && pode_alterar_proc(proc, false) && !lista_consumo(proc).length">
                                                <button class="btn btn-outline-secondary" type="button" @click="buscar_componentes (proc)" title="Buscar Componentes">
                                                    <i class="fa-solid fa-angles-down fa-fw"></i>
                                                </button>
                                            </div>
                                        </template>
                                    </az-input>

                                    <div v-if="proc.medico && !( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 )">
                                        <span class="small text-muted">{{ proc.medico.search }}</span>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="( proc.procedimento
                                && ( proc.procedimento.id_classe_procedimento == 2  
                                    || proc.procedimento.fl_info_data
                                    || ( proc.item_tabela && proc.item_tabela.fl_info_data )
                                    || is_externo ) 
                                && !dados.plano.operadora.fl_sus )">
                                <div class="form-row">
                                    <div class="col col-12" :class="( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 ) ? 'col-md-3' : 'col-md-4'" v-if="is_externo || proc.procedimento.fl_info_data || ( proc.item_tabela && proc.item_tabela.fl_info_data )">
                                        <az-input
                                            buttonToolTip="Data de Execução"
                                            sizing="sm"
                                            icon="fa fa-calendar-o fa-fw"
                                            type="date"
                                            placeholder="Data"
                                            :validate="validate"
                                            v-model="proc.dt_execucao" />
                                    </div>
                                    <div class="col col-12" :class="( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 ) ? 'col-md-3' : 'col-md-4'">
                                        <az-input
                                            buttonToolTip="Hora Inicial"
                                            sizing="sm"
                                            icon="fas fa-play fa-fw"
                                            type="time"
                                            placeholder="Inicial"
                                            :required="!( proc.procedimento && proc.procedimento.id_classe_procedimento == 17 ) && !particular && cirurg_campos_obrig"
                                            :validate="validate"
                                            v-model="proc.proc_inicio" />
                                    </div>
                                    <div class="col col-12" :class="( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 ) ? 'col-md-3' : 'col-md-4'">
                                        <az-input
                                            sizing="sm"
                                            buttonToolTip="Hora Final"
                                            icon="fas fa-stop fa-fw"
                                            type="time"
                                            placeholder="Final"
                                            :required="!( proc.procedimento && proc.procedimento.id_classe_procedimento == 17 ) && !particular && cirurg_campos_obrig"
                                            :validate="validate"
                                            v-model="proc.proc_termino"
                                            @input="recalc_preco_cirurgia(proc)" />
                                    </div>
                                    <div class="col col-12 col-md-3" v-if="( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 )">
                                        <az-input
                                            sizing="sm"
                                            type="select"
                                            placeholder="via..."
                                            :required="cirurg_campos_obrig"
                                            :validate="validate"
                                            :optionsList="[{id: 'U', search: 'Via Única'},{id: 'M', search: 'Mesma Via'},{id: 'D', search: 'Diferente Via'}]"
                                            optionsData="id"
                                            v-model="proc.st_proc_via"
                                            @input="recalc_preco_cirurgia(proc)" />
                                    </div>
                                    <div class="col col-12 col-md-3" v-if="( proc.procedimento && proc.procedimento.id_classe_procedimento == 2 && proc.medico )">
                                        <az-input
                                            sizing="sm"
                                            :edicao="false"
                                            v-if="proc.medico && !proc.medico.tem_certificado"
                                            v-model="proc.medico.search">
                                            <template v-slot:button-pre>
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-primary" type="button" title="Editar Cirurgião"
                                                        @click="proc.id_medico = null; proc.medico.tem_certificado = !proc.medico.tem_certificado" v-if="!consultorio">
                                                        <i class="fa fa-user-md" aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            </template>
                                        </az-input>

                                        <az-input
                                            v-else
                                            sizing="sm"
                                            type="autocomplete"
                                            optionsMode="aql"
                                            service="medico_procedimento_unidades.index"
                                            optionsData="id_pessoa"
                                            optionsDisplay="search"
                                            :optionsParm="{
                                                troca_em_procedimento: true,
                                                id_procedimento: proc.procedimento.id_procedimento,
                                                id_especialidade: proc.procedimento.id_especialidade,
                                                id_operadora: dados.plano.operadora.id_operadora,
                                            }"
                                            required
                                            :validate="validate"
                                            v-model="proc.id_medico">
                                            <template v-slot:button-pre>
                                                <div class="input-group-append">
                                                    <span class="input-group-text" id="basic-addon2">
                                                        <i class="fa fa-user-md" aria-hidden="true"></i>
                                                    </span>
                                                </div>
                                            </template>
                                        </az-input>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <div class="col col-12 col-md-2">
                            <!-- Inclusão de componente -->
                            <template v-if="( proc.incluir_consumo && !consultorio && !particular )">
                                <az-input
                                    sizing="sm"
                                    type="select"
                                    service="planos.index"
                                    optionsMode="aql"
                                    optionsName="planos.particular"
                                    :optionsParm="{ where: { id_operadora: 1 } }"
                                    placeholder="Cobrança Operadora"
                                    :edicao="!proc.plano_particular"
                                    v-model="novoPlanoCobranca" />
                            </template>

                            <template v-else-if="( !proc.incluir_consumo && proc.procedimento && proc.procedimento.id_classe_procedimento == 2 && !dados.plano.operadora.fl_sus && !particular )">
                                <az-input
                                    sizing="sm"
                                    type="select"
                                    placeholder="técnica..."
                                    :required="cirurg_campos_obrig"
                                    :validate="validate"
                                    :optionsList="[{id: 'C', search: 'Convencional'},{id: 'V', search: 'Videolaparoscópica'}]"
                                    optionsData="id"
                                    v-model="proc.st_proc_tecnica" />
                            </template>
                        </div>
                        <template v-if="!consultorio && proc.senha_datas">
                            <div class="col col-12 col-md-2" v-if="proc.st_codigo_autorizacao">
                                <az-input
                                    sizing="sm"
                                    icon="fas fa-plus fa-fw"
                                    buttonToolTip="Data da Autorização"
                                    type="date"
                                    required
                                    v-model="proc.dt_autorizacao"
                                    :validate="validate" />
                            </div>
                            <div class="col col-12 col-md-2" v-if="proc.st_codigo_autorizacao">
                                <az-input
                                    sizing="sm"
                                    buttonToolTip="Validade da Autorização"
                                    type="date"
                                    required
                                    v-model="proc.dt_validade_autorizacao"
                                    :validate="validate">
                                    <template v-slot:button-pre>
                                        <div class="input-group-prepend">
                                            <button class="btn btn-outline-secondary" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" title="Validade da Senha">
                                                <i class="fas fa-check fa-fw"></i>               
                                            </button>
                                            <div class="dropdown-menu">
                                                <a class="dropdown-item" href="#" @click.prevent="definir_validade(proc, 30)">+30 dias</a>
                                                <a class="dropdown-item" href="#" @click.prevent="definir_validade(proc, 60)">+60 dias</a>
                                            </div>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                        </template>
                    </div>

                    <template v-if="proc.info_cid">

                        <div class="form-row">
                            <div class="col col-12" :class2="( consultorio && !conta ) ? 'col-md-10' : 'col-md-6'">
                                <az-input
                                    placeholder="Informar o CID do procedimento..."
                                    class="ml-3"
                                    sizing="sm"
                                    type="autocomplete"
                                    optionsMode="aql"
                                    service="cids.index"
                                    optionsDisplay="search"
                                    required
                                    :validate="validate"
                                    v-model="proc.cid">
                                    <template v-slot:button-pre>
                                        <div class="input-group-prepend">
                                            <span class="input-group-text" title="CID">
                                                CID
                                            </span>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                        </div>

                    </template>

                    <div class="form-row" v-if="is_odonto && proc.js_solics && proc.procedimento && proc.procedimento.id_classe_procedimento > 0">
                        <div class="col col-12 col-md-6">
                            <az-input
                                class="ml-3"
                                icon="fa-solid fa-tooth fa-fw"
                                sizing="sm"
                                type="select"
                                placeholder="Dente..."
                                :optionsList="dentes"
                                optionsData="id"
                                v-model="proc.js_solics.cod_dente" />
                        </div>
                        <div class="col col-12 col-md-4">
                            <az-input
                                icon="fa-solid fa-teeth fa-fw"
                                sizing="sm"
                                type="select"
                                placeholder="Região..."
                                :optionsList="regioes"
                                optionsData="id"
                                v-model="proc.js_solics.cod_regiao" />
                        </div>
                        <div class="col col-12 col-md-2">
                            <az-input
                                icon="fa-solid fa-arrows-left-right fa-fw"
                                sizing="sm"
                                type="text"
                                placeholder="Face..."
                                v-model="proc.js_solics.dente_face" />
                        </div>
                    </div>

                    <!-- Equipe -->
                    <template v-if="( !consultorio || assistencia ) && proc.procedimento.id_especialidade != 2 && proc.procedimento.id_especialidade != 67">
                        <div class="form-row" v-for="(equipe, iex) in lista_equipe(proc)" :key="index + '-e-' + iex">
                            <div class="col col-12 col-md-5">
                                <az-input
                                    placeholder="Profissional..."
                                    class="ml-3"
                                    sizing="sm"
                                    type="select"
                                    optionsMode="aql"
                                    optionsData="id_pessoa"
                                    service="medicos.index"
                                    :optionsName="'medicos.index' + ( equipe.id_grau_participacao == 7 ? '.anest' : '' )"
                                    :optionsParm=" { anestesista: ( equipe.id_grau_participacao == 7 ) }"
                                    :edicao="!consultorio || assistencia"
                                    required
                                    :validate="validate"
                                    v-model="equipe.id_medico">
                                    <template v-slot:button-pre>
                                        <div class="input-group-prepend" v-if="( !consultorio || assistencia ) && !conta">
                                            <button class="btn btn-outline-danger" type="button" id="button-addon2"
                                                title="Remover membro da equipe."
                                                @click="removerEquipe(equipe)">
                                                <i class="fas fa-minus fa-fw"></i>
                                            </button>
                                        </div>
                                    </template>
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append" v-if="equipe.js_info && equipe.js_info.coop" title="Cooperado">
                                            <div class="input-group-text">
                                                <i class="fa-solid fa-c"></i>
                                            </div>
                                        </div>
                                        <div class="input-group-append" v-if="!consultorio && ( equipe.dc_valor > 0 || equipe.id_medico_rateio !== null )">
                                            <button class="btn btn-outline-secondary" type="button" id="button-addon2"
                                                data-toggle="tooltip" data-placement="top"
                                                title="Definir médico diferente para o rateio."
                                                @click="equipe.id_medico_rateio === null
                                                    ? equipe.id_medico_rateio = ''
                                                    : equipe.id_medico_rateio = null">
                                                <i class="fas fa-directions"></i>
                                            </button>
                                        </div>
                                    </template>
                                </az-input>

                                <az-input
                                    v-if="( !consultorio || assistencia ) && equipe.id_medico_rateio !== null"
                                    placeholder="Selecione o Médico do Rateio..."
                                    class="ml-5"
                                    sizing="sm"
                                    icon="fas fa-directions fa-fw"
                                    type="select"
                                    optionsMode="aql"
                                    optionsData="id_pessoa"
                                    service="medicos.index"
                                    required
                                    :validate="validate"
                                    v-model="equipe.id_medico_rateio">
                                </az-input>
                            </div>
                            <div class="col col-12 col-md-3">
                                <az-input
                                    sizing="sm"
                                    placeholder="Especialidade..."
                                    type="select"
                                    :optionsList="especialidades_equipe(equipe)"
                                    optionsData="id"
                                    required
                                    :validate="validate"
                                    :edicao="( !consultorio || assistencia )"
                                    v-model="equipe.id_especialidade" />
                            </div>
                            <div class="col col-12 col-md-2">
                                <az-input
                                    icon="fa-solid fa-brazilian-real-sign fa-fw"
                                    sizing="sm"
                                    placeholder="Valor"
                                    :type="editando_valores ? 'number' : 'money'"
                                    v-model="equipe.dc_valor"
                                    v-if="!consultorio && tem_valor_editavel(proc) && equipe.dc_valor !== null">
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append" v-if="equipe.id_grau_participacao != 7 && proc.in_quantidade > 1">
                                            <span class="input-group-text" id="basic-addon2">
                                                <i class="fas fa-equals mr-2"></i>
                                                {{ $util.formatMoeda(equipe.dc_valor * proc.in_quantidade) }}
                                            </span>
                                        </div>
                                        <div class="input-group-append" v-if="proc.item_tabela && proc.item_tabela.fl_tiss_zerado">
                                            <button type="button" class="btn btn-outline-secondary" @click="equipe.fl_block_zerar = !equipe.fl_block_zerar" title="Zerar o valor na guia TISS.">
                                                <i class="fa-brands fa-creative-commons-nc" v-if="!equipe.fl_block_zerar"></i>
                                                <i class="fa-regular fa-circle-dollar" v-else></i>
                                            </button>
                                        </div>
                                    </template>
                                </az-input>

                                <az-input
                                    icon="fa-solid fa-brazilian-real-sign fa-fw"
                                    sizing="sm"
                                    placeholder="Valor"
                                    type="text"
                                    :edicao="false"
                                    :value="$util.formatMoeda(equipe.dc_valor)"
                                    v-else-if="!consultorio && equipe.dc_valor && equipe.dc_valor > 0">
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append" v-if="equipe.id_grau_participacao != 7 && proc.in_quantidade > 1">
                                            <span class="input-group-text" id="basic-addon2">
                                                <i class="fas fa-equals mr-2"></i>
                                                {{ $util.formatMoeda(equipe.dc_valor * proc.in_quantidade) }}
                                            </span>
                                        </div>
                                        <div class="input-group-append" v-if="proc.item_tabela && proc.item_tabela.fl_tiss_zerado">
                                            <button type="button" class="btn btn-outline-secondary" @click="equipe.fl_block_zerar = !equipe.fl_block_zerar" title="Zerar o valor na guia TISS.">
                                                <i class="fa-brands fa-creative-commons-nc" v-if="!equipe.fl_block_zerar"></i>
                                                <i class="fa-regular fa-circle-dollar" v-else></i>
                                            </button>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                            <div class="col col-12 col-md-2">
                                <az-input
                                    :edicao="false"
                                    sizing="sm"
                                    v-model="equipe.grau_participacao.st_grau_participacao">
                                    <template v-slot:buttonposb>
                                        <div class="input-group-append" v-if="equipe.id_rateio_medico">
                                            <span class="input-group-text" id="basic-addon2" v-if="equipe.fl_pag_med" title="Pagamento Direto para o Profissional">
                                                <i class="fa-solid fa-dollar-sign"></i>
                                            </span>
                                            <span class="input-group-text" id="basic-addon2">
                                                <small>#{{ equipe.id_rateio_medico }}</small>
                                            </span>
                                        </div>

                                        <div class="input-group-append" v-if="config_pag_medico && ( dados.plano.id_operadora == 1 || proc.id_plano_particular )">
                                            <button class="btn btn-outline-secondary px-1" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                <a class="dropdown-item" href="#" @click.prevent="toggle_pag_med(equipe)" v-if="config_pag_medico">
                                                    <i class="fa-solid fa-dollar-sign fa-fw mr-2" v-if="equipe.fl_pag_med"></i>
                                                    <span v-if="equipe.fl_pag_med">(Retirar)</span>
                                                    Pagamento Direto para o Profissional
                                                </a>
                                            </div>
                                        </div>
                                    </template>
                                </az-input>
                            </div>
                        </div>
                    </template>

                    <!-- Inclusão da Equipe Cirúrgica -->
                    <!-- ( proc.procedimento && ( proc.procedimento.id_classe_procedimento == 2 || _.get(proc, 'procedimento') ) ) -->
                    <div class="form-row mb-2" v-if="tem_equipe(proc) && ( !proc.id_atendimento_procedimento || proc.incluir_consumo )">
                        <div class="col col-12">
                            <az-input
                                class="ml-3"
                                type="select"
                                optionsMode="json"
                                :optionsList="lista_graus(proc)"
                                sizing="sm"
                                icon="fas fa-user-md fa-fw"
                                placeholder="Informar demais membro da equipe..."
                                buttonToolTip="Equipe Cirúrgica"
                                v-model="proc.novo_membro"
                                :iconEnd="proc.item_tabela && proc.item_tabela.in_numero_auxiliares ?
                                    { text: proc.item_tabela.in_numero_auxiliares +
                                        ( proc.item_tabela.in_numero_auxiliares == 1 ? ' Auxiliar' : ' Auxiliares' ) } : null"
                                @input="incluirMembro (proc)">
                                <template v-slot:buttonposb>
                                    <div class="input-group-append" v-if="assistencia 
                                                    && proc.procedimento
                                                    && proc.procedimento.id_classe_procedimento == 2
                                                    && lista_equipe(proc).length">
                                        <button class="btn px-1 btn-outline-secondary"
                                            type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="$store.state.loading">
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                        <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                            <a class="dropdown-item" href="#" @click.prevent="copiar_info_proc(proc)"
                                                v-if="false 
                                                    && ( proc.procedimento
                                                    && ( proc.procedimento.id_classe_procedimento == 2
                                                        || proc.procedimento.fl_info_data
                                                        || ( proc.item_tabela && proc.item_tabela.fl_info_data )
                                                        || is_externo ) )">
                                                Replicar Informações do Procedimento
                                            </a>

                                            <a class="dropdown-item" href="#" @click.prevent="copiar_equipe_proc(proc)"
                                                v-if="proc.procedimento
                                                    && proc.procedimento.id_classe_procedimento == 2
                                                    && lista_equipe(proc).length">
                                                Replicar Equipe
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </az-input>
                        </div>
                    </div>

                    <!-- Exibir seleção de pacote -->
                    <div class="form-row mb-2" v-if="indexProcPacote == index">
                        <div class="col col-12">
                            <div class="list-group">
                                <button type="button" class="list-group-item list-group-item-action"
                                    v-for="(pacote, index) in pacotes" :key="index" @click="selecionar_pacote(pacote)">
                                    <i class="fas fa-layer-group mr-2 text-primary"></i>
                                    {{ pacote.search }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Exibição do Pacote Selecionado -->
                    <div class="form-row mb-2" v-if="proc.atendimento_pacote &&
                        ( proc.atendimento_pacote.id_atendimento == proc.id_atendimento || !proc.atendimento_pacote.id_atendimento )">
                        <div class="col col-12" :class="consultorio ? 'col-md-10' : 'col-md-6'">
                            <az-input
                                v-if="proc.procedimento_pacote"
                                sizing="sm"
                                icon="fas fa-minus fa-fw"
                                buttonText="fas fa-minus fa-fw"
                                :buttonIcon="true"
                                buttonColor="outline-danger"
                                @clickButton="removerPacote(proc)"
                                :edicao="false"
                                type="text"
                                v-model="proc.procedimento_pacote.search">
                            </az-input>

                            <az-input
                                v-else-if="proc.atendimento_pacote.procedimento_pacote"
                                sizing="sm"
                                icon="fas fa-minus fa-fw"
                                buttonText="fas fa-minus fa-fw"
                                :buttonIcon="true"
                                buttonColor="outline-danger"
                                @clickButton="removerPacote(proc)"
                                :edicao="false"
                                type="text"
                                v-model="proc.atendimento_pacote.procedimento_pacote.search">
                            </az-input>
                        </div>

                        <template v-if="!consultorio">

                            <div class="col col-12 col-md-2">
                                <az-input
                                    sizing="sm"
                                    :edicao="false"
                                    type="text"
                                    value="1" />
                            </div>

                            <div class="col col-12 col-md-2">

                                <!-- Valor do Pacote -->

                                <template v-if="!dados.block_valores">

                                    <az-input
                                        type="money"
                                        sizing="sm"
                                        icon="fa-solid fa-brazilian-real-sign fa-fw"
                                        v-if="tem_valor_editavel(proc)"
                                        v-model="proc.atendimento_pacote.dc_valor_pacote">
                                    </az-input>

                                    <az-input
                                        v-else
                                        sizing="sm"
                                        icon="fa-solid fa-brazilian-real-sign fa-fw"
                                        :edicao="false"
                                        :value="$util.formatMoeda(proc.atendimento_pacote.dc_valor_pacote)">
                                    </az-input>

                                </template>

                            </div>

                        </template>
                    </div>

                </div>

                <div>

                    <div class="form-row" v-if="!conta">

                        <div class="col col-12" :class="( !consultorio ) ? 'col-md-10' : ''">

                            <az-input
                                placeholder="Procedimento"
                                icon="fa fa-plus"
                                sizing="sm"
                                type="autocomplete"
                                optionsMode="aql"
                                optionsName2="procedimentos.combos.agenda"
                                :optionsName="( ( $store.state.user.proc_oper ) ? 'procedimentos.combos.agenda.op.' 
                                    + ( novoPlanoCobrancaProcedimento ? 'p.' + novoPlanoCobrancaProcedimento.id_plano : 
                                        ( dados.plano.operadora.id_operadora == 1 ? 'p.' + dados.plano.id_plano : dados.plano.operadora.id_operadora ) ) : 'procedimentos.combos.agenda' )
                                    + ( dados.id_unidade_operacao ? '.uo' + dados.id_unidade_operacao : '' )"
                                :optionsParm="( $store.state.user.proc_oper ) 
                                    ? ( novoPlanoCobrancaProcedimento 
                                        ? { id_operadora: novoPlanoCobrancaProcedimento.id_operadora, id_plano: novoPlanoCobrancaProcedimento.id_plano, id_unidade_operacao: dados.id_unidade_operacao }
                                        : { id_operadora: dados.plano.operadora.id_operadora, id_plano: dados.plano.id_plano, id_unidade_operacao: dados.id_unidade_operacao } 
                                    )
                                    : {
                                        id_unidade_operacao: dados.id_unidade_operacao, 
                                    }"
                                :optionsSync="false"
                                v-model="novoProcedimento"
                                ref="info_procedimento"
                                :procsigla="true"
                                @input="incluirProcedimento (null, null, '')">
                                <template v-slot:buttonposb>
                                    <template v-if="novoProcedimento && novoProcedimento.amostras && novoProcedimento.amostras.length">
                                        <az-input
                                            :mb="false"
                                            sizing="sm"
                                            type="select"
                                            :optionsList="novoProcedimento.amostras"
                                            optionsData="id_amostra_material"
                                            placeholder="Selecione o material..."
                                            v-model="novoProcedimento.id_amostra_material"
                                            @onEnter="incluirProcedimento (null, null, '')">
                                            <template v-slot:buttonposb>
                                                <div class="input-group-append">
                                                    <button type="button" class="btn btn-sm btn-outline-success" @click.prevent="incluirProcedimento (null, null, '')">
                                                        <i class="fa-solid fa-check"></i>
                                                    </button>
                                                </div>
                                            </template>
                                        </az-input>
                                    </template>

                                    <template v-if="novoProcedimento && novoProcedimento.js_complementos && novoProcedimento.js_complementos.length && base_novo_procedimento">
                                        <div class="input-group-append">
                                            <procedimento-regiao v-model="base_novo_procedimento" />
                                            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="!base_novo_procedimento.st_regiao" @click.prevent="incluirProcedimento(null, null, base_novo_procedimento.st_regiao)">
                                                <i class="fa-solid fa-check"></i>
                                            </button>
                                        </div>
                                    </template>

                                    <div class="input-group-append" v-else-if="novoProcedimento && novoProcedimento.js_complementos && novoProcedimento.js_complementos.length">
                                        <button class="btn btn-sm btn-secondary dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            Região...
                                        </button>
                                        <div class="dropdown-menu dropdown-menu-scroll" aria-labelledby="dropdownMenuButton">
                                            <a class="dropdown-item" 
                                                href="#" v-for="(regiao, irx) in novoProcedimento.js_complementos" 
                                                :key="irx"
                                                @click.prevent="incluirProcedimento(null, null, regiao)">
                                                {{ regiao }}
                                            </a>
                                        </div>
                                    </div>
                                    <div class="input-group-append" v-if="dados && dados.pode_block_valor">
                                        <button type="button" class="btn btn-outline-secondary" @click="novo_block_valor = !novo_block_valor">
                                            <i class="fa-brands fa-creative-commons-nc fa-fw" v-if="novo_block_valor" title="Sem cobrança de valor."></i>
                                            <i class="fa-solid fa-dollar-sign fa-fw" v-else title="Com cobrança normal."></i>
                                        </button>
                                    </div>
                                    <div class="input-group-append" v-if="is_lab">
                                        <button type="button" class="btn btn-outline-secondary" @click="nao_coletar_default = !nao_coletar_default">
                                            <i class="fa-solid fa-ban" v-if="nao_coletar_default" title="Não Coletar"></i>
                                            <i class="fa-regular fa-circle" v-else title="Coletar"></i>
                                        </button>
                                    </div>
                                </template>

                            </az-input>

                        </div>

                        <!-- Inclusão de componente / && !dados.fl_particular -->

                        <div class="col col-12 col-md-2" v-if="!consultorio">
                            <az-input
                                sizing="sm"
                                type="select"
                                service="planos.index"
                                optionsMode="aql"
                                optionsName="planos.particular"
                                :optionsParm="{ where: { id_operadora: 1 } }"
                                placeholder="Cobrança Operadora"
                                v-model="novoPlanoCobrancaProcedimento" />
                        </div>

                    </div>

                </div>

                <div v-if="consultorio">
                    <a href="#" @click.prevent="toggle_sort_order()" class="card-link ml-2" title="Ordenação dos lançamentos">
                        <i class="fas fa-sort-alpha-down" v-if="sort_order == 'A'"></i>
                        <i class="fas fa-sort-numeric-down" v-else></i>
                    </a>
                </div>

            </div>

            <!-- EXIBIÇÃO DA AUOTIRZAÇÃO -->

            <div v-else>

                <div class="row" v-if="!dados.fl_particular && dados.paciente.paciente_unidade.st_numero_carteira">
                    <div class="col d-flex justify-content-between">
                        <div>
                            <span class="text-muted mr-2">
                                Número da Carteira:
                            </span>
                            <a class="lead" href="#" @click.prevent="copiar_clipboard(dados.paciente.paciente_unidade.st_numero_carteira)" data-toggle="tooltip" data-placement="top" title="Copiar para o área de transferência.">
                                <i class="far fa-copy mr-2"></i>
                            </a>
                            {{ dados.paciente.paciente_unidade.st_numero_carteira }}
                        </div>
                        <div v-if="dados.plano.operadora.st_site">
                            <a :href="dados.plano.operadora.st_site" target="_blank" class="card-link">
                                Acessar Site da Operadora
                            </a>
                        </div>
                    </div>
                </div>

                <table class="table table-sm mt-2 table-hover">
                    <thead>
                        <tr class="small">
                            <td>
                                <refresh-preco
                                    :lista="lista_atendimento_procedimentos"
                                    :todos="true"
                                    :link="true" 
                                    @refresh="carregar ()" />
                                Descrição
                            </td>
                            <td></td>
                            <td>Código</td>
                            <td>Tabela</td>
                            <td class="text-right">Redução</td>
                            <td class="text-right" v-if="!dados.block_valores">Valor</td>
                            <td>Senha</td>
                            <td>Autorização</td>
                            <td>Validade</td>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(proc, index) in lista_atendimento_procedimentos">
                            <tr  :key="'P' + index" v-if="true">
                                <template v-if="!proc.item_tabela">
                                    <td>
                                        <small class="mr-2" v-if="!proc.atendimento_pacote">
                                            <refresh-preco
                                                :lista="lista_atendimento_procedimentos"
                                                :index="index"
                                                :proc="proc"
                                                :link="true" @refresh="carregar ()" />
                                        </small>

                                        <small>{{ proc.procedimento.search }}</small>
                                    </td>
                                    <td class="small">
                                        {{ proc.procedimento.st_sigla }}
                                    </td>
                                </template>
                                <template v-else>
                                    <td>
                                        <small class="mr-2" v-if="!proc.atendimento_pacote">
                                            <refresh-preco
                                                :lista="lista_atendimento_procedimentos"
                                                :index="index"
                                                :proc="proc"
                                                :link="true" @refresh="carregar ()" />
                                        </small>

                                        <small>{{ proc.item_tabela.st_nome }}</small>

                                        <span class="badge badge-secondary ml-1" v-if="proc.item_tabela && proc.item_tabela.in_porte_anestesico" title="Porte Anestésico">
                                            P{{ proc.item_tabela.in_porte_anestesico }}
                                        </span>
                                    </td>
                                    <td class="small">
                                        {{ proc.procedimento.st_sigla }}
                                    </td>
                                    <td class="text-nowrap">
                                        <small title="Código do Procedimento">
                                            <a href="#" @click.prevent="copiar_clipboard(proc.item_tabela.st_codigo)" data-toggle="tooltip" data-placement="top" title="Copiar para o área de transferência.">
                                                <i class="far fa-copy mr-2"></i>
                                            </a>
                                            <span>
                                                {{ proc.item_tabela.st_codigo }}
                                            </span>
                                        </small>

                                        <div class="small" v-if="proc.item_tabela.st_codigo_autorizacao" title="Código para Autorização do Procedimento">
                                            <a href="#" @click.prevent="copiar_clipboard(proc.item_tabela.st_codigo_autorizacao)" data-toggle="tooltip" data-placement="top" title="Copiar para o área de transferência.">
                                                <i class="far fa-copy mr-2"></i>
                                            </a>
                                            <span>
                                                Aut: {{ proc.item_tabela.st_codigo_autorizacao }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <small v-if="proc.codigo_tabela">
                                            <span data-toggle="tooltip" data-placement="top" :title="proc.codigo_tabela.st_descricao">
                                                {{ proc.codigo_tabela.ch_codigo }}
                                            </span>
                                        </small>
                                    </td>
                                    <td class="text-right">
                                        <small v-if="proc.dc_percentual_reducao && proc.dc_percentual_reducao != 1">
                                            {{ Math.round(proc.dc_percentual_reducao * 100) / 100 }}
                                        </small>
                                    </td>
                                    <td class="text-right" v-if="!dados.block_valores">
                                        <small v-if="!proc.atendimento_pacote">
                                            <i class="fa-brands fa-creative-commons-nc fa-fw text-danger mr-1" v-if="proc && proc.js_solics && proc.js_solics.fl_block_valor"></i>
                                            <i class="fa-solid fa-dollar-sign fa-fw ml-1" v-else-if="proc && proc.js_solics && proc.js_solics.fl_block_valor === false"></i>

                                            <a href="#" @click.prevent="exibir_composicao_valor(proc.item_tabela, dados.plano.operadora, proc.id_reducao, proc)" class="card-link">
                                                {{ $util.formatMoeda (proc.dc_valor_procedimento) }}
                                            </a>
                                            <div class="small text-muted" v-if="proc.dc_valor_filme" title="Valor do Filme">
                                                FLM: {{ $util.formatMoeda (proc.dc_valor_filme) }}
                                            </div>
                                            <div class="small text-muted" v-if="proc.dc_valor_co" title="Valor do Custo Operacional">
                                                CO: {{ $util.formatMoeda (proc.dc_valor_co) }}
                                            </div>
                                        </small>
                                        <small v-else>Pacote</small>
                                    </td>
                                    <td style="width: 150px;">
                                        <az-input
                                            type="text"
                                            sizing="sm"
                                            v-model="proc.st_codigo_autorizacao"
                                            :required="proc.restricao.autorizacao && proc.restricao.bloquear"
                                            :validate="validate"
                                            :icon="!proc.st_codigo_autorizacao && proc.restricao.autorizacao ? 'fas fa-exclamation-triangle' : ''"
                                            :buttonColor="proc.restricao.bloquear ? 'warning' : ''"
                                            :buttonToolTip="proc.restricao.bloquear ? 'Senha Obrigatória' : 'Autorização Prévia'">
                                            <template v-slot:button-pre>
                                                <div class="input-group-prepend input-group-sm" v-if="proc.st_codigo_autorizacao">
                                                    <button class="btn btn-outline-secondary" type="button" data-toggle="tooltip" data-placement="top"
                                                        title="Copiar para os demais items." @click="copiar_senha(proc)">
                                                        <i class="fas fa-arrows-alt-v"></i>
                                                    </button>
                                                </div>
                                            </template>
                                        </az-input>
                                    </td>
                                    <template v-if="proc.js_solics && proc.js_solics.aut_glosa">
                                        <td style="width: 200;" colspan="2">
                                            <small class="text-danger">
                                                {{ proc.js_solics.aut_glosa }}
                                            </small>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td style="width: 150px;">
                                            <az-input
                                                v-if="proc.st_codigo_autorizacao"
                                                type="date"
                                                sizing="sm"
                                                :required="proc.st_codigo_autorizacao ? true : false"
                                                :edicao="proc.st_codigo_autorizacao ? true : false"
                                                v-model="proc.dt_autorizacao"
                                                :validate="validate" />
                                                
                                        </td>
                                        <td style="width: 150px;">
                                            <az-input
                                                v-if="proc.st_codigo_autorizacao"
                                                type="date"
                                                sizing="sm"
                                                :required="proc.st_codigo_autorizacao ? true : false"
                                                :edicao="proc.st_codigo_autorizacao ? true : false"
                                                v-model="proc.dt_validade_autorizacao"
                                                :validate="validate" />
                                        </td>
                                    </template>
                                </template>
                            </tr>
                            <template v-if="proc.atendimento_procedimento_consumos && proc.atendimento_procedimento_consumos.length">
                                <tr class="small" v-for="(consumo, icx) in lista_consumo(proc, 'T')" :key="'C' + index + '.' + icx">
                                    <td>
                                        {{ consumo.item_tabela ? consumo.item_tabela.st_nome : consumo.search }}
                                    </td>
                                    <td></td>
                                    <td class="text-nowrap">
                                        <a href="#" v-if="consumo.item_tabela && consumo.item_tabela.st_codigo"
                                            @click.prevent="copiar_clipboard(consumo.item_tabela.st_codigo)" data-toggle="tooltip" data-placement="top" title="Copiar para o área de transferência.">
                                            <i class="far fa-copy mr-2"></i>
                                        </a>
                                        <span>
                                            {{ consumo.item_tabela ? consumo.item_tabela.st_codigo : '' }}
                                        </span>
                                    </td>
                                    <td colspan="2">
                                        &nbsp;
                                    </td>
                                    <td class="text-right">
                                        <a href="#" @click.prevent="exibir_composicao_valor(consumo.item_tabela, dados.plano.operadora, null, proc)" class="card-link" v-if="consumo.id_taxa">
                                            {{ $util.formatMoeda (consumo.dc_valor_unitario) }}
                                        </a>
                                        <span v-else>
                                            {{ $util.formatMoeda (consumo.dc_valor_unitario) }}
                                        </span>
                                    </td>
                                </tr>
                            </template>
                        </template>
                    </tbody>
                </table>

            </div>

            <div class="alert alert-primary d-flex justify-content-between mt-2 py-2" v-if="!consultorio && dados.ocr_codigos && dados.ocr_codigos.length">
                <div class="text-nowrap text-truncate">Pedido: <strong>{{ dados.ocr_codigos.join(', ') }}</strong></div>
                <div>
                    <a href="#" @click.prevent="incluir_codigos(dados.ocr_codigos.join(', ') + ',')">
                        <span class="badge badge-primary mr-2">{{ dados.ocr_codigos.length }}</span>
                        <i class="fa-solid fa-arrow-up"></i>
                    </a>
                </div>
            </div>

            <template v-if="!consultorio || conta">

                <div class="form-row">
                    <div class="col col-12" :class="conta ? '' : 'col-md-8'">
                        <div class="input-group mt-3">
                            <label class="form form-control d-flex justify-content-between">
                                <div class="d-flex">
                                    <span class="mr-3" v-if="!dados.block_valores">
                                        Total: R$ {{ $util.formatMoeda (valorTotal) }}
                                        <span v-if="valorTotalExtraParticular">
                                            / Particular: R$ {{ $util.formatMoeda (valorTotalExtraParticular) }}
                                        </span>
                                    </span>
                                    <h5 class="mb-0" v-if="quantidadeTotal">
                                        <span class="badge badge-secondary">
                                            {{ quantidadeTotal }}
                                        </span>
                                    </h5>
                                </div>
                                <div v-if="!consultorio">
                                    <a href="#" @click.prevent="download_pedido()" class="card-link ml-2" title="Consultar Pedido Online." v-if="dominio == 'teste' || dominio =='datalab'"> 
                                        <i class="fa-solid fa-file-arrow-down"></i>
                                    </a>

                                    <a href="#" @click.prevent="abrir_historico_pacotes()" class="card-link ml-2" title="Pacotes do Paciente." v-if="dados && dados.atendimento"> 
                                        <i class="fas fa-layer-group"></i>
                                    </a>

                                    <span class="dropdown ml-2" v-if="dados && dados.arquivos" title="Anexos">
                                        <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <i class="fa-solid fa-plus" v-if="!dados.arquivos.length && !( dados.paciente && dados.paciente.arquivos )"></i>
                                            <i class="fa-solid fa-paperclip" v-else></i>
                                        </a>
                                        <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                            <a href="#" @click.prevent="abrir_anexar_arquivo()" class="dropdown-item" v-if="dados && dados.arquivos"> 
                                                <i class="fa-solid fa-plus fa-fw mr-2" v-if="!dados.arquivos.length"></i>
                                                <i class="fa-solid fa-paperclip fa-fw mr-2" v-else></i>
                                                Atendimento
                                            </a>
                                            <a href="#" @click.prevent="abrir_anexar_arquivo(true)" class="dropdown-item" v-if="dados && dados.paciente && dados.paciente.arquivos"> 
                                                <i class="fa-solid fa-plus fa-fw mr-2" v-if="!dados.paciente.arquivos"></i>
                                                <i class="fa-solid fa-paperclip fa-fw mr-2" v-else></i>
                                                Paciente
                                            </a>
                                        </div>
                                    </span>

                                    <a href="#" @click.prevent="abrir_calcular_valores()" class="card-link ml-2" title="Calcular valores." v-if="dados && dados.atendimento && dados.atendimento.plano.fl_valor_editavel && dados.atendimento.plano.valor_editavel"> 
                                        <i class="fa-solid fa-calculator fa-fw"></i>
                                    </a>

                                    <a href="#" @click.prevent="apagar_todos()" class="card-link ml-2" title="Apagar todos os itens." v-if="dados && dados.procedimento && dados.procedimento.id_especialidade == 2"> 
                                        <i class="fa-solid fa-eraser text-danger fa-fw"></i>
                                    </a>

                                    <a href="#" @click.prevent="copiar_siglas()" class="card-link ml-2" title="Copiar códigos dos exames." v-if="dados && dados.procedimento && dados.procedimento.id_especialidade == 2"> 
                                        <i class="fa-solid fa-clipboard-list fa-fw"></i>
                                    </a>

                                    <a href="#" @click.prevent="ver_amostras()" class="card-link ml-2" title="Amostras." v-if="dados && dados.atendimento && dados.atendimento.amostras_count"> 
                                        <i class="fa-solid fa-vial fa-fw"></i>
                                    </a>

                                    <a href="#" @click.prevent="get_prazos()" class="card-link ml-2" title="Ver Prazos." v-if="dados && dados.atendimento && is_lab"> 
                                        <i class="fa-regular fa-calendar-check"></i>
                                    </a>

                                    <!-- v-if="tem_item_com_senha" -->
                                    <a href="#" @click.prevent="toggle_show_datas()" class="card-link ml-2" title="Visualização das datas de autorização."> 
                                        <i class="far fa-calendar fa-fw"></i>
                                    </a>

                                    <a href="#" @click.prevent="toggle_show_consumo()" class="card-link ml-2" title="Visualização dos mat/med/taxas">
                                        <i class="fas fa-eye fa-fw" v-if="show_consumo"></i>
                                        <i class="fas fa-eye-slash fa-fw" v-else></i>
                                    </a>

                                    <a href="#" @click.prevent="toggle_sort_order()" class="card-link ml-2" title="Ordenação dos lançamentos">
                                        <i class="fas fa-sort-alpha-down fa-fw" v-if="sort_order == 'A'"></i>
                                        <i class="fas fa-sort-numeric-down fa-fw" v-else></i>
                                    </a>

                                    <a href="#" @click.prevent="dados.atendimento.st_cirurgia_tipo_guia = ''" class="card-link ml-2" title="Selecionar Tipo de Guia" v-if="dados.atendimento.st_cirurgia_tipo_guia === null">
                                        <i class="fa-regular fa-file-lines"></i>
                                    </a>
                                </div>
                            </label>
                            <div class="input-group-append" v-if="!conta">
                                <button class="btn btn-outline-secondary" type="button" data-toggle="tooltip" data-placement="top" title="Atualizar Valores"
                                    v-if="particular || valorTotalExtraParticular > 0"
                                    @click="recalc_preco_cirurgias(null, true, true)">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                                <button class="btn btn-primary" type="button" @click="selecionar_requisicao()" v-if="dados.requisicoes && dados.requisicoes.length">
                                    Verificar Requisições
                                    <span class="badge badge-light">
                                        {{ dados.requisicoes.length }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="col col-12 col-md-2" v-if="!conta">
                        <div class="input-group mt-3">
                            <div class="input-group-prepend">
                                <span class="input-group-text" id="basic-addon1">
                                    <i class="fas fa-concierge-bell"></i>
                                </span>
                            </div>
                            <input type="text" class="form-control" :placeholder="dados.recepcao ? dados.recepcao.search : ''" disabled>
                        </div>
                    </div>

                    <div class="col col-12 col-md-2" v-if="!conta && !particular && dados.atendimento">
                        <az-input
                            :label="dados.atendimento.st_cirurgia_tipo_guia == 'H' ? 'Guia de Solic de Internação' : (dados.atendimento.st_cirurgia_tipo_guia == 'R' ? 'Guia Solicitação de Internação' : 'Guia Principal')"
                            sizing="sm"
                            type="text"
                            :validate="validate"
                            v-model="dados.atendimento.st_guia_principal"  />
                    </div>
                </div>

                <!-- Laudo Comparativo -->
                <div v-if="tem_laudo_comparativo && dados.atendimento" class="form-row mt-3">
                    <div class="col col-12 col-md-4">
                        <az-input
                            sizing="sm"
                            :compact="true"
                            label="Atend. Relacionado"
                            placeholder="Informe No. do Atend..."
                            :edicao="!dados.atendimento.origem"
                            v-model="dados.atendimento.id_atendimento_origem">
                            <template v-slot:buttonposb>
                                <div class="input-group-append">
                                    <button type="button" class="btn btn-outline-primary" @click="buscar_comparativo_origem()" v-if="!dados.atendimento.origem && dados.atendimento.id_atendimento_origem" :disabled="$store.state.loading">
                                        <i class="fa-solid fa-rotate fa-w"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-danger" @click="remover_comparativo_origem()" v-if="dados.atendimento.origem">
                                        <i class="fa-solid fa-xmark fa-fw"></i>
                                    </button>
                                </div>
                            </template>
                        </az-input>
                    </div>
                    <div class="col col-12 col-md-8" v-if="dados.atendimento.origem && dados.atendimento.origem.paciente">
                        <az-input
                            sizing="sm"
                            :compact="true"
                            label="Nome"
                            :edicao="false"
                            v-model="dados.atendimento.origem.paciente.search" />
                    </div>
                </div>

                <!-- Apoiado -->

                <div v-if="dados.tem_apoiado" class="form-row mt-3">
                    <div class="col col-12 col-md-6">
                        <az-input
                            placeholder="Apoiado..."
                            icon="fa-solid fa-house-signal fa-fw"
                            sizing="sm"
                            type="autocomplete"
                            service="apoiados.index"
                            optionsMode="aql"
                            optionsName="apoiados.index"
                            :optionsParm="{ id_atendimento: dados.atendimento.id_atendimento }"
                            v-model="dados.atendimento.apoiado" />
                    </div>

                    <div class="col col-12 col-md-6">
                        <az-input
                            placeholder="Origem..."
                            icon="fa-solid fa-location-dot fa-fw"
                            sizing="sm"
                            type="autocomplete"
                            service="apoiado_origens.index"
                            optionsMode="aql"
                            optionsName="apoiados.index"
                            :optionsParm="{ id_apoiado: dados.atendimento.apoiado ? dados.atendimento.apoiado.id_apoiado : -1 }"
                            :edicao="( dados.atendimento.apoiado && dados.atendimento.apoiado.origens_count > 0 ) ? true : false"
                            v-model="dados.atendimento.apoiado_origem" />
                    </div>
                </div>

                <!-- Classificação -->

                <template v-if="dados.classes && dados.atendimento.js_info && dados.atendimento.js_info.classes">
                    <div class="form-row mt-2">
                        <div class="col col-12">
                            <multiselect
                                v-model="dados.atendimento.js_info.classes"
                                :options="dados.classes"
                                :multiple="true"
                                :taggable="false"
                                tag2="addTag"
                                placeholder="Classificação..."
                                selectLabel="Pressione [ENTER] para selecionar"
                                deselectLabel="Pressione [ENTER] para remover"
                                tagPlaceholder="Pressione [ENTER] para incluir"
                                selectedLabel="Selecionado">
                                <template slot="noOptions">
                                    Nenhuma classificação criada.
                                </template>
                                <template slot="noResult">
                                    Nenhuma classificação encontrada.
                                </template>
                            </multiselect>
                        </div>
                    </div>
                </template>

            </template>
            
            <!-- Autorização -->

            <div class="mt-3" v-if="dados.autorizacao && dados.autorizacao.status_solicitacao && dados.autorizacao.st_comentarios && !consultorio">
                <div class="alert" :class="dados.autorizacao.status_solicitacao.color ? 'alert-' + dados.autorizacao.status_solicitacao.color : 'alert-secondary'">
                    <strong>{{ dados.autorizacao.status_solicitacao.msg ? dados.autorizacao.status_solicitacao.msg : 'Autorização' }}</strong>
                    <div class="small mb-0">{{ dados.autorizacao.st_comentarios }}</div>
                </div>
            </div>

            <template v-if="!consultorio">

                <outras-marcacoes class="mt-3" :dados="dados" @reabrir="reabrir" :pode_encaminhar="false" />

                <div class="mt-3" v-if="dados.ret_prevs && dados.ret_prevs.length">
                    <div class="card">
                        <div class="card-header alert-warning d-flex justify-content-between">
                            <div>
                                <a class="card-link" data-toggle="collapse" href="#retprevs" role="button" aria-expanded="false" aria-controls="collapseExample">
                                    Retornos Previstos
                                </a>
                            </div>
                            <div>
                                <span class="mr-2" v-if="dados.ret_prevs.find(retorno => retorno.dt_retorno <= $util.hoje() )" title="Retorno já vencido.">
                                    <i class="fa-solid fa-triangle-exclamation text-danger"></i>
                                </span>

                                <span class="badge badge-warning">
                                    {{ dados.ret_prevs.length }}
                                </span>
                            </div>
                        </div>
                        <div class="collapse" id="retprevs">
                            <table class="table table-hover mb-0 small">
                                <tr v-for="(item, index) in dados.ret_prevs" :key="'R' + index">
                                    <td>{{ item.procedimento.search }}</td>
                                    <td>{{ item.medico.search }}
                                        <span v-if="item.created_at">
                                            em {{ $util.getFormatDate(item.created_at.substr(0,10)) }}
                                        </span>
                                    </td>
                                    <td>
                                        Para: 
                                        {{ $util.getFormatDate(item.dt_retorno) }}
                                        <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                        <span>{{ getHumanDuration (item.dt_retorno) }}</span>
                                    </td>
                                    <td v-if="item.st_observacoes">
                                            {{ item.st_observacoes }}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="mt-3" v-if="dados.orcamento">
                    <div class="alert alert-secondary">
                        <strong>Orçamento: 
                            <a href="#" @click.prevent="abrir_orcamento(dados.orcamento)" class="card-link">
                                #{{ dados.orcamento.id_orcamento }}
                            </a>
                        </strong>
                        <div class="small mb-0" style="white-space: pre-wrap;" v-if="dados.orcamento.st_observacoes">{{ dados.orcamento.st_observacoes }}</div>
                    </div>
                </div>

                <!-- Orçamentos -->
                <div class="mt-3" v-else-if="dados.orcs && dados.orcs.length">
                    <div class="form-row" v-for="(orc, iox) in dados.orcs" :key="iox">
                        <div class="col col-12">
                            <div class="alert alert-secondary mb-0 small">
                                <div class="text-nowrap text-truncate">
                                    <a href="#" @click.prevent="vincular_orcamento(orc)" v-if="dados.procedimento.id_especialidade == 2 && !dados.outros_proc && lista_atendimento_procedimentos.length <= 1">
                                        <i class="fa fa-plus mr-3" aria-hidden="true"></i>
                                    </a>
                                    <input type="checkbox" v-model="orc.vincular" class="mr-3" v-else>

                                    Orçamento Realizado: #{{ orc.id_orcamento }}
                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                    {{ $util.getFormatDate(orc.created_at) }}
                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                    {{ orc.plano.search }}
                                    <template v-if="orc.unidade_operacao && orc.unidade_operacao.st_sigla">
                                        <span class="badge badge-light ml-2">
                                            {{ orc.unidade_operacao.st_sigla }}
                                        </span>
                                    </template>

                                    <template v-if="orc.valor_total">
                                        <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                        {{ $util.formatMoeda(orc.valor_total) }}
                                    </template>
                                </div>
                                <ul class="mb-0 mt-2">
                                    <template v-for="(orc_item, ioix) in orc.js_procedimentos">
                                        <li :key="ioix" v-if="iox < 5">
                                            {{ orc_item.procedimento.search }}
                                            <template v-if="orc_item.dc_valor_procedimento && orc_item.dc_valor_procedimento > 0">
                                                <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                {{ $util.formatMoeda(orc_item.dc_valor_procedimento) }}
                                            </template>
                                            <template v-if="orc_item.in_quantidade && orc_item.in_quantidade > 1">
                                                <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                x{{ orc_item.in_quantidade }}
                                            </template>
                                        </li>
                                        <li :key="'U' + ioix" v-else-if="iox == 5">
                                            ...
                                        </li>
                                    </template>
                                </ul>
                                <div class="small mb-0" style="white-space: pre-wrap;" v-if="orc.st_observacoes">{{ orc.st_observacoes }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Autorizações -->
                <div class="mt-3" v-else-if="dados.auts && dados.auts.length">
                    <div class="form-row" v-for="(solaut, iox) in dados.auts" :key="iox">
                        <div class="col col-12">
                            <div class="alert mb-0 small" :class="solaut.status_solicitacao.color ? 'alert-' + solaut.status_solicitacao.color : 'alert-secondary'">
                                <div class="text-nowrap text-truncate">
                                    <a href="#" @click.prevent="vincular_aut(solaut)" v-if="!dados.outros_proc && lista_atendimento_procedimentos.length <= 1">
                                        <i class="fa fa-plus mr-3" aria-hidden="true"></i>
                                    </a>

                                    <i class="fa-solid fa-circle-dot mr-3" v-else></i>

                                    Autorização: <strong>#{{ solaut.id_solicitacao_autorizacao }}</strong>
                                    <span class="badge ml-2" :class="'badge-' + solaut.status_solicitacao.color" v-if="solaut.status_solicitacao">
                                        {{ solaut.status_solicitacao.msg }}
                                    </span>
                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                    {{ $util.getFormatDate(solaut.created_at) }}
                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                    <span v-if="solaut.plano">
                                        {{ solaut.plano.search }}
                                    </span>
                                    <template v-if="solaut.unidade_operacao && solaut.unidade_operacao.st_sigla">
                                        <span class="badge badge-light ml-2">
                                            {{ solaut.unidade_operacao.st_sigla }}
                                        </span>
                                    </template>
                                </div>
                                <ul class="mb-0 mt-2">
                                    <li v-if="solaut.procedimento">
                                        {{ solaut.procedimento.search }}
                                    </li>
                                    <template v-if="solaut.js_secundarios">
                                        <template v-for="(solaut_item, ioix) in solaut.js_secundarios">
                                            <li :key="ioix" v-if="iox < 5">
                                                {{ solaut_item.search }}
                                            </li>
                                            <li :key="'U' + ioix" v-else-if="iox == 5">
                                                ...
                                            </li>
                                        </template>
                                    </template>
                                </ul>
                                <div class="small mb-0" style="white-space: pre-wrap;" v-if="solaut.st_observacoes">{{ solaut.st_observacoes }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Outros Pendentes -->
                <div class="mt-3" v-if="dados.atendimento && dados.atendimento.outros_pendentes && dados.atendimento.outros_pendentes.length">
                    <div class="card">
                        <div class="card-header alert-danger d-flex justify-content-between">
                            <div>
                                <a class="card-link" data-toggle="collapse" href="#outrospendentes" role="button" aria-expanded="false" aria-controls="outrospendentes">
                                    Outros Atendimentos com Material Pendente
                                </a>
                            </div>
                            <div>
                                <span class="badge badge-danger">
                                    {{ dados.atendimento.outros_pendentes.length }}
                                </span>
                            </div>
                        </div>
                        <div class="collapse" id="outrospendentes">
                            <ul class="list-group">
                                <li class="list-group-item d-flex justify-content-between" v-for="(outro, iox) in dados.atendimento.outros_pendentes" :key="iox">
                                    <div>
                                        <a href="#" @click.prevent="abrir_atend(outro.id_atendimento)" class="card-link">
                                            #{{ outro.id_atendimento }}, {{ $util.getFormatDate(outro.dt_inicio) }}
                                        </a>
                                        <span class="badge badge-light ml-3" v-if="outro.unidade_operacao && outro.unidade_operacao.st_sigla">
                                            {{ outro.unidade_operacao.st_sigla }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="badge badge-secondary">
                                            {{ outro.atendimento_procedimentos_count }}
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

            </template>

            <!-- Atendimentos Programados -->
            <div class="form-row mt-3" v-if="!consultorio && dados.outras_futuras && dados.outras_futuras.length">
                <div class="col col-12 col-md-3" v-for="(futura, iox) in dados.outras_futuras" :key="iox">
                    <div class="alert alert-success mb-0 small" data-toggle="tooltip" data-placement="top" title="Próximos agendamentos do paciente." >
                        <div class="text-nowrap text-truncate">
                            Marcação: 
                            {{ $util.getFormatDate(futura.dt_inicio) }}
                            {{ futura.dt_inicio.substr(11,5) }}
                            <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                            {{ $util.getHumanDuration(futura.dt_inicio) }}
                        </div>
                        <div class="text-nowrap text-truncate">{{ futura.procedimento.search }}</div>
                        <div class="text-nowrap text-truncate">{{ futura.medico.search }}</div>
                    </div>
                </div>
            </div>

            <!-- Retorno de Atendimento -->
            <template v-if="dados.retornos && dados.retornos.length && !consultorio">
                <div v-if="true">
                    <div class="alert alert-primary mt-3" v-for="(ret, irx) in dados.retornos" :key="irx">
                        <i class="fas fa-undo-alt mr-2"></i>
                        Retorno Programado para: <strong>{{ $util.getFormatDate(ret.dt_retorno) }}</strong>
                        <span class="small text-muted ml-2">{{ $util.getHumanDuration(ret.dt_retorno) }}</span>
                        <i class="fa fa-caret-right mx-3" aria-hidden="true"></i>
                        <small>{{ ret.procedimento.search }}</small>
                        <template v-if="ret.medico">
                            <i class="fa fa-caret-right mx-3" aria-hidden="true"></i>
                            <small>{{ ret.medico.search }}</small>
                        </template>
                        <div class="small" v-if="ret.st_observacoes">{{ ret.st_observacoes }}</div>
                    </div>
                </div>

                <lista-retornos class="mt-3" :retornos="dados.retornos" />
            </template>

            <div class="mt-3" v-if="dados.instrucoes && dados.instrucoes.length && !consultorio">
                <div class="alert" :class="inst.color ? 'alert-' + inst.color : 'alert-warning'" v-for="(inst, iix) in dados.instrucoes" :key="iix">
                    <strong>{{ inst.title }}</strong>
                    <small v-html="inst.text" class="mb-0"></small>
                </div>
            </div>

            <p class="alert alert-danger lead mt-3" v-if="dados.atendimento && dados.atendimento.fl_reembolso">
                Atendimento com Reembolso
            </p>

            <div class="alert alert-info mt-3" v-if="dados.autorizacao && dados.autorizacao.st_observacao_envio">
                <strong>Autorização</strong>
                <div class="small">{{ dados.autorizacao.st_observacao_envio }}</div>
            </div>

            <template v-if="!conta">
                <br><br><br><br><br>
            </template>

        </template>

        <template v-else>
            <az-loading class="my-3" />
        </template>

        <autoriz-online ref="autoriz" @refresh="carregar()" />
        <registrar-autorizacao ref="regautoriz" v-if="dados && dados.autorizacao" v-model="dados.autorizacao" />
        <cadastro-item-tabela ref="cadastroitemtabela" />
        <selecionar-requisicao ref="selec_req" />
        <cancelar-procedimento ref="canc_atd_proc" />
        <autorizar-valor-editado ref="aut_valor_editado" />
        <empacotados ref="empac" />
        <selecao-tempo-curva ref="curva" />

        <!-- Modal -->
        <div class="modal fade" id="qtdeSessaoModal" tabindex="-1" role="dialog" aria-labelledby="modelTitleId">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Sessões</h5>
                        <button type="button" class="close" aria-label="Close" @click="fechar_modal_qtde_sessoes ()">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <az-input
                            label="Total de Sessões"
                            type="number"
                            required
                            v-model="qtde_sessoes" />

                        <az-input
                            label="Sessões já realizadas em outra lugar"
                            type="number"
                            placeholder="Nenhuma..."
                            v-model="qtde_previas" />

                        <az-input
                            label="Observações"
                            v-model="sessao_obs" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="fechar_modal_qtde_sessoes ()">Cancelar</button>
                        <button type="button" class="btn btn-success" @click="selecionar_pacote (sel_pacote, qtde_sessoes, false, null, qtde_previas, sessao_obs)">Selecionar</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Calc -->
        <div class="modal fade" id="calcValoresModal" tabindex="-1" role="dialog" aria-labelledby="modelTitleId" aria-hidden="true">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Calculcar</h5>
                        <button type="button" class="close" @click="fechar_modal ('calcValoresModal')" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <az-input   
                            label="Ação"
                            type="select"
                            placeholder="Multiplicar Fator"
                            :optionsList="[
                                { id: 'P', search: 'Aplicar Percentual' },
                                { id: 'A', search: 'Acrescentar Percentual' },
                                { id: 'R', search: 'Reduzir Percentual' },
                            ]"
                            optionsData="id"
                            v-model="calc.tipo" />

                        <az-input   
                            :label="calc.tipo ? 'Percentual' : 'Fator'"
                            type="money"
                            :precision="6"
                            v-model="calc.fator">
                            <template v-slot:button-pre>
                                <div class="input-group-prepend">
                                    <span class="input-group-text" id="basic-addon1" v-if="!calc.tipo">
                                        <i class="fa fa-times" aria-hidden="true"></i>
                                    </span>

                                    <span class="input-group-text" id="basic-addon1" v-if="calc.tipo == 'A'">
                                        <i class="fa fa-plus" aria-hidden="true"></i>
                                    </span>

                                    <span class="input-group-text" id="basic-addon1" v-if="calc.tipo == 'R'">
                                        <i class="fa fa-minus" aria-hidden="true"></i>
                                    </span>
                                </div>
                            </template>
                            <template v-slot:buttonposb>
                                <div class="input-group-append" v-if="calc.tipo">
                                    <span class="input-group-text" id="basic-addon1" >
                                        <i class="fa fa-percent" aria-hidden="true"></i>
                                    </span>
                                </div>
                            </template>
                        </az-input>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="fechar_modal ('calcValoresModal')">Cancelar</button>
                        <button type="button" class="btn btn-success" @click="calc_valores()">Aplicar</button>
                    </div>
                </div>
            </div>
        </div>

        <ciente-instrucoes ref="ciente" @ciente="ciente()" />
        <procedimento-outras-datas ref="outras_datas" :id_medico="dados.id_medico" v-if="dados" @lancar="gerar_outras_datas"/>
        <editar-valor-total ref="edit_valor_total" />
        <coparticipacao ref="copart" @confirmar="salvar_com_copartic" />
        <historico-pacotes-paciente-modal 
            ref="historico_pacotes" 
            :paciente="dados ? dados.paciente : null"
            :id_atendimento="dados ? dados.id_atendimento : null" />

        <template v-if="acao_anexar && dados">
            <az-file-upload
                ref="upload"
                @refresh="carregar_arquivos(false, anexar_pacientes)"
                :multiple="true"
                :arquivos="anexar_pacientes ? dados.paciente.arquivos : dados.arquivos"
                :parms="{ 
                    model_type: anexar_pacientes ? 'pessoa' : 'marcacao', 
                    model_id: anexar_pacientes ? dados.id_paciente : dados.id_marcacao, 
                    id_paciente: dados.id_paciente,
                }"
                @ocr="atualizar_ocr" />
        </template>

        <editar-qtde-pacote ref="editar_qtde_pacote" @refresh="carregar ()" />
        <buscar-prescricao ref="prescricoes" @refresh="carregar ()" />
        <ver-prazos-exames ref="prazos_exames" :prazos="prazos" />
        <download-pedido ref="download_pedido" 
            :id_operadora="( dados && dados.plano && dados.plano.operadora ) ? dados.plano.operadora.id_operadora : null"
            :id_plano="( dados && dados.plano ) ? dados.plano.id_plano : null"
            :id_unidade_operacao="dados ? dados.id_unidade_operacao : null"
            :id_marcacao="idMarcacao" />
        <help-exame-klis ref="help" />
        <alterar-centro-custo ref="alterar_cc" @refresh="carregar ()" />

    </div>

</template>

<script>
import _ from 'lodash';
import $ from 'jquery';
import * as moment from "moment";
import AzSubTitle from '../../util/AzSubTitle.vue';
import AzInput from '../../shared/forms/AzInput.vue';
import RefreshPreco from '../../shared/RefreshPreco.vue';
import LembretePaciente from '../consultorio/LembretePaciente.vue';
import AzLoading from '../../util/AzLoading.vue';
import MedicoSolicitante from'../../shared/MedicoSolicitante.vue';
import AutorizOnline from '../../modulos/autoriz/AutorizOnline.vue';
import CadastroItemTabela from '../../cadastros/shared/CadastroItemTabela.vue';
import RegistrarAutorizacao from '../../modulos/autoriz/RegistrarAutorizacao.vue';
import SelecionarRequisicao from './SelecionarRequisicao.vue';
import CancelarProcedimento from './CancelarProcedimento.vue';
import ProcedimentoRegiao from '../callcenter/ProcedimentoRegiao.vue';
import ListaRetornos from '../../shared/ListaRetornos.vue';
import AutorizarValorEditado from './AutorizarValorEditado.vue';
import Empacotados from '../faturamento/Empacotados.vue';
import SelecaoTempoCurva from './SelecaoTempoCurva.vue';
import CienteInstrucoes from './CienteInstrucoes.vue';
import ProcedimentoOutrasDatas from './ProcedimentoOutrasDatas.vue';
import AzFileUpload from '../../util/AzFileUpload.vue';
import EditarValorTotal from './EditarValorTotal.vue';
import Coparticipacao from './Coparticipacao.vue';
import Multiselect from 'vue-multiselect';
import EditarQtdePacote from './EditarQtdePacote.vue';
import BuscarPrescricao from './BuscarPrescricao.vue';
import HistoricoPacotesPacienteModal from '../../shared/HistoricoPacotesPacienteModal.vue';
import VerPrazosExames from '../callcenter/VerPrazosExames.vue';
import DownloadPedido from './DownloadPedido.vue';
import HelpExameKlis from './HelpExameKlis.vue';
import OutrasMarcacoes from "./OutrasMarcacoes.vue";
import AlterarCentroCusto from './AlterarCentroCusto.vue';

export default {
    data () {
        return {
            dados: null,
            edicao: true,
            novoProcedimento: null,
            novoPlanoCobrancaProcedimento: "",
            pacotes: null,
            indexProcPacote: null,
            nome_medico_solicitante: null,
            podeAtender: true,
            cidInfoShow: false,
            senhaInfoShow: false,
            viewMode: "L",
            novoComponente: null,
            carregando: false,
            iniciando: false,
            qtde_sessoes: 0,
            qtde_previas: 0, 
            sessao_obs: "", 
            sel_pacote: null,
            graus: [],
            novoPlanoCobranca: "",
            validate: false,
            show_consumo: this.$util.getCookie('proc_show_consumo') == 'S',
            excluindo: false,
            sort_order: 'L',
            lista_proximo: [],
            incluindo: false, 
            calc: { 
                tipo: "",
                fator: 0, 
            },
            editando_valores: false, 
            check_selecao: [], 
            nao_coletar_default: false, 
            novo_block_valor: false, 
            base_novo_procedimento: "", 
            recalc_com_comps: false, 
            habilita_recalc_preco: false, 
            acao_anexar: false, 
            anexar_pacientes: false, 
            rest_exibidas: {}, 
            troca_solic: false, 
            valor_copart: null, 
            config_pag_medico: false, 
            config_solicitantes_multiplos: false, 
            configs_loaded: false,
            config_medico_lancar_material_consultorio: false, 
            config_pacote_bloq_qt: false, 
            prazos: null, 
            lancar_solicitante: false, 
            centro_base: { 
                centro: null, 
            }
        }
    },

    props: [ 'idMarcacao', 'posUpdate', 'validacao', 'consultorio', 'assistencia', 'conta', 'altera_prof'],

    watch: {
        validacao: {
            handler() {
                this.validate = this.validacao;
            },
            deep: true,
            immediate: true,
        },

        dados: {
            handler() {

                if ( this.dados && this.dados.atendimento && !this.dados.atendimento.medico_solicitante ) {
                    this.dados.atendimento.medico_solicitante = { st_nome: "" };
                }

                if ( this.is_odonto && this.dados && this.dados.atendimento && this.dados.atendimento.atendimento_procedimentos ) { 
                    this.dados.atendimento.atendimento_procedimentos.forEach(item => { 
                        if ( !item.js_solics ) { 
                            item.js_solics = { s: null };
                        }
                    });
                }

            },
            deep: true,
            immediate: true,
        },
    },

    components: {
        AzSubTitle,
        AzInput,
        RefreshPreco,
        LembretePaciente,
        AzLoading,
        MedicoSolicitante,
        AutorizOnline,
        CadastroItemTabela,
        RegistrarAutorizacao,
        SelecionarRequisicao,
        CancelarProcedimento, 
        ProcedimentoRegiao, 
        ListaRetornos, 
        AutorizarValorEditado, 
        Empacotados, 
        SelecaoTempoCurva, 
        CienteInstrucoes,
        ProcedimentoOutrasDatas, 
        AzFileUpload, 
        EditarValorTotal, 
        Coparticipacao, 
        Multiselect,
        EditarQtdePacote, 
        BuscarPrescricao, 
        HistoricoPacotesPacienteModal, 
        VerPrazosExames, 
        DownloadPedido, 
        HelpExameKlis, 
        OutrasMarcacoes, 
        AlterarCentroCusto, 
    },

    updated () {
      // $('[data-toggle="tooltip"]').tooltip();
    },

    computed: {
        cirurg_campos_obrig: function () { 
            if ( this.particular )
                return false; 

            if ( this.dados && this.dados.cirurgia_block_obrig ) { 
                return false; 
            }

            return true; 
        },

        dominio: function () { 
            return window.redir;
        },

        tem_laudo_comparativo: function () { 
            return this.lista_atendimento_procedimentos.find(item => item.procedimento && item.procedimento.cfm);
        },

        regioes: function () { 
            return [
                { id: 'AS', search: 'Arco Superior' },
                { id: "AI", search: "Arco Inferior" }, 
                { id: "HASD", search: "Hemi-Arco Superior Direito" },
                { id: "HASE", search: "Hemi-Arco Superior Esquerdo" },
                { id: "HAID", search: "Hemi-Arco Inferior Direito" },
                { id: "HAIE", search: "Hemi-Arco Inferior Esquerdo" },
                { id: "ASAI", search: "Arcadas Superiores e inferiores" },
                { id: "S1", search: "Sextante superior posterior direito" },
                { id: "S2", search: "Sextante superior anterior" },
                { id: "S3", search: "Sextante superior posterior esquerdo" },
                { id: "S4", search: "Sextante inferior posterior esquerdo" },
                { id: "S5", search: "Sextante inferior anterior" },
                { id: "S6", search: "Sextante inferior posterior direito" },
                { id: "LG", search: "Língua" },
                { id: "CL", search: "Comissura labial" },
                { id: "AB", search: "Assoalho de boca" },
                { id: "PA", search: "Palato" },
                { id: "MJ", search: "Mucosa jugal" },
                { id: "PD", search: "Palato duro" },
                { id: "PM", search: "Palato mole" },
                { id: "RM", search: "Região retromolar" },
                { id: "MA", search: "Mucosa alveolar" },
                { id: "GI", search: "Gengiva inserida" },
                { id: "PT", search: "Parítida" },
                { id: "TP", search: "Tonsilas palatinas" },
                { id: "RIS", search: "Região dos Incisivos centrais superiores" }, 
                { id: "RCSD", search: "Região do canino e lateral superior direito" },
                { id: "RPSD", search: "Região dos pré-molares superiores direito" },
                { id: "RMSD", search: "Região dos molares superiores direito" },
                { id: "RCSE", search: "Região do canino e lateral superior esquerdo" },
                { id: "RPSE", search: "Região dos pré-molares superiores esquerdo" },
                { id: "RMSE", search: "Região dos molares superiores esquerdo" },
                { id: "RII", search: "Região dos incisivos inferiores" }, 
                { id: "RCID", search: "Região de canino inferior direito" }, 
                { id: "RPID", search: "Região dos pré-molares inferiores direito" }, 
                { id: "RMID", search: "Região dos molares inferiores direito" }, 
                { id: "RCIE", search: "Região de canino inferior esquerdo" }, 
                { id: "RPIE", search: "Região dos pré-molares inferiores esquerdo" }, 
                { id: "RMIE", search: "Região dos molares inferiores esquerdo" }, 
                { id: "RMD", search: "Região dos molares lado direito" },
                { id: "RME", search: "Região dos molares lado esquerdo" },
                { id: "RPD", search: "Região dos pré-molares lado direito" },
                { id: "RPE", search: "Região dos pré-molares lado esquerdo" },
                { id: "RMPE", search: "Região dos molares e pré-molares lado esquerdo" },
                { id: "RMPD", search: "Região dos molares e pré-molares lado direito" },
                { id: "SM", search: "Região do assoalho do seio maxilar" },
                { id: "TU", search: "Região do Túber" },
                { id: "SI", search: "Região de Sínfise" },
                { id: "FLI", search: "Freio lingual" },
                { id: "FLA", search: "Freios labiais" },
                { id: "UV", search: "Úvula" },
                { id: "PP", search: "Pregas palatinas" },
                { id: "PI", search: "Papila incisiva" },
                { id: "LS", search: "Lábio Superior" },
                { id: "LI", search: "Lábio inferior" },
                { id: "RL", search: "Região lingual" },
                { id: "RP", search: "Região palatina" },
                { id: "RV", search: "Região vestibular" },
                { id: "RSMD", search: "Região Sub-Mandibular Direita" },
                { id: "RSME", search: "Região Sub-Mandibular Esquerda" },
                { id: "RSL", search: "Região Sub-Lingual" },
            ];
        },

        dentes: function () { 
            return [
                { id: "11", search: 'Incisivo Central Superior Direito' },
                { id: "12", search: "Incisivo Lateral Superior Direito" },
                { id: "13", search: "Canino Superior Direito" },
                { id: "14", search: "Primeiro Pré-molar Superior Direito" },
                { id: "15", search: "Segundo Pré-molar Superior Direito" },
                { id: "16", search: "Primeiro Molar Superior Direito" },
                { id: "17", search: "Segundo Molar Superior Direito" },
                { id: "18", search: "Terceiro Molar Superior Direito" },
                { id: "19", search: "Dente Permanente Supranumerário em Hemi-arco Superior Direito" },
                { id: "21", search: "Incisivo Central Superior Esquerdo" },
                { id: "22", search: "Incisivo Lateral Superior Esquerdo" },
                { id: "23", search: "Canino Superior Esquerdo" },
                { id: "24", search: "Primeiro Pré-molar Superior Esquerdo" },
                { id: "25", search: "Segundo Pré-molar Superior Esquerdo" },
                { id: "26", search: "Primeiro Molar Superior Esquerdo" },
                { id: "27", search: "Segundo Molar Superior Esquerdo" },
                { id: "28", search: "Terceiro Molar Superior Esquerdo" },
                { id: "29", search: "Dente Permanente Supranumerário em Hemi-arco Superior Esquerdo" },
                { id: "31", search: "Incisivo Central Inferior Esquerdo" },
                { id: "32", search: "Incisivo Lateral Inferior Esquerdo" },
                { id: "33", search: "Canino Inferior Esquerdo" },
                { id: "34", search: "Primeiro Pré-molar Inferior Esquerdo" },
                { id: "35", search: "Segundo Pré-molar Inferior Esquerdo" },
                { id: "36", search: "Primeiro Molar Inferior Esquerdo" },
                { id: "37", search: "Segundo Molar Inferior Esquerdo" },
                { id: "38", search: "Terceiro Molar Inferior Esquerdo" },
                { id: "39", search: "Dente Permanente Supranumerário em Hemi-arco Inferior Esquerdo" },
                { id: "41", search: "Incisivo Central Inferior Direito" },
                { id: "42", search: "Incisivo Lateral Inferior Direito" },
                { id: "43", search: "Canino Inferior Direito" },
                { id: "44", search: "Primeiro Pré-molar Inferior Direito" },
                { id: "45", search: "Segundo Pré-molar Inferior Direito" },
                { id: "46", search: "Primeiro Molar Inferior Direito" },
                { id: "47", search: "Segundo Molar Inferior Direito" },
                { id: "48", search: "Terceiro Molar Inferior Direito" },
                { id: "49", search: "Dente Permanente Supranumerário em Hemi-arco Inferior Direito" },
                { id: "51", search: "Incisivo Central Decíduo Superior Direito" },
                { id: "52", search: "Incisivo Lateral Decíduo Superior Direito" },
                { id: "53", search: "Canino Decíduo Superior Direito" },
                { id: "54", search: "Primeiro Molar Decíduo Superior Direito" },
                { id: "55", search: "Segundo Molar Decíduo Superior Direito" },
                { id: "59", search: "Dente Decíduo Supranumerário em Hemi-arco Superior Direito" },
                { id: "61", search: "Incisivo Central Decíduo Superior Esquerdo" },
                { id: "62", search: "Incisivo Lateral Decíduo Superior Esquerdo" },
                { id: "63", search: "Canino Decíduo Superior Esquerdo" },
                { id: "64", search: "Primeiro Molar Decíduo Superior Esquerdo" },
                { id: "65", search: "Segundo Molar Decíduo Superior Esquerdo" },
                { id: "69", search: "Dente Decíduo Supranumerário em Hemi-arco Superior Esquerdo" },
                { id: "71", search: "Incisivo Central Decíduo Inferior Esquerdo" },
                { id: "72", search: "Incisivo Lateral Decíduo Inferior Esquerdo" },
                { id: "73", search: "Canino Decíduo Inferior Esquerdo" },
                { id: "74", search: "Primeiro Molar Decíduo Inferior Esquerdo" },
                { id: "75", search: "Segundo Molar Decíduo Inferior Esquerdo" },
                { id: "79", search: "Dente Decíduo Supranumerário em Hemi-arco Inferior Esquerdo" },
                { id: "81", search: "Incisivo Central Decíduo Inferior Direito" },
                { id: "82", search: "Incisivo Lateral Decíduo Inferior Direito" },
                { id: "83", search: "Canino Decíduo Inferior Direito" },
                { id: "84", search: "Primeiro Molar Decíduo Inferior Direito" },
                { id: "85", search: "Segundo molar Decíduo Inferior Direito" },
                { id: "89", search: "Dente Decíduo Supranumerário em Hemi-arco Inferior Direito" }
            ];
        },
        is_odonto: function () {
            return this.dados 
                && this.dados.procedimento 
                && ( this.dados.procedimento.id_especialidade == 59
                || this.dados.procedimento.id_especialidade == 63 );
        },
        lista_medicos_solicitantes: function () {
            if ( !this.dados || !this.dados.atendimento || !this.dados.atendimento.medico_solicitante )
                return [];

            let lista = [this.dados.atendimento.medico_solicitante];

            if ( this.dados.atendimento.solicitante_outros && this.dados.atendimento.solicitante_outros.length ) { 
                lista = lista.concat(
                    this.dados.atendimento.solicitante_outros.filter(item => !item.excluido && !item.fl_indicador && item.medico_solicitante)
                        .map(item => {
                            return item.medico_solicitante;
                        })
                    );
            }

            return lista;
        },

        tem_item_com_senha: function () {
            if ( this.incluindo )
                return false;

            return this.lista_atendimento_procedimentos.find(item => item.st_codigo_autorizacao);
        },

        show_datas: function () {
            if ( this.incluindo )
                return false;

            return this.lista_atendimento_procedimentos.find(item => item.st_codigo_autorizacao && item.senha_datas);
        },

        pode_remover: function () {
            if ( this.dados && this.dados.item_status.valor == 'em atendimento' )
                return false;
            else
                return true;
        },

        pode_alterar_valor: function() {
            if ( this.dados
                && this.dados.atendimento
                && this.dados.atendimento.pode_alterar_valor
                && this.dados.atendimento.pode_alterar_valor.status_code >= 400 ) {
                return false;
            }

            return true;
        },

        guia_consulta: function () {
            return this.dados.atendimento.atendimento_procedimentos &&
                    (
                        // this.is_externo ||
                        (
                            this.dados.atendimento.atendimento_procedimentos.length == 1 &&
                            this.dados.atendimento.atendimento_procedimentos[0].procedimento.id_classe_procedimento == 1 &&
                            (
                                !_.get(this.dados, 'plano.operadora.guia_operadora.guia_operadora_consultas') ||
                                !this.dados.plano.operadora.guia_operadora.guia_operadora_consultas
                                    .find(item => item.fl_guia_sadt && item.id_procedimento == this.dados.atendimento.atendimento_procedimentos[0].id_procedimento)
                            )
                        )
                    );
        },

        is_cirurgia: function () {
            return this.dados.atendimento.atendimento_procedimentos &&
                    this.dados.atendimento.atendimento_procedimentos.length > 0 &&
                    this.dados.atendimento.atendimento_procedimentos[0].procedimento.id_classe_procedimento == 2;
        },

        is_proc_com_auxilio: function () {

            if ( !this.dados || !this.dados.atendimento || !this.dados.atendimento.atendimento_procedimentos ) {
                return false;
            }

            if ( this.dados.procedimento.id_especialidade == 2 || this.dados.procedimento.id_especialidade == 67 ) {
                return false;
            }

            let teste = this.dados.atendimento.atendimento_procedimentos.find(item =>
                _.get(item, 'item_tabela.in_numero_auxiliares') > 0 || _.get(item, 'item_tabela.dc_valor_anest') > 0
            ) ? true : false ;

            return teste;
        },

        is_lab: function () { 

            if ( !this.dados || !this.dados.procedimento ) {
                return false;
            }

            if ( this.dados.procedimento.id_especialidade == 2 || this.dados.procedimento.id_especialidade == 67 ) {
                return true;
            }

            return false;

        },

        is_externo: function () {
            return ( this.dados.atendimento.id_tipo_atendimento == 7 ) ||
                (
                    this.dados.atendimento.atendimento_procedimentos &&
                    this.dados.atendimento.atendimento_procedimentos.length > 0 &&
                    this.dados.atendimento.atendimento_procedimentos[0].procedimento.st_codigo_cbhpm == '10102019'
                );
        },

        particular: function () {
            //this.dados.fl_particular == 1 || 

            if ( this.dados && ( this.dados.id_plano == 1 || ( this.dados.plano && this.dados.plano.id_operadora == 1 ) ) )
                return true;
            else
                return false;
        },

        lista_atendimento_procedimentos: function () {
            if ( !this.dados )
                return [];

            if ( this.sort_order == 'A' ) {
                return _.orderBy(
                    this.dados.atendimento.atendimento_procedimentos.filter (item => {
                        return !item.excluido && item.fl_ativo;
                    }), item => {
                    return item.id_atendimento_procedimento ? '1' + item.procedimento.search : '2';
                });
            } else { 
                return this.dados.atendimento.atendimento_procedimentos.filter (item => {
                    return !item.excluido && item.fl_ativo;
                });
            }

        },

        lista_lab_duplicados: function () { 
            return _.groupBy(
                this.lista_atendimento_procedimentos, 
                item => { 
                    return item.procedimento.id_procedimento + '|' + item.id_amostra_material;

                    /*
                        + ( 
                            ( item.id_amostra_material && item.js_solics && this.dados && this.dados.id_amostra_material_diversos && this.dados.id_amostra_material_diversos[item.id_amostra_material] && item.js_solics ) 
                                ? item.js_solics.amo
                                : '' 
                        );
                    */
                }
            );
        },

        quantidadeTotal: function () {
            return this.lista_atendimento_procedimentos ? this.lista_atendimento_procedimentos.length : 0;
        },

        valorTotal: function () {
            if ( this.incluindo ) { 
                return 0;
            }

            let lista = _.cloneDeep(this.lista_atendimento_procedimentos);

            lista = lista.filter(item => this.dados.fl_particular || !item.id_plano_particular)
                .map((item, index) => {
                    item.index = index;
                    return item;
                });

            return _.uniqBy(lista, item => {
                    return item.atendimento_pacote
                        ? 'P' + item.atendimento_pacote.id_pacote
                        : item.index;
                }).reduce((total, item) => {
                    if ( item.id_atendimento_procedimento_empacotado )
                        return total;

                    let consumos = item.atendimento_procedimento_consumos
                        .filter(item => this.dados.fl_particular || !item.id_plano)
                        .reduce((total, consumo) => {
                            return total + ( consumo.id_atendimento_procedimento_empacotado ? 0 : ( consumo.in_quantidade * consumo.dc_valor_unitario ) );
                        }, 0);

                    total = item.atendimento_procedimento_equipes.reduce ((total, membro) => {
                        return membro.dc_valor ? (total + (parseFloat(membro.dc_valor) * parseFloat(item.in_quantidade))) : total;
                    }, total);

                    if ( item.atendimento_pacote && ( !item.dc_valor_procedimento || ( !item.id_atendimento_procedimento && item.numero_sessao == 1 ) ) )
                        return total + ( ( item.atendimento_pacote.id_atendimento == item.id_atendimento || !item.atendimento_pacote.id_atendimento ) ? parseFloat(item.atendimento_pacote.dc_valor_pacote) : 0 ) + consumos;
                    else
                        return total + ( ( item.ch_tipo_consulta == 'R' && item.procedimento.id_classe_procedimento == 1 ) ? 0 : item.dc_valor_procedimento * item.in_quantidade ) + consumos;
                }, 0);
        },

        valorTotalExtraParticular: function () {
            if ( this.particular )
                return 0;

            if ( this.incluindo ) { 
                return 0;
            }
            
            return this.lista_atendimento_procedimentos.reduce((total, item) => {
                let valor_proc = 0;

                if ( item.id_plano_particular ) {
                    if ( item.atendimento_pacote && ( !item.dc_valor_procedimento || ( !item.id_atendimento_procedimento && item.numero_sessao == 1 ) ) )
                        valor_proc = ( ( item.atendimento_pacote.id_atendimento == item.id_atendimento || !item.atendimento_pacote.id_atendimento ) ? parseFloat(item.atendimento_pacote.dc_valor_pacote) : 0 );
                    else
                        valor_proc = ( ( item.ch_tipo_consulta == 'R' && item.procedimento.id_classe_procedimento == 1 ) ? 0 : item.dc_valor_procedimento * item.in_quantidade );
                }

                return total + valor_proc +
                    item.atendimento_procedimento_consumos
                        .filter(item => item.id_plano && !item.excluido)
                        .reduce((totalc, consumo) => {
                            return totalc + ( consumo.in_quantidade * consumo.dc_valor_unitario );
                        }, 0);
            }, 0);
        },

        reembolso_consultorio: function() {
            return ( this.consultorio && this.dados && this.dados.atendimento && this.dados.atendimento.fl_reembolso ) ? true : false;
        },

    },

    methods: {
        toggle_cid_proc (proc) { 
            proc.info_cid = !proc.info_cid;

            if ( !proc.id_cid && !proc.cid ) { 
                proc.id_cid = ""; 
                proc.cid = ""; 
            }

            this.$forceUpdate();
        },

        change_filtro_consumo (proc, tipo) { 
            this.editando_valores = true; 

            this.$nextTick(() => { 
                proc.incluir_consumo = tipo; 
                this.editando_valores = false; 
            });
        },

        placeholder_consumo(proc) { 
            switch (proc.incluir_consumo) {
                case 'MAT':
                    return 'Incluir material...';
                case 'MED':
                    return 'Incluir medicamento...';
                case 'TX':
                    return 'Incluir taxa...';
                default:
                    return 'Incluir material, medicamento ou taxa...'; 
            }
        },

        alterar_centro_custo (item, apenas_selecao = false) { 
            this.$refs.alterar_cc.abrir(item, apenas_selecao);
        },

        toggle_block_fatura (proc) { 
            if ( _.get(proc, 'js_solics.fl_block_valor') ) { 
                proc.js_solics.fl_block_valor = false;
                this.recalc_preco_cirurgia (proc);
            } else { 
                if ( !proc.js_solics ) { 
                    proc.js_solics = {};
                }
                proc.js_solics.fl_block_valor = true;

                this.editando_valores = true;

                this.$nextTick(() => { 
                    proc.dc_valor_procedimento = 0; 
                    this.editando_valores = false;
                });
            }

            this.$forceUpdate();
        },

        alterar_profissional_geral () { 
            this.altera_prof.alterar(this.dados, () => {
                this.$toast.add({ 
                    severity: 'warn', 
                    summary: 'Atenção', 
                    detail: 'Alteração não disponível.', 
                    life: 5000
                });
                // this.$util.remarcar(this.marcacao, true);
            }, { alt_proc: true, id_procedimento_1: _.get(_.first(this.lista_atendimento_procedimentos), 'procedimento.id_procedimento'), teste: true }, this.carregar);
        },

        abrir_help (id_procedimento) { 
            this.$refs.help.abrir_procedimento(id_procedimento);
        },

        alterar_profissional (atp) { 
            atp.id_medico = null; 
            atp.medico = { 
                tem_certificado: true, 
            };
        },

        //

        download_pedido () { 
            this.$refs.download_pedido.abrir();
        },

        //

        get_prazos () { 
            this.prazos = null;

            this.$refs.prazos_exames.abrir(); 

            let items = this.lista_atendimento_procedimentos.filter(item => !item.dt_entrega 
                && ( !item.laudo || !item.nao_coletar )
                && ( !item.id_atendimento_procedimento || item.laudo ));

            if ( !items.length ) { 

                this.prazos = { 
                    status: 'success',
                    lista: this.lista_atendimento_procedimentos
                            .filter(item => item.dt_entrega && item.procedimento && item.procedimento.id_procedimento && ( !item.laudo || !item.nao_coletar ))
                            .map(item => {
                                return {
                                    codigo: item.procedimento.st_id_externo ? item.procedimento.st_id_externo : item.procedimento.st_sigla,
                                    nome: item.procedimento.st_procedimento,
                                    prazo: item.dt_entrega,
                                    sigla: item.procedimento.st_sigla,
                                    terc: 'N', 
                                    terc_sigla: null, 
                                };
                            })
                };
                        
                this.$refs.prazos_exames.abrir();

                return;

            }

            this.$util.load(this, {
                service: 'procedimentos',
                func: 'prazos',
                parms: {
                    id_plano: this.dados.plano.id_plano,
                    id_unidade_operacao: this.dados.unidade_operacao ? this.dados.unidade_operacao.id_unidade_operacao : null,
                    id_procedimentos: items.map(item => item.procedimento ? item.procedimento.id_procedimento : item.id_procedimento), 
                },
                loadTo: res => { 
                    res.lista = res.lista.concat(
                        this.lista_atendimento_procedimentos
                            .filter(item => item.dt_entrega && item.procedimento && item.procedimento.id_procedimento)
                            .map(item => {
                                return {
                                    codigo: item.procedimento.st_id_externo ? item.procedimento.st_id_externo : item.procedimento.st_sigla,
                                    nome: item.procedimento.st_procedimento,
                                    prazo: item.dt_entrega,
                                    sigla: item.procedimento.st_sigla,
                                    terc: 'N', 
                                    terc_sigla: null, 
                                };
                            })
                        );

                    this.prazos = res;
                }
            });
        },

        toggle_info_obs_amostra(proc) { 
            if ( _.get(proc, 'js_solics.oa') ) { 
                proc.js_solics.oa = false;
                proc.js_solics.amo = ""; 
            } else { 
                proc.js_solics.oa = true;
            }

            this.$forceUpdate();
        },

        buscar_componentes (proc) { 
            this.recalc_preco_cirurgia (proc, null, -1, null, false, true);
        },

        abrir_historico_pacotes () { 
            this.$refs.historico_pacotes.abrir(this.dados.atendimento.id_paciente);
        },

        remover_comparativo_origem () { 
            this.dados.atendimento.id_atendimento_origem = null;
            this.dados.atendimento.origem = null;
            this.$forceUpdate();
        },

        buscar_comparativo_origem () { 
            if ( !this.dados.atendimento.id_atendimento_origem ) { 
                this.dados.atendimento.origem = null;
                return;
            }

            this.$util.load(this, {
                service: 'atendimentos',
                func: 'show',
                parms: {
                    id: this.dados.atendimento.id_atendimento_origem,
                    origem: true, 
                },
                loadTo: res => { 
                    this.dados.atendimento.origem = res;
                }
            });
        },

        ordernar (lista, campo) { 
            return _.orderBy(lista, campo);
        },

        buscar_prescricao(atp) { 
            this.$refs.prescricoes.abrir(this.dados.atendimento, receita => { 
                if ( receita && receita.cobranca_items ) { 
                    receita.cobranca_items.forEach(item => { 
                        item.item.dc_quantidade = item.dc_dose_calc ? item.dc_dose_calc : item.in_quantidade;
                        this.incluirComponente (atp, item.item);
                    });
                }
            });
        },

        editar_qtde_pacote (atp) { 
            this.$refs.editar_qtde_pacote.abrir(atp);
        },

        toggle_pag_med (proc) { 
            proc.fl_pag_med = !proc.fl_pag_med;
        },

        reabrir (atendimento) { 
            this.abrir_atend(atendimento.id_atendimento);
        }, 

        abrir_atend (atend) { 
            $('.modal').modal('hide');
            console.log(atend);
            this.$util.abrir_atendimento(this, atend);
        },

        salvar_com_copartic (valor) { 
            this.valor_copart = valor;
            this.$parent.proxima_etapa();
        },

        atualizar_ocr (ocr) { 
            this.dados.ocr_codigos = ocr.ocr_codigos;
        },

        abrir_anexar_arquivo(paciente = false) {
            this.acao_anexar = true;
            this.anexar_pacientes = paciente;
            this.carregar_arquivos(true, paciente);
        },

        carregar_arquivos (abrir = false, paciente = false) {

            this.$store.commit('loading', true);

            this.$util.loadItem(this, {
                service: 'arquivos',
                func: 'links',
                parms: {
                    id: paciente ? this.dados.id_paciente : this.dados.id_marcacao,
                    type: paciente ? 'pessoa' : 'marcacao',
                }
            })
            .then(res => {
                if ( paciente ) { 
                    this.dados.paciente.arquivos = res;
                } else { 
                    this.dados.arquivos = res;
                }

                if ( abrir ) {
                    this.$nextTick(() => {
                        this.$refs.upload.abrir();
                    });
                }
            })
            .finally(() => {
                this.$store.commit('loading', false);
            });

        },

        incluir_codigos(codigos) {
            // this.copiar_clipboard(codigos);
            this.$refs.info_procedimento.$refs.input.focus ();
            this.$refs.info_procedimento.search = codigos;
            this.$refs.info_procedimento.onChange();
        },

        getHumanDuration (date) {
            let end = moment().startOf('day');
            return moment.duration(moment(date, 'YYYY-MM-DD').diff(end)).humanize(true);
        },

        gerar_outras_datas (datas) { 

            let hora_base = this.$util.agora().slice(0,5);

            datas.forEach((data, idx) => { 
                if ( !idx ) { 
                    let base = _.first(this.lista_atendimento_procedimentos);

                    if ( base.proc_inicio ) { 
                        hora_base = base.proc_inicio;
                    } else { 
                        base.proc_inicio = hora_base;
                        base.proc_termino = hora_base;
                    }

                    base.dt_execucao = data;
                } else { 
                    let base = _.cloneDeep(_.first(this.lista_atendimento_procedimentos));
                    base.id_atendimento_procedimento = null;
                    base.dt_execucao = data;

                    if ( hora_base ) { 
                        base.proc_inicio = hora_base;
                        base.proc_termino = hora_base;
                    }

                    this.dados.atendimento.atendimento_procedimentos.push(base);
                }
            });
        
        },

        lancar_outras_datas () { 
            let base = _.first(this.lista_atendimento_procedimentos);

            this.$refs.outras_datas.abrir(
                ( base && base.dt_execucao ) ? base.dt_execucao : this.$util.hoje(), 
                ( base && base.proc_inicio ) ? base.proc_inicio : this.$util.agora()
            );
        },

        definir_validade(proc, prazo) { 
            proc.dt_validade_autorizacao = moment(proc.dt_autorizacao).add(prazo, 'days').format('YYYY-MM-DD');
        },

        trocar_view_mode (mode) {
            this.iniciando = true;
            this.viewMode = mode;

            this.$nextTick(() => {
                this.iniciando = false;
            });
        },

        vincular_aut (solaut) { 
            if ( !confirm('Confirma vincular a autorização ao atendimento?') )  
                return;

            this.troca_solic = true;

            this.$nextTick(() => { 

                if ( solaut.medico_solicitante ) { 
                    this.dados.atendimento.medico_solicitante = solaut.medico_solicitante;

                    if ( solaut.dt_solicitacao ) { 
                        this.dados.atendimento.dt_solicitacao = solaut.dt_solicitacao;
                    }
                }

                this.troca_solic = false;
                
                this.$util.atualizar(this, { 
                    service: 'solicitacao_autorizacoes',
                    func: 'vincular_marcacao', 
                    dados: { 
                        id_solicitacao_autorizacao: solaut.id_solicitacao_autorizacao,
                        id_marcacao: this.idMarcacao,
                        retirar: false, 
                    },
                    callback: () => { 
                        this.carregar();
                    }
                });

            });
        },

        vincular_orcamento (orc) { 
            this.$util.atualizar(this, { 
                service: 'orcamentos',
                func: 'vincular', 
                dados: { 
                    id_orcamento: orc.id_orcamento,
                    id_marcacao: this.idMarcacao,
                },
                callback: () => { 
                    this.carregar();
                }
            });
        },

        validar_solicitantes () { 
            if ( this.lista_medicos_solicitantes.length > 1 ) { 
                let teste = this.lista_atendimento_procedimentos.find(item => { 
                    if ( item.js_solics ) { 
                        if ( !this.lista_medicos_solicitantes.find(solic => !item.js_solics[solic.id_medico_solicitante] ) ) { 
                            return true;
                        }
                    }
                    
                    return false;
                });

                if ( teste ) { 
                    this.$toast.add({
                        severity: "error",
                        summary: "Atenção",
                        detail: "Procedimento '" + teste.procedimento.search + "' sem solicitante selecionado.",
                        life: 3000
                    });
                    return false;
                }
            }

            return true;
        },

        gerar_laudo (atp) { 
            if ( !confirm('Confirma gerar laudo para ' + atp.procedimento.search + '?') )  
                return;
            
            this.$util.atualizar(this, { 
                service: 'atendimentos', 
                func: 'gerar_laudo',
                dados: { 
                    id_atendimento_procedimento: atp.id_atendimento_procedimento, 
                    nao_coletar: atp.nao_coletar ? true : false, 
                }, 
                callback: () => { 
                    this.carregar();
                }
            });
        },

        abrir_orcamento (orcamento) { 
            $('.modal').modal('hide');
            this.$router.push({ name: 'rorcamento', params: { id_orcamento: orcamento.id_orcamento} });
        },

        calc_valores() { 
            if ( !this.calc.fator ) { 
                this.$toast.add({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Informe o " + ( this.calc.tipo ? 'percentual' : 'fator' ),
                    life: 3000
                });
                return;
            }

            this.editando_valores = true;

            this.$nextTick(() => { 
                this.lista_atendimento_procedimentos.forEach(item => { 
                    if ( this.tem_valor_editavel(item) ) { 
                        switch (this.calc.tipo) {
                            case 'P':
                                item.dc_valor_procedimento = Math.round(item.dc_valor_procedimento * this.calc.fator) / 100;
                                break;

                            case 'A':
                                item.dc_valor_procedimento = Math.round(item.dc_valor_procedimento * ( 100 + this.calc.fator ) ) / 100;
                                break;

                            case 'R':
                                item.dc_valor_procedimento = Math.round(item.dc_valor_procedimento * ( 100 - this.calc.fator ) ) / 100;
                                break;
                        
                            default:
                                item.dc_valor_procedimento = Math.round(item.dc_valor_procedimento * this.calc.fator * 100) / 100;
                                break;
                        }
                    }
                });

                this.editando_valores = false;

                $('#calcValoresModal').modal('hide');
            });
            
        },

        abrir_calcular_valores () { 
            $('#calcValoresModal').modal('show');
        },

        copiar_siglas () { 
            
            let texto = this.lista_atendimento_procedimentos.filter(item => item.procedimento.st_sigla)
                .map(item => item.procedimento.st_sigla)
                .join(",");

            if ( texto ) { 
                this.copiar_clipboard(texto.trim() + ',');
            } else { 
                this.$toast.add({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Nenhuma código encontrado.",
                    life: 3000
                });
            }
            
        },

        ver_amostras () { 
            $('.modal').modal('hide');
            this.$parent.$parent.$refs.amostras.abrir(this.dados.id_atendimento);
        },

        apagar_todos () { 
            if ( !confirm("Confirma remover todos os itens para relançamento?") ) { 
                return;
            }

            this.lista_atendimento_procedimentos.forEach((item, idx) => { 
                if ( idx ) { 
                    this.excluirProcedimento(item, false);
                }
            });
        },

        tem_valor_editavel (item, consumo = null) { 

            if ( this.excluindo )
                return false;

            if ( consumo && consumo.plano && consumo.plano.fl_valor_editavel ) { 
                return true;
            }

            if ( item && item.plano_particular && item.plano_particular.fl_valor_editavel && item.plano_particular.valor_editavel && !item.id_atendimento_procedimento_empacotado ) { 
                return true;
            }

            if ( this.dados && this.dados.atendimento && this.dados.atendimento.plano.fl_valor_editavel && this.dados.atendimento.plano.valor_editavel && !item.id_atendimento_procedimento_empacotado ) { 
                return true;
            }

            return false;

        },

        toggle_proc_solic (proc, solic) { 
            if ( !proc.js_solics ) { 
                proc.js_solics = { s: null };
            }
            
            proc.js_solics[solic.id_medico_solicitante] = !proc.js_solics[solic.id_medico_solicitante];
            this.$forceUpdate();
        },

        incluirSolicitante (indicador = false) { 
            if ( indicador ) { 
                if ( this.dados.atendimento.solicitante_outros.find(item => !item.excluido && item.fl_indicador) ) { 
                    this.$toast.add({
                        severity: "warn",
                        summary: "Atenção",
                        detail: "Indicador já requisitado.",
                        life: 3000
                    });
                    return;
                }
            }

            this.dados.atendimento.atendimento_procedimentos.forEach (item => {
                if ( !item.js_solics ) { 
                    item.js_solics = { s: null };
                }
            });

            this.dados.atendimento.solicitante_outros.push({
                id_atendimento_solicitante: null,
                id_medico_solicitante: "", 
                medico_solicitante: null, 
                id_cid: "", 
                dt_solicitacao: this.dados.atendimento.dt_solicitacao, 
                fl_indicador: indicador,
                excluido: false, 
            });
        },

        exibir_info_pacote (proc) { 
            this.$refs.empac.abrir(proc.id_atendimento_procedimento_empacotado);

            /*
            if ( proc.id_atendimento_procedimento_empacotado ) { 
                this.$util.load(this, { 
                    service: 'atendimentos',
                    func: 'check_pacote',
                    parms: { 
                        id: proc.id_atendimento_procedimento_empacotado, 
                    },
                    loadTo: res => { 
                        this.$toast.add({
                            severity: "info",
                            summary: 'Atendimento: #' + res.atp.id_atendimento,
                            detail: res.atp.procedimento.search,
                            life: 8000
                        });
                    }
                })
            }
            */
        },

        alterar_valor (proc) { 
            this.$refs.aut_valor_editado.abrir(res => { 
                proc.dc_valor_procedimento = res.valor;
                proc.st_obs = 'Autorizado por ' + res.login;
            });
        },

        nao_coletar (proc) {
            proc.nao_coletar = !proc.nao_coletar;
        },

        toggle_show_datas () {
            if ( !this.tem_item_com_senha ) { 
                this.$toast.add({
                    severity: "warn",
                    summary: "Atenção",
                    detail: "Nenhum item com senha.",
                    life: 3000
                });

                return;
            }

            let opcao = !this.show_datas;

            this.lista_atendimento_procedimentos.forEach(item => {
                item.senha_datas = opcao;
            });
        },

        assist_real(proc, info) {
            if ( proc.procedimento && ( proc.procedimento.id_especialidade == 2 || proc.procedimento.id_especialidade == 67 ) ) { 
                return;
            }

            if ( info == 'R' ) {
                let teste = this.lista_atendimento_procedimentos.find(item => item.id_procedimento == proc.id_procedimento
                    && item.fl_iniciado && item.hr_proc_inicio);

                if ( teste )
                    return true;
            } else if ( info == 'A' ) {
                let teste = this.lista_atendimento_procedimentos.find(item => item.id_procedimento == proc.id_procedimento
                    && item.fl_iniciado);

                if ( teste )
                    return true;
            } else if ( info == 'O' ) {
                let teste = this.lista_atendimento_procedimentos.find(item => item.id_procedimento == proc.id_procedimento
                    && item.st_obs);

                if ( teste )
                    return teste.st_obs;
            }
        },

        exibir_senha_datas (proc) {
            proc.senha_datas = true;
            this.$forceUpdate();
        },

        cobranca_particular(proc) {
            proc.id_plano_particular = 1;
        },

        opcao_sobre_fatura (atp) { 
            if ( !this.dados || !this.dados.atendimento || !this.dados.atendimento.plano || !this.dados.atendimento.plano.id_operadora == 1 ) { 
                return false;
            }

            if ( atp && atp.id_plano_particular ) { 
                return false;
            }

            return true; 
        },

        opcao_apenas_cobranca (atp) {

            if ( atp.id_atendimento_procedimento )
                return false;

            if ( !atp.procedimento.id_status_laudo )
                return false;

            if (
                this.dados.atendimento
                && this.dados.atendimento.progresso_atendimentos
                && this.dados.atendimento.progresso_atendimentos.find(pa => [14,15,16].includes(pa.id_etapa_atendimento))
            ) {
                return true;
            }

            return false;

        },

        valor_pacote(pacote) {
            if ( this.dados.plano.id_operadora == 1 ) {

                let sessao_valor;

                if ( pacote.pacote.pacote_sessao_valores ) {
                    sessao_valor = pacote.pacote.pacote_sessao_valores.find(psv => psv.id_plano == this.dados.plano.id_plano);
                }

                return sessao_valor ? sessao_valor.dc_valor : pacote.pacote.dc_valor;

            }
        },

        selec_guia_separada(proc) {
            proc.guia_separada = !proc.guia_separada;
            this.$forceUpdate();
        },

        selec_apenas_cobranca(proc) {
            proc.apenas_cobranca = !proc.apenas_cobranca;
            this.$forceUpdate();
        },

        tem_equipe(proc) {
            if ( proc.procedimento && ( proc.procedimento.id_especialidade == 2 || proc.procedimento.id_especialidade == 67 ) ) { 
                return false;
            }

            return ( proc.procedimento
                && ( proc.procedimento.id_classe_procedimento == 2
                    || _.get(proc, 'item_tabela.in_numero_auxiliares') > 0
                    || ( _.get(proc, 'item_tabela.in_porte_anestesico') > 0 && _.get(proc, 'item_tabela.dc_valor_anest') > 0 )
                )
            );
        },

        toggle_sort_order() {
            this.sort_order = ( this.sort_order == 'A' ? 'L' : 'A' );
        },

        toggle_show_consumo () {
            this.show_consumo = !this.show_consumo;
            this.$util.setCookie('proc_show_consumo', this.show_consumo ? 'S' : 'N');
        },

        selecionar_requisicao () {
            this.$refs.selec_req.abrir(this.dados);
        },

        alterar_qtde(proc, plus, um = false) {
            if ( proc.in_quantidade == 0 && !plus )
                return;

            let atual = proc.in_quantidade;

            if ( um ) { 
                proc.in_quantidade = 1;     
            } else { 
                proc.in_quantidade = proc.in_quantidade + ( 1 * ( plus ? 1 : -1 ) );
            }

            proc.atendimento_procedimento_consumos.forEach(item => {
                if ( !item.fl_nao_repetir ) { 
                    if ( !atual ) { 
                        item.in_quantidade = 1;    
                    } else { 
                        item.in_quantidade = Math.round( ( item.in_quantidade / atual ) * proc.in_quantidade );
                    }
                }
            });
        },

        copiar_senha(proc, apenas_datas = false) {
            this.lista_atendimento_procedimentos.forEach(item => {
                if ( !apenas_datas ) { 
                    item.st_codigo_autorizacao = proc.st_codigo_autorizacao;
                }

                if ( !apenas_datas || item.st_codigo_autorizacao ) { 
                    item.dt_validade_autorizacao = proc.dt_validade_autorizacao;
                    item.dt_autorizacao = proc.dt_autorizacao;
                }
            });
        },

        copiar_info_proc(base) { 

            this.lista_atendimento_procedimentos.forEach(proc => {

                if ( proc.procedimento
                    && ( proc.procedimento.id_classe_procedimento == 2
                        || proc.procedimento.fl_info_data
                        || ( proc.item_tabela && proc.item_tabela.fl_info_data )
                        || this.is_externo ) ) { 

                    proc.proc_inicio = base.proc_inicio;
                    proc.proc_termino = base.proc_termino;

                    if ( !proc.st_proc_via ) { 
                        proc.st_proc_via = base.st_proc_via;
                    }

                    if ( !proc.st_proc_tecnica ) {
                        proc.st_proc_tecnica = base.st_proc_tecnica;
                    }

                    if ( this.is_externo || proc.procedimento.fl_info_data || ( proc.item_tabela && proc.item_tabela.fl_info_data ) ) { 
                        proc.dt_execucao = base.dt_execucao;
                    }

                }

            });

        },

        copiar_equipe_proc(base) { 

            if ( !confirm('Confirma replicar a equipa para as demais cirurgias?') ) { 
                return;
            }

            let recalcular = false;

            this.lista_atendimento_procedimentos.forEach(proc => {

                if ( proc.procedimento
                    && proc.procedimento.id_classe_procedimento == 2
                    && !this.lista_equipe(proc).length ) { 

                    proc.atendimento_procedimento_equipes = _.cloneDeep(base.atendimento_procedimento_equipes).map(equipe => { 
                        equipe.id_atendimento_procedimento_equipe = null;
                        equipe.id_atendimento_procedimento = proc.id_atendimento_procedimento;

                        recalcular = true; 

                        return equipe;
                    });
                }

            });

            if ( recalcular ) { 
                this.recalc_preco_cirurgias();
            }

        },

        exibir_composicao_valor(item, operadora, id_reducao = null, proc = null) {
            const contexto = {
                data: this.dados.atendimento.dt_inicio,
                cirurgia: this.is_cirurgia,
                emergencia: this.dados.recepcao ? this.dados.recepcao.fl_emergencia : false,
                id_procedimento: proc.procedimento ? proc.procedimento.id_procedimento : -1,
                id_plano: this.dados.id_plano,
            };

            this.$refs.cadastroitemtabela.exibir_moeda_corrente(item, operadora, id_reducao, contexto);
        },

        autorizar_online () {

            this.$util.atualizar(this, {
                service: 'atendimentos',
                dados: {
                    ...this.dados,
                    consultorio: this.consultorio
                },
                callback: () => {
                    this.carregar();
                    this.$refs.autoriz.autorizar_marcacao(this.dados);
                }
            });

        },

        buscar_solicitante_pelo_executante() {
            if ( !this.dados || !this.dados.atendimento || !this.dados.atendimento.medico )
                return;

            this.$store.commit('loading', true);

            this.$util.loadItem(this, {
                service: "medicos",
                func: "show",
                parms: {
                    id: this.dados.atendimento.medico.id_pessoa,
                    com_solicitante: true,
                }
            })
            .then(res => {
                if ( res.solicitante ) {
                    this.dados.atendimento.medico_solicitante = res.solicitante;
                } else {
                    this.dados.atendimento.medico_solicitante = {
                        st_nome: res.pessoa.st_nome,
                        st_numero_conselho: res.in_numero_conselho,
                        id_conselho: res.id_conselho,
                        id_estado_conselho: res.id_estado_conselho,
                        especialidade: this.dados.procedimento.especialidade,
                        search: res.pessoa.st_nome,
                    };
                }

                if ( !this.dados.atendimento.dt_solicitacao ) {
                    this.dados.atendimento.dt_solicitacao = this.$util.hoje().substr(0,10);
                }
            })
            .finally(() => {
                this.$store.commit('loading', false);
            });
        },

        cirurgia_alerta_valor() {
            return false;
            /*
            if ( proc && proc.procedimento && proc.procedimento.id_classe_procedimento == 2 ) {

                if ( proc.st_proc_via && proc.st_proc_via == 'U' ) {

                    let teste = this.lista_atendimento_procedimentos.findIndex(item => {
                        return item.id_classe_procedimento == 2 && item.st_proc_via && item.st_proc_via != 'U' &&
                            item.dc_valor_procedimento > proc.dc_valor_procedimento;
                    });

                    if ( teste >= 0 ) {
                        return true;
                    }

                }
            }

            return false;
            */
        },

        lista_graus (proc) {
            if ( !this.graus )
                return [];

            let lista = this.graus.filter(item => item.id_grau_participacao > 1)
                .filter(item => this.dados.fl_particular 
                    || ( ( proc.item_tabela && proc.item_tabela.in_numero_auxiliares ) ? item.id_grau_participacao - 1 <= proc.item_tabela.in_numero_auxiliares : false )
                    || ( item.id_grau_participacao > 5 && this.is_cirurgia )
                    || ( item.id_grau_participacao == 7 && proc.item_tabela.dc_valor_anest > 0 ) 
                    || ( this.dados.plano.operadora.fl_sus )
                );

            return lista;
        },

        removerEquipe(equipe) {
            $('[data-toggle="tooltip"]').tooltip('dispose');
            equipe.excluido = true;
        },

        especialidades_equipe (equipe) {

            if ( !equipe.id_medico )
                return [];

            let name = 'medicos.index' + ( equipe.id_grau_participacao == 7 ? '.anest' : '' );
            let medicos = this.$store.state.options[name];

            if ( !medicos )
                return [];

            let medico = medicos.find(item => item.id_pessoa == equipe.id_medico);

            if ( !medico )
                return [];

            this.$nextTick(() => {
                if ( medico.medico_especialidade.length == 1 ) {
                    equipe.id_especialidade = medico.medico_especialidade[0].id_especialidade;
                }
            });

            return medico.medico_especialidade.map(item => item.especialidade)
                .map(item => {
                    return { id: item.id_especialidade, search: item.search }
                });

        },

        incluirMembro(proc) {

            if ( proc.novo_membro ) {

                let novo_membro = {
                    id_atendimento_procedimento_equipe: null,
                    id_atendimento_procedimento: proc.id_atendimento_procedimento,
                    id_grau_participacao: proc.novo_membro.id_grau_participacao,
                    grau_participacao: proc.novo_membro,
                    id_medico: "",
                    id_medico_rateio: null,
                    id_rateio_medico: null,
                    id_especialidade: "",
                    dc_valor: 0, 
                    excluido: false,
                };

                let valor = this.calc_preco_membro (proc, proc.novo_membro, novo_membro);

                if ( valor ) { 
                    novo_membro.dc_valor = valor;
                }

                proc.atendimento_procedimento_equipes.push(novo_membro);

                proc.atendimento_procedimento_equipes = _.orderBy(proc.atendimento_procedimento_equipes, 'id_grau_participacao');

                this.$nextTick(() => {
                    proc.novo_membro = "";
                });

            }

        },

        calc_preco_membro (proc, grau_partic, ape = null) {
            if ( !grau_partic || ( grau_partic.id_grau_participacao > 7 && !grau_partic.dc_percentual_mesma_via > 0 ) ) {
                return 0;
            }

            if ( proc && proc.item_tabela && proc.item_tabela.fl_cirurg_membros_zerados ) {
                return 0;
            }

            if ( grau_partic.id_grau_participacao == 7 ) {

                if ( !proc.item_tabela && this.particular ) {

                    this.$util.loadItem(this, {
                        service: 'procedimentos',
                        func: 'valor',
                        parms: {
                            id_procedimento: proc.procedimento.id_procedimento,
                            id_plano: proc.id_plano_particular ? proc.id_plano_particular : this.dados.id_plano,
                            data: proc.proc_termino ? this.dados.atendimento.dt_inicio.substr(0,10) + ' ' + proc.proc_termino :
                                this.dados.atendimento.dt_inicio,
                            id_medico: this.dados.id_medico,
                            id_unidade_operacao: this.dados.id_unidade_operacao,
                            id_recepcao: this.dados.id_recepcao,
                        }
                    })
                    .then(res => {

                        if ( res && res.valor && res.valor.valor_anest ) { 
                            ape.dc_valor = res.valor.valor_anest;
                        }
                        
                    });

                }

                return ( proc.item_tabela && proc.item_tabela.dc_valor_anest ) 
                    ? proc.item_tabela.dc_valor_anest : 0;
            }

            let valor = Math.round( ( proc.dc_valor_procedimento ) *             // * proc.in_quantidade
                (
                    grau_partic.id_grau_participacao == 6 ? 10 :
                    (
                        grau_partic.id_grau_participacao == 2 ? 30 :
                        (
                            grau_partic.id_grau_participacao <= 5 ? 20 : 0
                        )
                    )
                )
            ) / 100;

            if ( grau_partic.id_grau_participacao != 7 && grau_partic.dc_percentual_mesma_via ) { 
                valor = Math.round( ( proc.dc_valor_procedimento ) * grau_partic.dc_percentual_mesma_via ) / 100;
            }

            return valor;
        },

        recalc_consumo (atp, apc, calc_item = false) { 

            this.$util.loadItem(this, {
                service: 'consumo',
                func: 'valor',
                parms: {
                    id_plano: atp.plano_particular
                        ? atp.plano_particular.id_plano
                        : ( apc.id_plano ? apc.id_plano : this.dados.id_plano ),
                    id_material: apc.id_material,
                    id_taxa: apc.id_taxa,
                    id_medicamento: apc.id_medicamento,
                    id_unidade_operacao: this.dados.id_unidade_operacao,
                    reembolso_consultorio: this.reembolso_consultorio,
                }
            })
            .then(valor => {
                if ( valor && valor.valor ) {

                    apc.dc_valor_unitario = valor.valor;
                    apc.id_propria = valor.id_propria;
                    apc.id_item_tabela = valor.id_item_tabela;

                } else if ( calc_item ) {
                    this.$toast.add({
                        severity: "error",
                        summary: "Atenção",
                        detail: "Item sem valor cadastrado para essa operadora.",
                        life: 3000
                    });
                }
            });

        },

        recalc_preco_cirurgias (atp = null, recalc_consumo = false, apenas_particular = false) {

            this.lista_atendimento_procedimentos.forEach((proc, chave) => {
                this.recalc_preco_cirurgia(proc, null, chave, atp ? atp.ch_tipo_consulta : null, false, false, recalc_consumo, apenas_particular);
            });

        },

        recalc_preco_cirurgia (proc, callback = null, index = -1, tipo_consulta = null, force_incluindo = false, comps = false, recalc_consumo = false, apenas_particular = false) {

            if ( this.incluindo && !force_incluindo ) { 
                return; 
            }

            if ( proc.js_solics && proc.js_solics.fl_block_valor ) { 
                console.log('recalc block', proc);
                return; 
            } else if ( proc.js_solics && proc.js_solics.norecalc ) { 
                console.log('norecalc', proc);
                return; 
            }

            if ( proc.procedimento.id_procedimento && !this.iniciando ) {

                if ( index == -1 )
                    index = this.lista_atendimento_procedimentos.findIndex(item => item == proc);

                if ( apenas_particular && !this.particular && !proc.id_plano_particular ) { 

                    if ( recalc_consumo ) { 
                        proc.incluir_consumo = false;
                        this.$forceUpdate();

                        proc.atendimento_procedimento_consumos.filter(apc => !apc.excluido && apc.id_plano)
                            .forEach(apc => { 
                                this.recalc_consumo(proc, apc);
                            });
                    }

                } else { 

                    this.$store.commit('loading', true);

                    this.$util.loadItem(this, {
                        service: 'procedimentos',
                        func: 'valor',
                        parms: {
                            id_procedimento: proc.procedimento.id_procedimento,
                            id_plano: proc.id_plano_particular ? proc.id_plano_particular : this.dados.id_plano,
                            data: proc.proc_termino ? this.dados.atendimento.dt_inicio.substr(0,10) + ' ' + proc.proc_termino :
                                this.dados.atendimento.dt_inicio,
                            acomodacao: this.dados.atendimento.st_cirurgia_acomodacao,
                            via: proc.st_proc_via,
                            lista: this.lista_atendimento_procedimentos,
                            tipo_consulta: ( proc.procedimento && proc.procedimento.id_classe_procedimento == 1 ) 
                                ? proc.ch_tipo_consulta
                                : ( tipo_consulta ? tipo_consulta : proc.ch_tipo_consulta ),
                            index: ( index || index === 0 ) ? index : null,
                            id_medico: this.dados.id_medico,
                            id_unidade_operacao: this.dados.id_unidade_operacao,
                            id_recepcao: this.dados.id_recepcao,
                            recem_nascido: this.dados.atendimento.fl_recem_nascido,
                            ch_carater: this.dados.atendimento.ch_carater, 
                            id_marcacao: this.dados.id_marcacao,
                            id_atendimento_procedimento: proc.id_atendimento_procedimento, 
                            id_tipo_atendimento: this.dados.atendimento.id_tipo_atendimento,
                        }
                    })
                    .then(res => {

                        if ( res ) {

                            if ( !res.valor ) {

                                this.$toast.add({
                                    severity: "error",
                                    summary: "Atenção",
                                    detail: "Item sem valor cadastrado.",
                                    life: 3000
                                });

                            } else {

                                this.editando_valores = true;

                                this.$nextTick(() => {

                                    if ( !comps ) { 
                
                                        proc.plano_particular = ( proc.id_plano_particular ? res.plano : null );
                                        
                                        proc.dc_valor_procedimento = res.valor.valor;
                                        proc.dc_valor_filme = res.valor.valor_filme ? res.valor.valor_filme : null;
                                        proc.dc_valor_co = res.valor.valor_co ? res.valor.valor_co : null;
                                        proc.dc_valor_original = _.get(res, 'valor.original');
                                        proc.id_item_tabela = res.valor.id_item_tabela;
                                        proc.item_tabela = res.valor.item_tabela;
                                        proc.id_reducao = res.valor.id_reducao;
                                        proc.dc_percentual_reducao = res.valor.dc_reducao ? res.valor.dc_reducao : 1;

                                        if ( res.proc && res.proc.procedimento_pacotes && res.proc.procedimento_pacotes.length ) { 
                                            proc.procedimento.procedimento_pacotes = res.proc.procedimento_pacotes;
                                        }

                                        if ( proc.atendimento_procedimento_equipes ) {

                                            proc.atendimento_procedimento_equipes.filter(item => item.grau_participacao.id_grau_participacao >= 2 && item.grau_participacao.id_grau_participacao <= 5 )
                                                .forEach(item => {
                                                    item.dc_valor = this.calc_preco_membro(proc, item.grau_participacao);

                                                    /*
                                                    if ( proc.st_proc_via ==  'D' ) {
                                                        item.dc_valor = res.valor.valor * (item.grau_participacao.id_grau_participacao == 2 ? 0.3 : 0.2);
                                                    } else {
                                                        item.dc_valor = res.valor.valor * (item.grau_participacao.id_grau_participacao == 2 ? 0.5 : 0.3);
                                                    }
                                                    */
                                                });

                                            proc.atendimento_procedimento_equipes.filter(item => item.grau_participacao.id_grau_participacao == 6 )
                                                .forEach(item => {
                                                    item.dc_valor = res.valor.valor * 0.1;
                                                });

                                        }

                                    }

                                    /* Código Provisório de Ajuste */

                                    if ( ( this.$store.state.user.id_usuario == 1 || this.recalc_com_comps || comps ) && proc.atendimento_procedimento_consumos && ( comps || proc.atendimento_procedimento_consumos.length == 0 ) && res.consumos && res.consumos.length ) {

                                        let lista_procs = this.dados.atendimento.atendimento_procedimentos
                                            .filter(item => item.fl_ativo && !item.excluido);

                                        let consumos_atuais = lista_procs.flatMap(
                                            item => item.atendimento_procedimento_consumos.filter(consumo => !consumo.excluido)
                                        );

                                        let consumos = res.consumos.filter(item => !item.fl_nao_repetir
                                            || (
                                                    !consumos_atuais.find(ca =>
                                                        ( ca.id_material && ca.id_material == item.id_material ) ||
                                                        ( ca.id_medicamento && ca.id_medicamento == item.id_medicamento ) ||
                                                        ( ca.id_taxa && ca.id_taxa == item.id_taxa )
                                                    )
                                                    && !lista_procs.find(pc => _.get(pc, 'procedimento.id_procedimento') == item.id_procedimento_item && item.id_procedimento_item)
                                                )
                                            );

                                        proc.atendimento_procedimento_consumos = consumos.filter(item => !item.id_procedimento_item);

                                    }

                                    if ( callback )
                                        callback(proc);

                                    this.editando_valores = false;

                                    if ( recalc_consumo ) { 
                                        proc.incluir_consumo = false;
                                        this.$forceUpdate();

                                        proc.atendimento_procedimento_consumos.filter(apc => !apc.excluido)
                                            .forEach(apc => { 
                                                this.recalc_consumo(proc, apc);
                                            });
                                    }

                                });

                            }

                        }

                    }).finally(() => {
                        this.$store.commit('loading', false);
                    });

                }

            }

        },

        lista_equipe (proc) {
            return proc.atendimento_procedimento_equipes.filter(item => !item.excluido);
        },

        carregar (callback = null) {

            this.novoPlanoCobranca = "";
            this.novoPlanoCobrancaProcedimento = "";
            this.carregando = true;
            this.iniciando = true;
            this.rest_exibidas = {};
            this.lancar_solicitante = false; 

            let lista_configs = [];


            if ( !this.configs_loaded ) { 
                this.configs_loaded = true; 

                lista_configs = [
                    'recepcao-recalculo-com-componentes', 
                    'recepcao-recalculo',
                    'pagamento-direto-medico',
                    'solicitantes-multiplos',
                    'medico-lancar-material-consultorio',
                    'pacote-block-edit-qt'
                ];
            }

            let query = { q: [
                {
                    name: "marcacoes.show",
                    id: "marcacao",
                    parms: {
                        id: this.idMarcacao,
                        para_atendimento: true,
                        lancamento_procedimento: true,
                        foto: true,
                    },
                    configs: lista_configs
                }
            ]};

            this.axios.post('/aql?=cmarcacoes.show', query)
                .then(res => {
                    this.dados = res.data.marcacao.data;

                    if ( this.dados.rec_subs && this.dados.rec_subs.length ) {
                        this.dados.id_recepcao = "";
                    }
                    
                    if ( res.data.configs_marcacao ) { 
                        if ( res.data.configs_marcacao['recepcao-recalculo-com-componentes'] ) {
                            this.recalc_com_comps = true;
                        }

                        if ( res.data.configs_marcacao['recepcao-recalculo'] ) {
                            this.habilita_recalc_preco = true;
                        }

                        if ( res.data.configs_marcacao['pagamento-direto-medico'] ) {
                            this.config_pag_medico = true;
                        }

                        if ( res.data.configs_marcacao['solicitantes-multiplos'] ) {
                            this.config_solicitantes_multiplos = true;
                        }

                        if ( res.data.configs_marcacao['medico-lancar-material-consultorio'] ) {
                            this.config_medico_lancar_material_consultorio = true;
                        }

                        if ( res.data.configs_marcacao['pacote-block-edit-qt'] ) {
                            this.config_pacote_bloq_qt = true;
                        }
                        
                    }

                    this.$emit('syncpac', this.dados.paciente);
                    this.load_graus();

                    this.lista_proximo = [];

                    this.dados.atendimento.atendimento_procedimentos.forEach(atp => {
                        if ( atp.outros ) {
                            atp.outros.forEach(consumo => {
                                if ( consumo.id_procedimento_item ) {
                                    this.lista_proximo.push({
                                        id_procedimento_pai: consumo.id_procedimento_pai 
                                            ? consumo.id_procedimento_pai : consumo.id_procedimento, 
                                        id_procedimento: consumo.id_procedimento_item,
                                        in_qtde_default: consumo.in_quantidade, 
                                        dc_valor_default: consumo.dc_valor_unitario,
                                        search: consumo.search,
                                        outro_medico: consumo.outro_medico,
                                        id_outro_medico: consumo.id_outro_medico,
                                        fl_block_valor: consumo.fl_block_valor,
                                        fl_block_laudo: consumo.fl_block_laudo,
                                        fl_block_print: consumo.fl_block_print,
                                        fl_force_guia: consumo.fl_force_guia,
                                    });
                                } else {
                                    this.lista_proximo.push(consumo);
                                }
                            });
                        }
                    });

                    if ( this.lista_proximo.length ) { 
                        this.incluir_proximo();
                    }

                })
                .finally(() => {
                    this.carregando = false;

                    this.$nextTick(() => {
                        this.iniciando = false;
                    });

                    if ( callback )
                        callback();

                    if ( this.particular && this.viewMode == 'A' ) {
                        this.trocar_view_mode('L');
                    }
                });

        },

        incluir_proximo () { 

            let novo = _.pullAt(this.lista_proximo, '0');    

            novo = _.get(novo, '0');

            if ( !novo )
                return;

            this.incluirProcedimento(novo, this.incluir_proximo);
            
        },

        load_graus() {
            if ( this.graus && this.graus.length )
                return;

            if ( this.is_cirurgia || this.is_proc_com_auxilio ) {

                if ( this.$store.state.options['grau_participacoes.idex'] ) {

                    this.graus = this.$store.state.options['grau_participacoes.idex'];

                } else {

                    this.$util.loadItem(this, {
                        service: 'grau_participacoes',
                        func: 'index',
                    })
                    .then(res => {
                        this.graus = res;
                    });

                }
            }
        },

        lista_consumo (proc, tipo = null) {
            if ( !proc.atendimento_procedimento_consumos || !proc.atendimento_procedimento_consumos.length ) { 
                return [];
            }

            if ( ( tipo === false ) && ( proc.incluir_consumo && proc.incluir_consumo !== true ) ) { 
                tipo = proc.incluir_consumo;
            }

            return proc.atendimento_procedimento_consumos.filter(item => !item.excluido
                && ( !tipo 
                    || ( tipo == 'T' && item.id_taxa )
                    || ( tipo == 'MAT' && item.id_material )
                    || ( tipo == 'MED' && item.id_medicamento && !item.id_material && !item.id_taxa )
                 ) );
        },

        total_consumo (proc) {
            if ( !proc.atendimento_procedimento_consumos || !proc.atendimento_procedimento_consumos.length ) { 
                return 0;
            }

            return this.lista_consumo(proc).reduce((total, consumo) => {
                    return total + ( consumo.id_atendimento_procedimento_empacotado ? 0 : ( consumo.in_quantidade * consumo.dc_valor_unitario ) );
                }, 0);
        },

        incluirComponente (proc, parmComponente = null) {
            if ( !this.novoComponente && !parmComponente )
                return;

            let novo = _.cloneDeep(this.novoComponente ? this.novoComponente : parmComponente);
            this.novoComponente = "";

            this.$store.commit('loading', true);

            this.$util.loadItem(this, {
                service: 'consumo',
                func: 'valor',
                parms: {
                    id_plano: proc.plano_particular
                        ? proc.plano_particular.id_plano
                        : ( this.novoPlanoCobranca ? this.novoPlanoCobranca.id_plano : this.dados.id_plano ),
                    id_material: novo.id_material,
                    id_taxa: novo.id_taxa,
                    id_medicamento: novo.id_medicamento,
                    id_unidade_operacao: this.dados.id_unidade_operacao,
                    reembolso_consultorio: this.reembolso_consultorio,
                }
            })
            .then(valor => {
                if ( valor ) {

                    if ( !novo.fl_kit ) { 
                        
                        proc.atendimento_procedimento_consumos.push({
                            id_atendimento_procedimento: proc.id_atendimento_procedimento,
                            id_material: novo.id_material,
                            id_taxa: novo.id_taxa,
                            id_medicamento: novo.id_medicamento,
                            in_quantidade: ( proc.in_quantidade ? proc.in_quantidade : 1 ) * ( novo.dc_quantidade ? novo.dc_quantidade : 1 ),
                            dc_valor_unitario: valor.valor,
                            id_propria: valor.id_propria,
                            id_item_tabela: valor.id_item_tabela,
                            id_simpro: valor.id_simpro,
                            id_brasindice: valor.id_brasindice,
                            search: novo.search,
                            excluido: false,
                            id_plano: proc.plano_particular
                                ? proc.plano_particular.id_plano
                                : ( this.novoPlanoCobranca ? this.novoPlanoCobranca.id_plano : null ),
                            plano: proc.plano_particular
                                ? proc.plano_particular
                                : ( this.novoPlanoCobranca ? this.novoPlanoCobranca : null ),
                            item_tabela: valor.item_tabela,
                            fl_pag_med: false, 
                            id_centro_custo: ( this.centro_base && this.centro_base.centro ) ? this.centro_base.centro.id_centro_custo : null,
                            centro: ( this.centro_base && this.centro_base.centro ) ? this.centro_base.centro : null,
                        });

                    } else if ( valor.consumos && valor.consumos.length ) {

                        let lista_procs = this.dados.atendimento.atendimento_procedimentos.filter(item => item.fl_ativo && !item.excluido);

                        let consumos_atuais = lista_procs.flatMap(
                                item => item.atendimento_procedimento_consumos.filter(consumo => !consumo.excluido)
                                    .map(consumo => {
                                        return {
                                            ...consumo,
                                            id_procedimento: _.get(item, 'procedimento.id_procedimento'),
                                        };
                                    })
                            );

                        valor.consumos.filter(item => !item.fl_nao_repetir
                                || (
                                    !consumos_atuais.find(ca =>
                                        (
                                            ( ca.id_material && ca.id_material == item.id_material ) ||
                                            ( ca.id_medicamento && ca.id_medicamento == item.id_medicamento ) ||
                                            ( ca.id_taxa && ca.id_taxa == item.id_taxa )
                                        )
                                    )
                                )
                            )
                            .forEach(consumo => {
                            this.novoComponente = { 
                                ...consumo,
                                in_quantidade: consumo.dc_quantidade, 
                            };

                            if ( consumo.id_medicamento_item ) { 
                                this.novoComponente.id_medicamento = consumo.id_medicamento_item;
                            }

                            this.incluirComponente(proc);    
                        });
                        
                    }
                } else {
                    this.$toast.add({
                        severity: "error",
                        summary: "Atenção",
                        detail: "Item sem valor cadastrado para essa operadora.",
                        life: 3000
                    });
                }
            })
            .finally(() => {
                this.$store.commit('loading', false);

                this.$nextTick(() => {
                    this.novoComponente = null;
                });
            });

        },

        exige_cid () {
            return this.dados.atendimento.atendimento_procedimentos.find (item => {
                return item.procedimento && item.procedimento.restricao && item.procedimento.restricao.length &&
                    !item.procedimento.restricao[0].fl_nao_possui_cobertura &&
                    item.procedimento.restricao[0].fl_cid_atendimento;
            }) ? true : false;
        },

        restricao_show_obss (proc) { 

            if ( !proc || !proc.procedimento || !proc.procedimento.restricao || !proc.procedimento.restricao.length ) { 
                return;
            }

            let lista = proc.procedimento.restricao.filter(rest => { 
                    return !rest.fl_nao_possui_cobertura && !rest.fl_autorizacao_previa;
                })
                .map(rest => { 
                    return {
                        id: rest.id_restricao_operadora,
                        titulo: rest.st_titulo,
                        obs: rest.st_observacao,
                        add_msg: _.get(rest, 'exibir.texto'),
                    };
                });

            lista.forEach(item => { 

                if ( !this.rest_exibidas['#' + item.id] ) { 

                    if ( item.titulo && item.obs ) { 

                        this.$toast.add({
                            severity: "info",
                            summary: item.titulo + ' (#' + item.id + ')',
                            detail: item.obs + ( item.add_msg ? " \n" + item.add_msg : '' ),
                            life: 8000
                        });

                    }

                }

                this.rest_exibidas['#' + item.id] = true;

            });

        },

        restricao (proc) {
            let rest;

            if ( !proc || !proc.procedimento || proc.ch_tipo_consulta == 'R' || !proc.procedimento.restricao || !proc.procedimento.restricao.length ) {

                rest = {
                    cobertura: true,
                    autorizacao: false,
                    bloquear: false,
                    exige_cid: false,
                    id_operadora: null,
                    titulo: '',
                    id: '',
                    obs: '', 
                };

            } else {

                let add_msg = _.get(proc, 'procedimento.restricao.0.exibir.texto');

                rest = {
                    cobertura: !proc.procedimento.restricao[0].fl_nao_possui_cobertura,
                    autorizacao: proc.procedimento.restricao[0].fl_autorizacao_previa,
                    bloquear: proc.procedimento.restricao[0].fl_nao_possui_cobertura ?
                        proc.procedimento.restricao[0].fl_bloqueia_lancamento :
                        ( proc.numero_sessao && proc.numero_sessao > 1 ? false : proc.procedimento.restricao[0].fl_senha_autorizacao ),
                    exige_cid: proc.procedimento.restricao[0].fl_cid_atendimento,
                    id: proc.procedimento.restricao[0].id_restricao_operadora,
                    id_operadora: proc.procedimento.restricao[0].id_operadora,
                    titulo: proc.procedimento.restricao[0].st_titulo,
                    obs: ( proc.procedimento.restricao[0].st_observacao ? proc.procedimento.restricao[0].st_observacao : '' ) 
                        + ( add_msg ? " \n" + add_msg : '' ), 
                };

            }

            if ( proc.aut_in_status > 0 ) {

                switch (proc.aut_in_status) {
                    case 1:
                    case '1':
                        rest.autorizacao = true;
                        rest.titulo = 'Autorizado ' + ( proc.aut_obs ? ' / ' + proc.aut_obs : '' );
                        rest.cor = 'success';
                        break;

                    case 2:
                    case '2':
                        rest.autorizacao = true;
                        rest.titulo = 'Em análise ' + ( proc.aut_obs ? ' / ' + proc.aut_obs : '' );
                        rest.cor = 'info';
                        break;

                    case 3:
                    case '3':
                        rest.autorizacao = true;
                        rest.titulo = 'Negado ' + ( proc.aut_obs ? ' / ' + proc.aut_obs : '' );
                        rest.cor = 'danger';
                        break;

                    case 4:
                    case '4':
                        rest.autorizacao = true;
                        rest.titulo = 'Autorizado Parcialmente ' + ( proc.aut_obs ? ' / ' + proc.aut_obs : '' );
                        rest.cor = 'warning';
                        break;

                    case 5:
                    case '5':
                        rest.autorizacao = true;
                        rest.titulo = 'Cancelado ' + ( proc.aut_obs ? ' / ' + proc.aut_obs : '' );
                        break;
                }

            }

            return rest;
        },

        copiar_clipboard (text) {
            if (!navigator.clipboard) {
                var textArea = document.createElement("textarea");
                textArea.value = text;
                textArea.style.position="fixed";  //avoid scrolling to bottom
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                    var successful = document.execCommand('copy');
                    var msg = successful ? 'successful' : 'unsuccessful';
                    console.log('Fallback: Copying text command was ' + msg);
                } catch (err) {
                    console.error('Fallback: Oops, unable to copy', err);
                }

                document.body.removeChild(textArea);
            } else {
                navigator.clipboard.writeText(text).then(function() {
                    console.log('Async: Copying to clipboard was successful!');
                }, function(err) {
                    console.error('Async: Could not copy text: ', err);
                });
            }

            this.$toast.add({
                severity: "success",
                summary: "Atenção",
                detail: "Código '" + text + "' copiado para a área de transferência.",
                life: 3000
            });
        },

        incluirConsumo (proc) {
            /*
            if ( !this.pode_alterar_proc(proc) ) {
                return;
            }
            */

            proc.incluir_consumo = !proc.incluir_consumo;
            $('[data-toggle="tooltip"]').tooltip('dispose');
        },

        senhaDetail () {
            this.senhaInfoShow = !this.senhaInfoShow;
        },

        cidDetail() {
            this.cidInfoShow = !this.cidInfoShow;
        },

        restricao2 (atd_proc) {

            if ( atd_proc && atd_proc.procedimento && atd_proc.procedimento.restricao ) {

                return atd_proc.procedimento.restricao.find(item => {
                    return item.fl_nao_possui_cobertura && item.fl_bloqueia_lancamento;
                })
            }

        },

        setNomeMedico (value) {
            this.dados.atendimento.medico_solicitante.st_nome = value;
        },

        pode_lancar_pacote (proc) {
            if ( this.conta ) { 
                return false;
            }

            if ( proc.procedimento.procedimento_pacotes && proc.procedimento.procedimento_pacotes.length &&
                !proc.atendimento_pacote ) {
                return true;
            } else {
                return false;
            }
        },

        refdate(base, add = true, dif = null) {
            if ( !dif )
                return moment(base).format('YYYY-MM-DD');

            if ( add )
                return moment(base).add(dif, "days").format('YYYY-MM-DD');
            else
                return moment(base).subtract(dif, "days").format('YYYY-MM-DD');
        },

        incluirProcedimento (novo = null, callback_final = null, regiao = null, lista_selec_parm = null) {

            if ( this.novoProcedimento && Array.isArray(this.novoProcedimento) ) {
                let lista = _.cloneDeep(this.novoProcedimento);

                this.novoProcedimento = "";

                let item = _.first(lista);

                if ( !item )
                    return;

                lista.splice(0, 1);

                this.incluirProcedimento(item, (items) => {
                    if ( items && items.length ) { 
                        // incluir no início do array
                        lista = [...items, ...lista];
                    }
                    
                    if ( lista.length ) {
                        this.novoProcedimento = lista;
                        this.incluirProcedimento();
                    }
                });

                /*
                lista.forEach(item => {
                    this.incluirProcedimento(item);
                });
                */

                return;
            } else if ( this.novoProcedimento 
                && this.novoProcedimento.js_complementos 
                && this.novoProcedimento.js_complementos.length
                && regiao === '' ) { 

                let padrao = ""; 

                if ( this.novoProcedimento.id_classe_procedimento == 20 ) { 
                    padrao = this.novoProcedimento.js_complementos.find(cmp => cmp == 'Não Definido');

                    if ( !padrao ) { 
                        padrao = ""; 
                    }
                }

                this.base_novo_procedimento = { 
                    procedimento: this.novoProcedimento,
                    st_regiao: padrao, 
                };

                return;

            } else if ( this.novoProcedimento && this.incluindo ) { 

                return;

            } else if ( novo && Array.isArray(novo) ) {

                let lista = _.cloneDeep(novo);
                let item = _.first(lista);

                if ( !item )
                    return;

                lista.splice(0, 1);

                this.incluirProcedimento(item, (items) => {

                    if ( items && items.length ) { 
                        // incluir no início do array
                        lista = [...items, ...lista];
                    }

                    if ( lista.length ) {
                        this.incluirProcedimento(lista, callback_final, regiao, lista_selec_parm);
                    }

                }, regiao, lista_selec_parm);

                return;

            }

            let novo_lancamento = this.novoProcedimento ? true : false;
            let zerar_novoProcedimento = true;
            let comp_procs; 
            let lista_inclusao;

            novo = novo ? novo : this.novoProcedimento;
            this.novoProcedimento = "";

            if ( novo ) {
                this.incluindo = true;
                this.$store.commit('loading', true);

                this.$util.loadItem(this, {
                    service: 'procedimentos',
                    func: 'valor',
                    parms: {
                        id_procedimento_pai: novo.id_procedimento_pai, 
                        id_procedimento: novo.id_procedimento,
                        id_plano: this.novoPlanoCobrancaProcedimento
                            ? this.novoPlanoCobrancaProcedimento.id_plano
                            : this.dados.id_plano,
                        is_plano_particular: this.novoPlanoCobrancaProcedimento ? true : false,
                        id_medico: this.dados.id_medico,
                        id_unidade_operacao: this.dados.id_unidade_operacao,
                        id_recepcao: this.dados.id_recepcao,
                        cadastro: this.dados.paciente.cadastro,
                        lista: this.lista_atendimento_procedimentos,
                        index: -1,
                        incluir: true,
                        data: this.dados.atendimento.dt_inicio,
                        acomodacao: this.dados.atendimento.st_cirurgia_acomodacao,
                        reembolso_consultorio: this.reembolso_consultorio,
                        emergencia: this.dados.recepcao ? this.dados.recepcao.fl_emergencia : false,
                        ch_carater: this.dados.atendimento.ch_carater,
                        id_marcacao: this.dados.id_marcacao,
                        id_tipo_atendimento: this.dados.atendimento.id_tipo_atendimento,
                        lancamento: true, 
                        regiao: regiao, 
                    }
                })
                .then(res => {

                    if ( res ) {

                        if ( novo.id_pacote && !_.get(res, 'proc.pacote.fl_definir_cobertura')) {

                            let novos = [];
                            let novos_comp = [];

                            res.lista.forEach(element => {

                                if ( element.valor ) {

                                    let rest = this.restricao (element);

                                    if ( rest && !rest.cobertura && rest.bloquear ) { 

                                        this.$toast.add({ 
                                            severity: "error",
                                            summary: ( rest.titulo ? rest.titulo : "Atenção" ),
                                            detail: "Procedimento '" + ( ( element && element.procedimento ) ? element.procedimento.st_procedimento : '' ) + "' não coberto pela operadora."
                                                + ( rest.obs ? '\n\n' + rest.obs : '' ),
                                            life: 10000
                                        });

                                    } else { 

                                        let id_plano_partic; 

                                        if ( rest && !rest.cobertura && !rest.bloquear ) { 
                                            if ( confirm(rest.titulo + "\n \n '" + element.procedimento.search + "' não possui cobertura. Deseja lançar como cobrança particular?"
                                                + ( rest.obs ? "\n \n" + rest.obs : '' ) ) ) { 
                                                id_plano_partic = 1;
                                            }
                                        }

                                        novos.push({
                                            id_item_tabela: element.valor.id_item_tabela,
                                            item_tabela: element.valor.item_tabela,
                                            procedimento: element.procedimento,
                                            in_quantidade: ( _.get(element.procedimento, 'in_qtde_default') ||  _.get(element, 'in_quantidade') )
                                                ? ( _.get(element.procedimento, 'in_qtde_default') > 1 ? _.get(element.procedimento, 'in_qtde_default') : ( _.get(element, 'in_quantidade', 1) ? _.get(element, 'in_quantidade', 1) : 1 ) )
                                                : 1,
                                            dc_valor_procedimento: novo.dc_valor_comp ? novo.dc_valor_comp : element.valor.valor,
                                            dc_valor_original: _.get(element, 'valor.original'),
                                            excluido: false,
                                            fl_ativo: true,
                                            incluir_consumo: false,
                                            atendimento_procedimento_consumos: element.consumos.filter(cons => !cons.id_procedimento_item),
                                            atendimento_procedimento_equipes: [],
                                            id_reducao: element.valor.id_reducao,
                                            dc_percentual_reducao: element.valor.dc_reducao ? element.valor.dc_reducao : 1,
                                            outro_medico: novo.outro_medico ? true : false,
                                            dc_valor_filme: element.valor.valor_filme ? element.valor.valor_filme : null, 
                                            dc_valor_co: element.valor.valor_co ? element.valor.valor_co : null, 
                                            restricao: this.restricao (element),
                                            js_solics: { s: null },
                                            st_codigo_autorizacao: "",
                                            dt_autorizacao: "", 
                                            dt_validade_autorizacao: "", 
                                            senha_datas: false, 
                                            atendimento_pacote: null, 
                                            codigo_tabela: null, 
                                            nao_coletar: ( lista_selec_parm || this.nao_coletar_default || ( this.dados.unidade_operacao && this.dados.unidade_operacao.fl_lab_pendente ) || ( this.dados.atendimento && this.dados.atendimento.dt_inicio.substr(0,10) > this.$util.hoje() ) ) 
                                                ? true 
                                                : false,
                                            id_amostra_material: element.id_amostra_material,
                                            id_plano_particular: id_plano_partic,
                                            fl_pag_med: false, 
                                        });        
                                        
                                    
                                    }

                                    novos_comp = novos_comp.concat(element.consumos.filter(item => item.id_procedimento_item));

                                    /*
                                    element.consumos.filter(item => item.id_procedimento_item)
                                        .forEach(item => {
                                            this.incluirProcedimento({
                                                id_procedimento_pai: item.id_procedimento,
                                                id_procedimento: item.id_procedimento_item,
                                                outro_medico: novo.outro_medico,
                                                id_outro_medico: novo.id_outro_medico,
                                            });
                                        });
                                    */

                                }

                            });

                            if ( novos && novos.length ) { 
                                novos.forEach(item => {
                                    this.dados.atendimento.atendimento_procedimentos.push(item);
                                });
                            }

                            novos_comp.forEach(item => {
                                this.incluirProcedimento({
                                    id_procedimento_pai: item.id_procedimento,
                                    id_procedimento: item.id_procedimento_item,
                                    outro_medico: novo.outro_medico,
                                    id_outro_medico: novo.id_outro_medico,
                                    fl_block_valor: item.fl_block_valor,
                                    fl_block_laudo: item.fl_block_laudo,
                                    fl_block_print: item.fl_block_print,
                                    fl_force_guia: item.fl_force_guia,
                                    dc_valor_comp: ( item.valor_fixo && item.dc_valor_unitario ) ? item.dc_valor_unitario : null, 
                                });
                            });

                        } else {

                            if ( !res.valor ) {

                                this.$toast.add({
                                    severity: "error",
                                    summary: "Atenção",
                                    detail: "Item sem valor cadastrado.",
                                    life: 3000
                                });

                            } else {

                                let js_solics = null;

                                if ( novo.fl_block_valor || this.novo_block_valor ) { 
                                    
                                    res.valor.valor = 0;
                                    js_solics = { 
                                        fl_block_valor: true, 
                                    }

                                    if ( novo.fl_force_guia ) { 
                                        js_solics.fl_force_guia = true;
                                    }

                                    res.sem_preco = false;

                                } else { 

                                    if ( res.sem_preco && res.proc && res.proc.search ) { 

                                        /*
                                            this.$toast.add({
                                                severity: "error",
                                                summary: res.proc.search,
                                                detail: "Item sem valor cadastrado. 222",
                                                life: 6000
                                            });
                                        */

                                    }

                                }

                                if ( novo.fl_block_laudo ) { 
                                    if ( !js_solics ) { 
                                        js_solics = {
                                            fl_block_laudo: true, 
                                        };
                                    } else { 
                                        js_solics.fl_block_laudo = true;
                                    }
                                }

                                if ( novo.fl_block_print ) { 
                                    if ( !js_solics ) { 
                                        js_solics = {
                                            fl_block_print: true, 
                                        };
                                    } else { 
                                        js_solics.fl_block_print = true;
                                    }
                                }

                                if ( novo.fl_force_guia ) { 
                                    if ( !js_solics ) { 
                                        js_solics = {
                                            fl_force_guia: true, 
                                        };
                                    } else { 
                                        js_solics.fl_force_guia = true;
                                    }
                                }

                                let proced = ( res.proc ? res.proc : novo );

                                let lista_procs = this.dados.atendimento.atendimento_procedimentos
                                    .filter(item => item.fl_ativo && !item.excluido);

                                let consumos_atuais = lista_procs.flatMap(
                                        item => item.atendimento_procedimento_consumos.filter(consumo => !consumo.excluido)
                                            .map(consumo => {
                                                return {
                                                    ...consumo,
                                                    id_procedimento: _.get(item, 'procedimento.id_procedimento'),
                                                };
                                            })
                                    );

                                if ( novo.id_procedimento == novo.id_procedimento_pai ) { 
                                    res.consumos = [];
                                }

                                let consumos = res.consumos.filter(item => !item.fl_nao_repetir
                                    || (
                                            !consumos_atuais.find(ca =>
                                                (
                                                    ( ca.id_material && ca.id_material == item.id_material ) ||
                                                    ( ca.id_medicamento && ca.id_medicamento == item.id_medicamento ) ||
                                                    ( ca.id_taxa && ca.id_taxa == item.id_taxa )
                                                )
                                                && (
                                                    ca.id_procedimento == proced.id_procedimento
                                                    || !item.fl_nao_repetir_mesmo_proc
                                                )
                                            )
                                            && !lista_procs.find(pc => _.get(pc, 'procedimento.id_procedimento') == item.id_procedimento_item && item.id_procedimento_item)
                                        )
                                    );

                                let force_nao_coletar = false;
                                let id_amostra_material = novo.id_amostra_material;

                                if ( id_amostra_material && proced.amostras ) { 
                                    if ( !proced.amostras.find(amostra => amostra.id_amostra_material == id_amostra_material ) ) { 
                                        id_amostra_material = null;
                                    }
                                }

                                if ( !id_amostra_material ) { 
                                    if ( proced.amostras && proced.amostras.length ) {
                                        const padrao = proced.amostras.find(amostra => amostra.fl_padrao);

                                        if ( padrao ) { 
                                            id_amostra_material = padrao ? padrao.id_amostra_material : null;
                                        } else { 
                                            let contagem = this.dados.atendimento.atendimento_procedimentos.filter(oatp => !oatp.excluido && oatp.fl_ativo && oatp.procedimento.id_procedimento == proced.id_procedimento).length;
                                            
                                            const padrao_ordem = proced.amostras.find(amostra => amostra.in_regra_ordem == contagem + 1);

                                            id_amostra_material = padrao_ordem ? padrao_ordem.id_amostra_material : null;

                                            if ( padrao_ordem && contagem ) { 
                                                force_nao_coletar = true;
                                            }
                                        }
                                        
                                    }
                                }

                                let dados_pacote = res.pacote ? res.pacote : {};

                                // Médico para procedimentos laboratóriais...

                                if ( !novo.outro_medico && ( proced.id_especialidade == 2 || proced.id_especialidade == 67 ) ) {
                                    let outro_lab = this.dados.atendimento.atendimento_procedimentos.find(oatp => !oatp.excluido && oatp.fl_ativo && (oatp.procedimento.id_especialidade == 2 || oatp.procedimento.id_especialidade == 67));

                                    if ( outro_lab && outro_lab.outro_medico && outro_lab.id_outro_medico ) {
                                        novo.outro_medico = true;
                                        novo.id_outro_medico = outro_lab.id_outro_medico;
                                    }
                                }

                                let rest = this.restricao ({ 
                                    procedimento: {
                                        ...proced,
                                        restricao: res.restricao,
                                    }
                                });

                                this.restricao_show_obss({ 
                                    procedimento: {
                                        ...proced,
                                        restricao: res.restricao,
                                    }
                                });

                                let novo_proc;

                                if ( rest && !rest.cobertura && rest.bloquear ) { 

                                    this.$toast.add({ 
                                        severity: "error",
                                        summary: ( rest.titulo ? rest.titulo : "Atenção1" ),
                                        detail: "Procedimento '" + proced.search + "' não coberto pela operadora. " 
                                            + ( rest.obs ? '\n\n' + rest.obs : '' ),
                                        life: 10000,
                                    });

                                } else { 

                                    if ( novo_lancamento && proced.amostras && proced.amostras.length > 1 ) { 
                                        proced.amostras = _.orderBy(proced.amostras, 'search');
                                    }

                                    if ( novo_lancamento && !id_amostra_material && ( proced.amostras && proced.amostras.length > 1 ) ) { 

                                        zerar_novoProcedimento = false;
                                        
                                        proced.id_amostra_material = "";
                                     
                                        proced.amostras = _.orderBy(proced.amostras, 'search');

                                        this.novoProcedimento = proced;
                                        
                                        this.$toast.add({ 
                                            severity: "info",
                                            summary: proced.search,
                                            detail: "Selecione o material.",
                                            life: 3000
                                        });

                                        return;

                                    }

                                    let id_plano_partic = ""; 
                                    let force_recalc = false;

                                    if ( !this.novoPlanoCobrancaProcedimento && rest && !rest.cobertura && !rest.bloquear ) { 
                                        if ( confirm(rest.titulo + "\n \n '" + proced.search + "' não possui cobertura. Deseja lançar como cobrança particular?"
                                            + ( rest.obs ? "\n \n" + rest.obs : '' ) ) ) { 
                                            id_plano_partic = 1;
                                            force_recalc = true; 
                                        }
                                    } else { 

                                        if ( res.sem_preco && res.proc && res.proc.search ) { 

                                            this.$toast.add({
                                                severity: "error",
                                                summary: res.proc.search,
                                                detail: "Item sem valor cadastrado.",
                                                life: 6000
                                            });

                                        }

                                    }

                                    if ( novo.dc_valor_default && res.valor ) { 
                                        res.valor.valor = novo.dc_valor_default;
                                        res.valor.original = novo.dc_valor_default;

                                        if ( !js_solics ) { 
                                            js_solics = {
                                                norecalc: true, 
                                            };
                                        } else { 
                                            js_solics.norecalc = true;
                                        }
                                    }

                                    novo_proc = {
                                        id_item_tabela: res.valor.id_item_tabela,
                                        item_tabela: res.valor.item_tabela,
                                        procedimento: {
                                            ...proced,
                                            restricao: res.restricao,
                                        },
                                        in_quantidade: _.get(novo, 'in_qtde_default', 1) 
                                            ? _.get(novo, 'in_qtde_default', 1) 
                                            : ( _.get(res, 'in_quantidade') > 1 ? _.get(res, 'in_quantidade') : 1 ),
                                        dc_valor_procedimento: novo.dc_valor_comp ? novo.dc_valor_comp : res.valor.valor,
                                        dc_valor_original: _.get(res, 'valor.original'),
                                        excluido: false,
                                        fl_ativo: lista_selec_parm ? false : true,
                                        incluir_consumo: false,
                                        atendimento_procedimento_consumos: consumos.filter(item => !item.id_procedimento_item),
                                        atendimento_procedimento_equipes: [],
                                        id_reducao: res.valor.id_reducao,
                                        dc_percentual_reducao: res.valor.dc_reducao ? res.valor.dc_reducao : 1,
                                        outro_medico: novo.outro_medico ? true : false,
                                        id_outro_medico: novo.id_outro_medico,
                                        ch_tipo_consulta: ( this.consultorio && proced.st_procedimento.substr(0,8) == 'CONSULTA' ) ? 'R' : '',
                                        id_amostra_material: id_amostra_material,
                                        // Campos da Autorização
                                        st_codigo_autorizacao: _.get(novo, 'st_codigo_autorizacao') ? _.get(novo, 'st_codigo_autorizacao') : _.get(res, 'st_codigo_autorizacao'),
                                        dt_autorizacao: _.get(novo, 'dt_autorizacao') ? _.get(novo, 'dt_autorizacao') : _.get(res, 'dt_autorizacao'),
                                        dt_validade_autorizacao: _.get(novo, 'dt_validade_autorizacao', _.get(novo, 'dt_valid_termino')) ? _.get(novo, 'dt_validade_autorizacao', _.get(novo, 'dt_valid_termino')) : _.get(res, 'dt_validade_autorizacao'),
                                        ...dados_pacote,
                                        id_plano_particular: this.novoPlanoCobrancaProcedimento
                                            ? this.novoPlanoCobrancaProcedimento.id_plano
                                            : id_plano_partic,
                                        plano_particular: this.novoPlanoCobrancaProcedimento,
                                        guia_separada: res.valor.item_tabela ? res.valor.item_tabela.fl_guia_separada : false,
                                        dc_valor_filme: res.valor.valor_filme ? res.valor.valor_filme : null, 
                                        dc_valor_co: res.valor.valor_co ? res.valor.valor_co : null, 
                                        st_regiao: regiao ? regiao : '', 
                                        js_solics: ( ( !js_solics && proced.id_especialidade == 2 ) ? { amo: "" } : js_solics ), 
                                        restricao: rest,
                                        senha_datas: false, 
                                        atendimento_pacote: null, 
                                        codigo_tabela: null, 
                                        nao_coletar: ( lista_selec_parm || this.nao_coletar_default || force_nao_coletar || ( this.dados.unidade_operacao && this.dados.unidade_operacao.fl_lab_pendente ) || ( this.dados.atendimento && this.dados.atendimento.dt_inicio.substr(0,10) > this.$util.hoje() ) ) 
                                            ? true 
                                            : false,
                                        id_consumo_procedimento: novo.id_consumo_procedimento, 
                                        force_recalc: force_recalc, 
                                        fl_pag_med: false, 
                                    };

                                    if ( res.pacote && res.pacote.atendimento_pacote && res.pacote.numero_sessao <= res.pacote.total_sessao ) { 
                                        novo_proc.atendimento_pacote = res.pacote.atendimento_pacote;
                                        novo_proc.numero_sessao = res.pacote.numero_sessao;
                                        novo_proc.total_sessao = res.pacote.total_sessao;
                                    }

                                    if ( lista_selec_parm ) { 
                                        lista_selec_parm.items.push(novo_proc);
                                    }

                                    this.dados.atendimento.atendimento_procedimentos.push(novo_proc);

                                    if ( force_recalc ) { 
                                        this.$nextTick(() => { 
                                            this.recalc_preco_cirurgia(novo_proc, null, -1, null, true);
                                        });
                                    }

                                }

                                let lista_selecao;

                                if ( res.conf_selec ) { 
                                    res.tempos = 1;
                                }

                                comp_procs = consumos.filter(item => item.id_procedimento_item);
                                let comp_selecs = consumos.filter(item => item.id_procedimento_item || res.conf_selec);

                                if ( comp_selecs.length && _.get(res, 'tempos') > 0 ) { 
                                    
                                    lista_selecao = { 
                                        proc: { 
                                            id_procedimento: proced.id_procedimento,
                                            search: proced.search,
                                        },
                                        tempos: res.tempos, 
                                        items: comp_selecs.filter(item => !item.id_procedimento_item), 
                                        conf_selec: false, 
                                    };

                                    if ( lista_selecao.items.length ) { 
                                        lista_selecao.conf_selec = true;
                                    }
                                
                                    this.check_selecao.push(lista_selecao);

                                    this.$refs.curva.abrir(this.check_selecao, () => { 
                                        this.check_selecao = [];
                                    });

                                }

                                lista_inclusao = comp_procs.map(item => { 
                                    console.log('item_comp', item);
                                    return {
                                        id_procedimento_pai: item.id_procedimento_pai
                                            ? item.id_procedimento_pai
                                            : item.id_procedimento,
                                        id_procedimento: item.id_procedimento_item,
                                        in_qtde_default: item.in_quantidade, 
                                        outro_medico: item.outro_medico,
                                        id_outro_medico: item.id_outro_medico,
                                        fl_block_valor: item.fl_block_valor, 
                                        fl_block_laudo: item.fl_block_laudo, 
                                        fl_block_print: item.fl_block_print, 
                                        fl_force_guia: item.fl_force_guia,
                                        id_amostra_material: item.id_amostra_material ? item.id_amostra_material : id_amostra_material, 
                                        id_consumo_procedimento: item.id_consumo_procedimento, 
                                        dc_valor_comp: ( item.valor_fixo && item.dc_valor_unitario ) ? item.dc_valor_unitario : null, 
                                    };
                                });

                                if ( !callback_final || ( comp_selecs && comp_selecs.length && _.get(res, 'tempos') > 0 ) ) { 
                                    this.incluirProcedimento(lista_inclusao, null, null, lista_selecao);                                
                                    comp_procs = null;
                                } else { 
                                    comp_procs = lista_inclusao;
                                }

                                if ( this.dados.atendimento.id_tipo_atendimento == 4
                                    && !( this.dados.recepcao ? this.dados.recepcao.fl_emergencia : false ) ) {

                                    if ( window && ( window.redir == 'viterbo' ) ) { 

                                        this.dados.atendimento.id_tipo_atendimento = 3;    

                                    } else { 

                                        if ( proced.id_classe_procedimento == 17 ) { 
                                            this.dados.atendimento.id_tipo_atendimento = 3;    
                                        } else { 
                                            this.dados.atendimento.id_tipo_atendimento = 5;
                                        }

                                    }

                                }

                                if ( _.get(res, 'valor.recalcular') ) {

                                    res.valor.recalcular.forEach(item => {

                                        if ( isNaN(item) && item.slice(0,1) == '#' && novo_proc ) { 
                                            if ( !novo_proc.recalcular ) { 
                                                novo_proc.recalcular = [];
                                            }

                                            novo_proc.recalcular.push(item);
                                            return;
                                        }

                                        this.recalc_preco_cirurgia(
                                            this.lista_atendimento_procedimentos[item],
                                            null,
                                            item,
                                            null, 
                                            true
                                        );
                                    });

                                }

                                this.load_graus();

                            }

                        }

                    }

                }).finally(() => {

                    this.$store.commit('loading', false);

                    this.$nextTick(() => {
                        novo = null;

                        if ( zerar_novoProcedimento ) { 
                            this.novoProcedimento = null;
                        }

                        this.incluindo = false;

                        if ( callback_final ) {
                            callback_final(comp_procs);
                        }
                    });
                });

            }

        },

        pode_alterar_proc(proc, msg = true) {
            if ( this.reembolso_consultorio && proc.id_atendimento_procedimento && proc.cd_valor_total > 0 ) {
                if ( msg ) {
                    this.$toast.add({
                        severity: "error",
                        summary: "Atenção",
                        detail: "Atendimento com reembolso. Procedimento do contrato não pode ser alterado: "
                            + proc.procedimento.search,
                        life: 3000
                    });
                }
                return false;
            }

            return true;
        },

        pode_alterar_qt(proc) {
            if ( this.config_pacote_bloq_qt && proc.atendimento_pacote ) { 
                return false;
            }

            if ( !proc.id_reducao 
                && ( proc.procedimento.id_especialidade != 2 && proc.procedimento.id_especialidade != 67 )
                && this.lista_atendimento_procedimentos.find(item => item.id_procedimento == proc.id_procedimento && item.id_reducao && !item.excluido) ) {
                return false;
            }

            return true;
        },

        excluirConsumo (consumo) { 
            this.excluindo = true;
            this.editando_valores = true;

            this.$nextTick(() => { 
                consumo.excluido = true;

                this.excluindo = false;
                this.editando_valores = false;
            });
        },

        excluirProcedimento (proc, confirmar = false) {
            if ( !this.pode_alterar_proc(proc) ) {
                return;
            }

            if ( confirmar ) {
                if ( proc.id_atendimento_procedimento && this.dados.canc_motivos && this.dados.canc_motivos.length ) { 
                    this.$refs.canc_atd_proc.cancelar(proc, this.dados.canc_motivos, this.excluirProcedimento);
                    return;       
                }
                
                if ( !confirm('Confirma remover o procedimento ' + _.get(proc, 'procedimento.search') + '?') ) {
                    return;
                }
            }

            this.excluindo = true;

            this.$nextTick(() => {

                proc.excluido = true;

                let lista_recalc = this.lista_atendimento_procedimentos.map((item, chave) => {
                        item.chave = chave;
                        return item;
                    })
                    .filter(item => item.id_reducao
                        && item.procedimento.id_classe_procedimento == proc.procedimento.id_classe_procedimento
                    );

                if ( lista_recalc && lista_recalc.length ) {

                    lista_recalc.forEach(item => {
                        this.recalc_preco_cirurgia(item, null, item.chave);
                    });

                }

                this.$nextTick (() => {
                    this.$forceUpdate ();
                    this.excluindo = false;
                });

            });
            // this.dados.atendimento.atendimento_procedimentos.splice(index, 1);
        },

        temItemBloqueado () {

            let item = this.dados.atendimento.atendimento_procedimentos.filter(item => !item.excluido && !item.id_plano_particular)
                .find(item => {
                    return this.restricao2(item);
                });

            if ( item ) {
                return {
                    ...item,
                    restricao: this.restricao2(item),
                }
            } else {
                return;
            }

        },

        ciente() { 
            this.dados.ciente = true;
            this.$parent.proxima_etapa();
        },

        atualizar () {
            if ( this.dados && this.dados.atendimento && this.dados.atendimento.atendimento_procedimentos ) { 
                this.dados.atendimento.atendimento_procedimentos.forEach(atp => { 
                    if ( !atp.excluido && !atp.fl_ativo ) { 
                        atp.excluido = true;
                    }
                });
            }

            if ( ( _.get(this.dados, 'plano.operadora.guia_operadora.ch_tipo_copart') == 'D' || _.get(this.dados, 'plano.operadora.guia_operadora.ch_tipo_copart') == 'A' )
                && !this.valorTotalExtraParticular
                // && !_.get(this.dados, 'atendimento.atendimento_procedimentos.0.id_atendimento_procedimento') 
                && this.valor_copart === null ) { 
                this.$refs.copart.abrir();
                return;
            }

            // Validação de existência de orçamento...

            if ( !this.dados.orcamento 
                && this.dados.orcs 
                && this.dados.orcs.length 
                && this.lista_atendimento_procedimentos.length 
                && !_.get(this.lista_atendimento_procedimentos, '0.id_atendimento_procedimento') ) { 

                if ( !confirm('Este paciente possui orçamento registrado. Confirma avançar sem vincular orçamento ao atendimento do paciente?') ) { 
                    return;
                }

            }

            // Validação da existência de autorização

            if ( !this.dados.autorizacao 
                && this.dados.auts 
                && this.dados.auts.length 
                && this.lista_atendimento_procedimentos.length 
                && !_.get(this.lista_atendimento_procedimentos, '0.id_atendimento_procedimento') ) { 

                if ( !confirm('Este paciente possui autorização registrada. Confirma avançar sem vincular autorização ao atendimento do paciente?') ) { 
                    return;
                }

            }

            //

            let query = {
                q: [
                    {
                        name: 'atendimentos.store',
                        id: "retorno",
                        parms: {
                            ...this.dados,
                            consultorio: this.consultorio,
                            assistencia: this.assistencia,
                            lancamento: true, 
                            valor_copart: this.valor_copart,
                        }
                    }
                ]
            };

            return this.axios.post('/aql?a=atendimentos.store', query)
                    .then(res => {
                        if ( res.data.retorno.status == 200 || res.data.retorno.status == 290 || res.data.retorno.status == 291 ) {
                            return res;
                        } else if ( res.data.retorno.status >= 400 && res.data.retorno.data && res.data.retorno.data.refresh ) {
                            this.carregar();
                        } else if ( res.data.retorno.status >= 403 && res.data.retorno.data && res.data.retorno.data.instrucoes ) { 
                            this.$refs.ciente.abrir(res.data.retorno.data.instrucoes);
                        }

                        this.valor_copart = null;
                    });
        },

        ver_pacotes (pacotes, index) {
            this.indexProcPacote = this.pacotes ? null : index;
            this.pacotes = this.pacotes ? null : pacotes;

            this.$forceUpdate();
        },

        fechar_modal_pacotes () {
            this.indexProcPacote = null;
            this.pacotes = null;
        },

        fechar_modal_qtde_sessoes () {
            $('#qtdeSessaoModal').modal('hide');
        },

        fechar_modal (modal) {
            $('#' + modal).modal('hide');
        },

        selecionar_pacote(pacote, total = null, calculado = false, valor = null, previas = 0, obs = "") {

            if ( !calculado && pacote && !pacote.qtde_original ) {
                pacote.qtde_original = pacote.in_quantidade;
            }

            console.log('pacote1', total, calculado, valor, pacote.in_quantidade, pacote);

            if ( previas && total && ( parseInt(previas) >= parseInt(total) ) ) { 
                this.$toast.add({
                    severity: "error",
                    summary: "Atenção",
                    detail: "Quantidade de sessões prévias não pode ser igual ou superior ao total de sessões. \n"
                        + "Prévias: " + previas + "\n"
                        + "Total: " + total,
                    life: 3000
                });
                return;
            }
            
            if ( pacote && pacote.pacote && pacote.pacote.fl_combo && pacote.pacote.fl_combo_pacote && !calculado ) {

                let proc = {
                    procedimento: pacote.pacote.procedimento,
                }

                if ( pacote.pacote.fl_particular ) {
                    this.selecionar_pacote(pacote, total, true, pacote.pacote.dc_valor, previas, obs);
                } else {
                    this.recalc_preco_cirurgia(proc, () => {
                        this.selecionar_pacote(pacote, total, true, proc.dc_valor_procedimento, previas, obs);
                    });
                }

                return;

            }

            if ( total && ( previas > 0 ) ) { 
                total = total - previas;
            }

            // if ( !pacote.pacote.fl_particular ) {

                if ( pacote.pacote.fl_qtde_variada && !total ) {
                    this.sel_pacote = pacote;
                    this.qtde_sessoes = pacote.in_quantidade;
                    this.qtde_previas = 0;
                    this.sessao_obs = "";
                    $('#qtdeSessaoModal').modal('show');
                    return;
                } else if ( pacote.pacote.fl_qtde_variada && total ) {
                    pacote.in_quantidade = total;
                    $('#qtdeSessaoModal').modal('hide');
                }

            // }

            // this.dados.atendimento.atendimento_procedimentos / lista_atendimento_procedimentos

            this.lista_atendimento_procedimentos[this.indexProcPacote].numero_sessao = 1;
            this.lista_atendimento_procedimentos[this.indexProcPacote].acum_sessao = this.lista_atendimento_procedimentos[this.indexProcPacote].in_quantidade;
            this.lista_atendimento_procedimentos[this.indexProcPacote].total_sessao = pacote.in_quantidade;

            if ( this.config_pacote_bloq_qt && this.lista_atendimento_procedimentos[this.indexProcPacote].in_quantidade != 1 ) { 
                this.alterar_qtde(this.lista_atendimento_procedimentos[this.indexProcPacote], true, true);
                this.lista_atendimento_procedimentos[this.indexProcPacote].acum_sessao = 1; 
            }
            
            if ( !pacote.pacote.fl_cobrar_mat_separado && this.lista_atendimento_procedimentos[this.indexProcPacote].atendimento_procedimento_consumos ) {

                if (  !this.lista_atendimento_procedimentos[this.indexProcPacote].id_atendimento_procedimento ) {
                    if ( !this.lista_atendimento_procedimentos[this.indexProcPacote].atendimento_procedimento_consumos.find(consumo => consumo.id_plano) ) { 
                        this.lista_atendimento_procedimentos[this.indexProcPacote].atendimento_procedimento_consumos = [];
                    }
                } else {
                    this.lista_atendimento_procedimentos[this.indexProcPacote].atendimento_procedimento_consumos.forEach(consumo => consumo.excluido = true);
                }

            }

            if ( !valor ) {

                if ( this.dados.plano.id_operadora == 1 && pacote.pacote.pacote_sessao_valores ) {
                    let sessao_valor = pacote.pacote.pacote_sessao_valores.find(psv => psv.id_plano == this.dados.plano.id_plano);

                    if ( sessao_valor ) {
                        valor = sessao_valor.dc_valor * ( ( pacote.pacote.fl_qtde_variada && ( pacote.pacote.ch_var_tipo_valor != 'T' ) ) ? pacote.in_quantidade : 1 );
                    }
                }

            }

            let id_plano_particular = this.lista_atendimento_procedimentos[this.indexProcPacote].id_plano_particular;

            this.lista_atendimento_procedimentos[this.indexProcPacote].atendimento_pacote = {
                id_atendimento: null,
                in_quantidade_items: total,
                dc_valor_pacote: valor !== null
                    ? valor
                    : (
                        ( this.dados.plano.id_operadora == 1 || id_plano_particular > 0 ) 
                            ? ( pacote.pacote.dc_valor * ( ( pacote.pacote.fl_qtde_variada && ( pacote.pacote.ch_var_tipo_valor != 'T' ) ) ? pacote.in_quantidade : 1 ) )
                            : this.lista_atendimento_procedimentos[this.indexProcPacote].dc_valor_procedimento *
                                ( ( 1==0 && pacote.pacote.ch_var_tipo_valor == 'T' && pacote.qtde_original ) ? pacote.qtde_original : pacote.in_quantidade )
                    ),
                procedimento_pacote: pacote, 
                in_sessoes_previas: previas, 
                st_obs: obs, 
            };

            this.lista_atendimento_procedimentos[this.indexProcPacote].procedimento_pacote = {
                ...pacote,
            };

            this.$forceUpdate();

            this.fechar_modal_pacotes ();

        },

        removerPacote (proc) {

            proc.numero_sessao = 0;
            proc.total_sessao = null;

            proc.atendimento_pacote = null;
            proc.procedimento_pacote = null;

            this.$forceUpdate ();

            this.recalc_preco_cirurgia(proc);

            /*

            this.$store.commit('loading', true);

            this.$util.loadItem(this, {
                service: 'item_tabelas',
                id: proc.id_item_tabela,
            })
            .then(res => {
                if ( res ) {

                    proc.dc_valor_procedimento = res.dc_valor;

                    proc.numero_sessao = 0;
                    proc.total_sessao = null;

                    proc.atendimento_pacote = null;
                    proc.procedimento_pacote = null;

                    this.$forceUpdate ();
                }
            }).finally(() => {
                this.$store.commit('loading', false);
            });
            */

        }

    },

    mounted() {
        this.sort_order = 'L';
    }
}
</script>
